import { useRef, useState, useEffect } from 'react';

type TProps = {
  selectAll?: boolean;
  className?: string;
  value?: string[];
  readOnly?: boolean;
  onChange?: (value: string[]) => void;
  setCanvasRef: (ref: any) => void;
};

const TeethCanvas = ({
  selectAll,
  className,
  value,
  readOnly,
  onChange,
  setCanvasRef,
}: TProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDefaultValue, setIsDefaultValue] = useState<boolean>(true);
  const [fillColors, setFillColors] = useState<string[]>([]);
  let ctx: CanvasRenderingContext2D | null;

  useEffect(() => {
    drawCanvas();
    canvasRef.current?.addEventListener('click', clickHandler);
    canvasRef.current?.addEventListener('mousemove', hoverHandler);
    if (onChange && !isDefaultValue) {
      onChange(fillColors);
    }
    setIsDefaultValue(false);
    return () => {
      canvasRef.current?.removeEventListener('click', clickHandler);
      canvasRef.current?.addEventListener('mousemove', hoverHandler);
    };
  }, [fillColors]);

  useEffect(() => {
    setFillColors((prev) => {
      return selectAll
        ? clickAreas.map((area) => area.id)
        : fillColors.length < 128
        ? [...prev]
        : [];
    });
  }, [selectAll]);

  useEffect(() => {
    if (value) {
      setIsDefaultValue(true);
      setFillColors(value);
    }
  }, [value]);

  useEffect(() => {
    setCanvasRef(canvasRef);
  }, [setCanvasRef]);

  const defindFillColors = (id: string) => {
    return fillColors.includes(id) ? '#FF7E6E' : 'rgba(0,0,0,0)';
  };

  const clickAreas = [
    // 1번 치아
    {
      id: '1left',
      path: () => {
        const p = new Path2D();
        p.moveTo(36.1175, 116.509);
        p.lineTo(58.7276, 125.165);
        p.lineTo(46.9959, 147.138);
        p.bezierCurveTo(44.0602, 147.698, 34.4772, 141.662, 32.7179, 132.713);
        p.bezierCurveTo(31.3105, 125.554, 34.3979, 118.928, 36.1175, 116.509);
        p.closePath();
        return p;
      },
    },
    {
      id: '1right',
      path: () => {
        const p = new Path2D();
        p.moveTo(70.1769, 103.83);
        p.lineTo(58.731, 125.163);
        p.lineTo(81.9758, 134.116);
        p.bezierCurveTo(84.5626, 132.62, 87.8657, 121.787, 83.3451, 113.866);
        p.bezierCurveTo(79.7287, 107.53, 73.0595, 104.535, 70.1769, 103.83);
        p.closePath();
        return p;
      },
    },
    {
      id: '1top',
      path: () => {
        const p = new Path2D();
        p.moveTo(70.1765, 103.834);
        p.lineTo(58.9051, 125.636);
        p.lineTo(36.1205, 116.512);
        p.bezierCurveTo(38.8254, 112.517, 43.2996, 110.46, 45.1986, 109.931);
        p.lineTo(60.3858, 104.277);
        p.bezierCurveTo(63.3312, 103.181, 68.1402, 103.525, 70.1765, 103.834);
        p.closePath();
        return p;
      },
    },
    {
      id: '1bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(81.6882, 134.757);
        p.lineTo(58.9037, 125.632);
        p.lineTo(47.6323, 147.435);
        p.bezierCurveTo(52.2909, 148.688, 57.0209, 147.319, 58.8037, 146.477);
        p.lineTo(73.9908, 140.823);
        p.bezierCurveTo(76.9362, 139.727, 80.3497, 136.322, 81.6882, 134.757);
        p.closePath();
        return p;
      },
    },
    // 2번 치아
    {
      id: '2left',
      path: () => {
        const p = new Path2D();
        p.moveTo(87.5735, 97.8181);
        p.lineTo(109.896, 107.191);
        p.lineTo(97.4687, 128.779);
        p.bezierCurveTo(94.5166, 129.244, 85.1312, 122.906, 83.6584, 113.906);
        p.bezierCurveTo(82.4802, 106.705, 85.7775, 100.181, 87.5735, 97.8181);
        p.closePath();
        return p;
      },
    },
    {
      id: '2right',
      path: () => {
        const p = new Path2D();
        p.moveTo(122.02, 86.2323);
        p.lineTo(109.899, 107.19);
        p.lineTo(132.846, 116.88);
        p.bezierCurveTo(135.48, 115.467, 139.127, 104.745, 134.861, 96.6839);
        p.bezierCurveTo(131.449, 90.2353, 124.879, 87.0292, 122.02, 86.2323);
        p.closePath();
        return p;
      },
    },
    {
      id: '2top',
      path: () => {
        const p = new Path2D();
        p.moveTo(122.02, 86.2365);
        p.lineTo(110.058, 107.668);
        p.lineTo(87.5764, 97.8211);
        p.bezierCurveTo(90.4074, 93.9148, 94.945, 92.0018, 96.86, 91.5336);
        p.lineTo(112.22, 86.3675);
        p.bezierCurveTo(115.199, 85.3655, 119.994, 85.8627, 122.02, 86.2365);
        p.closePath();
        return p;
      },
    },
    {
      id: '2bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(132.539, 117.511);
        p.lineTo(110.057, 107.664);
        p.lineTo(98.0953, 129.096);
        p.bezierCurveTo(102.712, 130.497, 107.483, 129.279, 109.292, 128.495);
        p.lineTo(124.651, 123.329);
        p.bezierCurveTo(127.63, 122.327, 131.151, 119.033, 132.539, 117.511);
        p.closePath();
        return p;
      },
    },
    // 3번 치아
    {
      id: '3left',
      path: () => {
        const p = new Path2D();
        p.moveTo(139.59, 80.9734);
        p.lineTo(161.304, 91.6802);
        p.lineTo(147.593, 112.476);
        p.bezierCurveTo(144.618, 112.762, 135.634, 105.867, 134.708, 96.7944);
        p.bezierCurveTo(133.968, 89.5363, 137.654, 83.2229, 139.59, 80.9734);
        p.closePath();
        return p;
      },
    },
    {
      id: '3right',
      path: () => {
        const p = new Path2D();
        p.moveTo(174.675, 71.4937);
        p.lineTo(161.308, 91.6792);
        p.lineTo(183.626, 102.741);
        p.bezierCurveTo(186.34, 101.489, 190.63, 91.0076, 186.86, 82.7034);
        p.bezierCurveTo(183.844, 76.0601, 177.48, 72.4623, 174.675, 71.4937);
        p.closePath();
        return p;
      },
    },
    {
      id: '3top',
      path: () => {
        const p = new Path2D();
        p.moveTo(174.674, 71.4979);
        p.lineTo(161.437, 92.1661);
        p.lineTo(139.593, 80.9767);
        p.bezierCurveTo(142.655, 77.2489, 147.3, 75.614, 149.24, 75.2625);
        p.lineTo(164.884, 71.0355);
        p.bezierCurveTo(167.918, 70.2157, 172.675, 71.0022, 174.674, 71.4979);
        p.closePath();
        return p;
      },
    },
    {
      id: '3bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(183.281, 103.352);
        p.lineTo(161.436, 92.1623);
        p.lineTo(148.199, 112.831);
        p.bezierCurveTo(152.722, 114.509, 157.559, 113.582, 159.412, 112.909);
        p.lineTo(175.056, 108.682);
        p.bezierCurveTo(178.09, 107.862, 181.803, 104.787, 183.281, 103.352);
        p.closePath();
        return p;
      },
    },
    // 4번 치아
    {
      id: '4left',
      path: () => {
        const p = new Path2D();
        p.moveTo(192.927, 67.3012);
        p.lineTo(214.102, 79.0376);
        p.lineTo(199.409, 99.152);
        p.bezierCurveTo(196.424, 99.2948, 187.78, 91.9764, 187.291, 82.8697);
        p.bezierCurveTo(186.9, 75.5844, 190.885, 69.4552, 192.927, 67.3012);
        p.closePath();
        return p;
      },
    },
    {
      id: '4right',
      path: () => {
        const p = new Path2D();
        p.moveTo(228.426, 59.5161);
        p.lineTo(214.105, 79.0368);
        p.lineTo(235.867, 91.1564);
        p.bezierCurveTo(238.638, 90.0369, 243.426, 79.7731, 240.059, 71.2976);
        p.bezierCurveTo(237.365, 64.5172, 231.181, 60.6181, 228.426, 59.5161);
        p.closePath();
        return p;
      },
    },
    {
      id: '4top',
      path: () => {
        const p = new Path2D();
        p.moveTo(228.425, 59.5202);
        p.lineTo(214.212, 79.5293);
        p.lineTo(192.929, 67.3045);
        p.bezierCurveTo(196.167, 63.728, 200.885, 62.3179, 202.839, 62.0599);
        p.lineTo(218.668, 58.5885);
        p.bezierCurveTo(221.738, 57.9152, 226.452, 58.9291, 228.425, 59.5202);
        p.closePath();
        return p;
      },
    },
    {
      id: '4bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(235.493, 91.7503);
        p.lineTo(214.211, 79.5255);
        p.lineTo(199.997, 99.5347);
        p.bezierCurveTo(204.435, 101.428, 209.31, 100.734, 211.193, 100.151);
        p.lineTo(227.022, 96.6793);
        p.bezierCurveTo(230.092, 96.0061, 233.949, 93.1128, 235.493, 91.7503);
        p.closePath();
        return p;
      },
    },
    // 5번 치아
    {
      id: '5left',
      path: () => {
        const p = new Path2D();
        p.moveTo(247.229, 57.2296);
        p.lineTo(267.934, 69.7776);
        p.lineTo(252.472, 89.3076);
        p.bezierCurveTo(249.484, 89.3346, 241.131, 81.6869, 240.995, 72.5681);
        p.bezierCurveTo(240.886, 65.2731, 245.106, 59.3029, 247.229, 57.2296);
        p.closePath();
        return p;
      },
    },
    {
      id: '5right',
      path: () => {
        const p = new Path2D();
        p.moveTo(283.003, 50.8256);
        p.lineTo(267.937, 69.7769);
        p.lineTo(289.214, 82.7306);
        p.bezierCurveTo(292.026, 81.7192, 297.207, 71.6486, 294.171, 63.049);
        p.bezierCurveTo(291.742, 56.1693, 285.714, 52.0336, 283.003, 50.8256);
        p.closePath();
        return p;
      },
    },
    {
      id: '5top',
      path: () => {
        const p = new Path2D();
        p.moveTo(283.002, 50.8297);
        p.lineTo(268.024, 70.2732);
        p.lineTo(247.232, 57.233);
        p.bezierCurveTo(250.605, 53.7846, 255.375, 52.5583, 257.337, 52.3763);
        p.lineTo(273.289, 49.5207);
        p.bezierCurveTo(276.383, 48.9669, 281.054, 50.1626, 283.002, 50.8297);
        p.closePath();
        return p;
      },
    },
    {
      id: '5bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(288.816, 83.3095);
        p.lineTo(268.024, 70.2694);
        p.lineTo(253.046, 89.7128);
        p.bezierCurveTo(257.406, 91.7768, 262.305, 91.2724, 264.209, 90.7622);
        p.lineTo(280.161, 87.9067);
        p.bezierCurveTo(283.254, 87.3529, 287.22, 84.6112, 288.816, 83.3095);
        p.closePath();
        return p;
      },
    },
    // 6번 치아
    {
      id: '6left',
      path: () => {
        const p = new Path2D();
        p.moveTo(302.253, 48.1297);
        p.lineTo(322.157, 61.9122);
        p.lineTo(305.538, 80.467);
        p.bezierCurveTo(302.554, 80.3124, 294.68, 72.1714, 295.099, 63.0613);
        p.bezierCurveTo(295.434, 55.7731, 300.008, 50.0702, 302.253, 48.1297);
        p.closePath();
        return p;
      },
    },
    {
      id: '6right',
      path: () => {
        const p = new Path2D();
        p.moveTo(338.35, 43.9106);
        p.lineTo(322.161, 61.9118);
        p.lineTo(342.611, 76.1338);
        p.bezierCurveTo(345.48, 75.2951, 351.263, 65.5578, 348.755, 56.7897);
        p.bezierCurveTo(346.749, 49.7752, 340.983, 45.2809, 338.35, 43.9106);
        p.closePath();
        return p;
      },
    },
    {
      id: '6top',
      path: () => {
        const p = new Path2D();
        p.moveTo(338.349, 43.9146);
        p.lineTo(322.218, 62.4124);
        p.lineTo(302.255, 48.1333);
        p.bezierCurveTo(305.832, 44.8962, 310.667, 43.9619, 312.638, 43.8994);
        p.lineTo(328.733, 42.018);
        p.bezierCurveTo(331.855, 41.6532, 336.444, 43.1304, 338.349, 43.9146);
        p.closePath();
        return p;
      },
    },
    {
      id: '6bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(342.18, 76.6875);
        p.lineTo(322.217, 62.4085);
        p.lineTo(306.086, 80.9063);
        p.bezierCurveTo(310.313, 83.2313, 315.233, 83.0253, 317.165, 82.6318);
        p.lineTo(333.261, 80.7504);
        p.bezierCurveTo(336.382, 80.3856, 340.507, 77.8898, 342.18, 76.6875);
        p.closePath();
        return p;
      },
    },
    // 7번 치아
    {
      id: '7left',
      path: () => {
        const p = new Path2D();
        p.moveTo(357.808, 41.7185);
        p.lineTo(376.677, 56.8876);
        p.lineTo(358.775, 74.2079);
        p.bezierCurveTo(355.809, 73.8405, 348.537, 65.1578, 349.605, 56.1008);
        p.bezierCurveTo(350.46, 48.8552, 355.43, 43.4936, 357.808, 41.7185);
        p.closePath();
        return p;
      },
    },
    {
      id: '7right',
      path: () => {
        const p = new Path2D();
        p.moveTo(394.114, 40.0888);
        p.lineTo(376.681, 56.8875);
        p.lineTo(396.062, 72.5341);
        p.bezierCurveTo(398.983, 71.9024, 405.448, 62.6032, 403.573, 53.6783);
        p.bezierCurveTo(402.072, 46.5384, 396.642, 41.6437, 394.114, 40.0888);
        p.closePath();
        return p;
      },
    },
    {
      id: '7top',
      path: () => {
        const p = new Path2D();
        p.moveTo(394.113, 40.0927);
        p.lineTo(376.701, 57.3909);
        p.lineTo(357.81, 41.7222);
        p.bezierCurveTo(361.609, 38.7489, 366.498, 38.1624, 368.468, 38.2408);
        p.lineTo(384.657, 37.5141);
        p.bezierCurveTo(387.797, 37.3731, 392.269, 39.1744, 394.113, 40.0927);
        p.closePath();
        return p;
      },
    },
    {
      id: '7bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(395.592, 73.0555);
        p.lineTo(376.701, 57.3869);
        p.lineTo(359.29, 74.6851);
        p.bezierCurveTo(363.34, 77.3061, 368.262, 77.4522, 370.217, 77.1976);
        p.lineTo(386.406, 76.4709);
        p.bezierCurveTo(389.546, 76.33, 393.838, 74.1353, 395.592, 73.0555);
        p.closePath();
        return p;
      },
    },
    // 8번 치아
    {
      id: '8left',
      path: () => {
        const p = new Path2D();
        p.moveTo(413.06, 38.592);
        p.lineTo(431.44, 54.3501);
        p.lineTo(412.999, 71.0956);
        p.bezierCurveTo(410.046, 70.6347, 403.053, 61.7264, 404.407, 52.7077);
        p.bezierCurveTo(405.49, 45.4928, 410.627, 40.291, 413.06, 38.592);
        p.closePath();
        return p;
      },
    },
    {
      id: '8right',
      path: () => {
        const p = new Path2D();
        p.moveTo(449.4, 38.1111);
        p.lineTo(431.444, 54.3501);
        p.lineTo(450.321, 70.6017);
        p.bezierCurveTo(453.261, 70.0628, 460.016, 60.9726, 458.424, 51.9929);
        p.bezierCurveTo(457.15, 44.8091, 451.877, 39.7451, 449.4, 38.1111);
        p.closePath();
        return p;
      },
    },
    {
      id: '8top',
      path: () => {
        const p = new Path2D();
        p.moveTo(449.398, 38.1149);
        p.lineTo(431.449, 54.8539);
        p.lineTo(413.062, 38.5958);
        p.bezierCurveTo(416.953, 35.744, 421.859, 35.3124, 423.825, 35.4531);
        p.lineTo(440.029, 35.2386);
        p.bezierCurveTo(443.172, 35.197, 447.585, 37.1388, 449.398, 38.1149);
        p.closePath();
        return p;
      },
    },
    {
      id: '8bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(449.835, 71.1081);
        p.lineTo(431.448, 54.85);
        p.lineTo(413.499, 71.589);
        p.bezierCurveTo(417.464, 74.3368, 422.379, 74.6384, 424.341, 74.4458);
        p.lineTo(440.545, 74.2313);
        p.bezierCurveTo(443.688, 74.1897, 448.048, 72.1318, 449.835, 71.1081);
        p.closePath();
        return p;
      },
    },
    // 9번 치아
    {
      id: '9left',
      path: () => {
        const p = new Path2D();
        p.moveTo(468.466, 37.7342);
        p.lineTo(486.077, 54.3472);
        p.lineTo(466.862, 70.1983);
        p.bezierCurveTo(463.935, 69.5977, 457.372, 60.3674, 459.153, 51.4232);
        p.bezierCurveTo(460.577, 44.2678, 465.955, 39.3158, 468.466, 37.7342);
        p.closePath();
        return p;
      },
    },
    {
      id: '9right',
      path: () => {
        const p = new Path2D();
        p.moveTo(504.788, 38.9791);
        p.lineTo(486.081, 54.3474);
        p.lineTo(504.166, 71.4769);
        p.bezierCurveTo(507.127, 71.0781, 514.307, 62.3189, 513.143, 53.2737);
        p.bezierCurveTo(512.211, 46.0376, 507.185, 40.7289, 504.788, 38.9791);
        p.closePath();
        return p;
      },
    },
    {
      id: '9top',
      path: () => {
        const p = new Path2D();
        p.moveTo(504.786, 38.9829);
        p.lineTo(486.062, 54.8508);
        p.lineTo(468.468, 37.7381);
        p.bezierCurveTo(472.49, 35.0743, 477.411, 34.8761, 479.368, 35.1099);
        p.lineTo(495.564, 35.665);
        p.bezierCurveTo(498.705, 35.7727, 503.021, 37.9218, 504.786, 38.9829);
        p.closePath();
        return p;
      },
    },
    {
      id: '9bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(503.656, 71.9596);
        p.lineTo(486.062, 54.8469);
        p.lineTo(467.338, 70.7148);
        p.bezierCurveTo(471.168, 73.6477, 476.063, 74.1824, 478.032, 74.0831);
        p.lineTo(494.228, 74.6382);
        p.bezierCurveTo(497.369, 74.7459, 501.822, 72.8974, 503.656, 71.9596);
        p.closePath();
        return p;
      },
    },
    // 10번 치아
    {
      id: '10left',
      path: () => {
        const p = new Path2D();
        p.moveTo(523.919, 39.0839);
        p.lineTo(540.754, 56.4824);
        p.lineTo(520.837, 71.4411);
        p.bezierCurveTo(517.939, 70.7076, 511.804, 61.1878, 513.991, 52.334);
        p.bezierCurveTo(515.74, 45.251, 521.338, 40.5493, 523.919, 39.0839);
        p.closePath();
        return p;
      },
    },
    {
      id: '10right',
      path: () => {
        const p = new Path2D();
        p.moveTo(560.146, 41.9833);
        p.lineTo(540.758, 56.4828);
        p.lineTo(558.043, 74.4189);
        p.bezierCurveTo(561.02, 74.1556, 568.591, 65.7328, 567.84, 56.6439);
        p.bezierCurveTo(567.24, 49.3728, 562.461, 43.8406, 560.146, 41.9833);
        p.closePath();
        return p;
      },
    },
    {
      id: '10top',
      path: () => {
        const p = new Path2D();
        p.moveTo(560.144, 41.987);
        p.lineTo(540.716, 56.9848);
        p.lineTo(523.92, 39.0878);
        p.bezierCurveTo(528.06, 36.6102, 532.984, 36.6364, 534.929, 36.9593);
        p.lineTo(551.083, 38.2522);
        p.bezierCurveTo(554.216, 38.5029, 558.429, 40.8465, 560.144, 41.987);
        p.closePath();
        return p;
      },
    },
    {
      id: '10bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(557.512, 74.8779);
        p.lineTo(540.716, 56.9809);
        p.lineTo(521.288, 71.9787);
        p.bezierCurveTo(524.981, 75.0832, 529.847, 75.8405, 531.818, 75.8311);
        p.lineTo(547.972, 77.1239);
        p.bezierCurveTo(551.104, 77.3747, 555.637, 75.731, 557.512, 74.8779);
        p.closePath();
        return p;
      },
    },
    // 11번 치아
    {
      id: '11left',
      path: () => {
        const p = new Path2D();
        p.moveTo(579.627, 42.9004);
        p.lineTo(595.06, 61.5547);
        p.lineTo(574.04, 74.9203);
        p.bezierCurveTo(571.209, 73.9639, 565.832, 63.9961, 568.7, 55.339);
        p.bezierCurveTo(570.994, 48.4134, 576.941, 44.1609, 579.627, 42.9004);
        p.closePath();
        return p;
      },
    },
    {
      id: '11right',
      path: () => {
        const p = new Path2D();
        p.moveTo(615.52, 48.6063);
        p.lineTo(595.063, 61.5553);
        p.lineTo(610.902, 80.7804);
        p.bezierCurveTo(613.891, 80.7492, 622.093, 72.9402, 622.051, 63.8205);
        p.bezierCurveTo(622.018, 56.5248, 617.683, 50.6378, 615.52, 48.6063);
        p.closePath();
        return p;
      },
    },
    {
      id: '11top',
      path: () => {
        const p = new Path2D();
        p.moveTo(615.517, 48.6098);
        p.lineTo(594.982, 62.0525);
        p.lineTo(579.629, 42.9045);
        p.bezierCurveTo(583.948, 40.756, 588.856, 41.1649, 590.769, 41.6379);
        p.lineTo(606.774, 44.1821);
        p.bezierCurveTo(609.878, 44.6756, 613.896, 47.3395, 615.517, 48.6098);
        p.closePath();
        return p;
      },
    },
    {
      id: '11bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(610.337, 81.1967);
        p.lineTo(594.983, 62.0487);
        p.lineTo(574.448, 75.4914);
        p.bezierCurveTo(577.889, 78.8734, 582.681, 80.0065, 584.647, 80.1503);
        p.lineTo(600.651, 82.6946);
        p.bezierCurveTo(603.755, 83.188, 608.402, 81.9016, 610.337, 81.1967);
        p.closePath();
        return p;
      },
    },
    // 12번 치아
    {
      id: '12left',
      path: () => {
        const p = new Path2D();
        p.moveTo(634.27, 49.6968);
        p.lineTo(649.163, 68.7847);
        p.lineTo(627.77, 81.5438);
        p.bezierCurveTo(624.967, 80.5069, 619.877, 70.3894, 622.991, 61.8179);
        p.bezierCurveTo(625.483, 54.9606, 631.549, 50.88, 634.27, 49.6968);
        p.closePath();
        return p;
      },
    },
    {
      id: '12right',
      path: () => {
        const p = new Path2D();
        p.moveTo(669.985, 56.4267);
        p.lineTo(649.167, 68.7854);
        p.lineTo(664.449, 88.4555);
        p.bezierCurveTo(667.437, 88.5098, 675.86, 80.9385, 676.079, 71.8214);
        p.bezierCurveTo(676.254, 64.5276, 672.089, 58.5192, 669.985, 56.4267);
        p.closePath();
        return p;
      },
    },
    {
      id: '12top',
      path: () => {
        const p = new Path2D();
        p.moveTo(669.982, 56.4301);
        p.lineTo(649.071, 69.2801);
        p.lineTo(634.271, 49.7009);
        p.bezierCurveTo(638.651, 47.6768, 643.544, 48.2258, 645.444, 48.7533);
        p.lineTo(661.369, 51.7542);
        p.bezierCurveTo(664.457, 52.3362, 668.398, 55.1139, 669.982, 56.4301);
        p.closePath();
        return p;
      },
    },
    {
      id: '12bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(663.872, 88.8555);
        p.lineTo(649.072, 69.2763);
        p.lineTo(628.161, 82.1263);
        p.bezierCurveTo(631.503, 85.6054, 636.261, 86.8751, 638.223, 87.075);
        p.lineTo(654.148, 90.0758);
        p.bezierCurveTo(657.236, 90.6578, 661.918, 89.5048, 663.872, 88.8555);
        p.closePath();
        return p;
      },
    },
    // 13번 치아
    {
      id: '13left',
      path: () => {
        const p = new Path2D();
        p.moveTo(688.828, 59.4107);
        p.lineTo(703.003, 79.0372);
        p.lineTo(681.152, 90.995);
        p.bezierCurveTo(678.389, 89.8549, 673.678, 79.5559, 677.108, 71.1056);
        p.bezierCurveTo(679.851, 64.3454, 686.064, 60.4923, 688.828, 59.4107);
        p.closePath();
        return p;
      },
    },
    {
      id: '13right',
      path: () => {
        const p = new Path2D();
        p.moveTo(724.268, 67.4591);
        p.lineTo(703.007, 79.038);
        p.lineTo(717.55, 99.2609);
        p.bezierCurveTo(720.534, 99.4258, 729.231, 92.1717, 729.788, 83.069);
        p.bezierCurveTo(730.233, 75.7867, 726.294, 69.6281, 724.268, 67.4591);
        p.closePath();
        return p;
      },
    },
    {
      id: '13top',
      path: () => {
        const p = new Path2D();
        p.moveTo(724.266, 67.4624);
        p.lineTo(702.893, 79.5289);
        p.lineTo(688.829, 59.4149);
        p.bezierCurveTo(693.28, 57.5544, 698.15, 58.2843, 700.028, 58.8819);
        p.lineTo(715.831, 62.4706);
        p.bezierCurveTo(718.896, 63.1666, 722.731, 66.0885, 724.266, 67.4624);
        p.closePath();
        return p;
      },
    },
    {
      id: '13bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(716.959, 99.6392);
        p.lineTo(702.894, 79.5251);
        p.lineTo(681.522, 91.5917);
        p.bezierCurveTo(684.733, 95.1922, 689.44, 96.6372, 691.393, 96.9097);
        p.lineTo(707.196, 100.498);
        p.bezierCurveTo(710.26, 101.194, 714.981, 100.216, 716.959, 99.6392);
        p.closePath();
        return p;
      },
    },
    // 14번 치아
    {
      id: '14left',
      path: () => {
        const p = new Path2D();
        p.moveTo(743.826, 70.9193);
        p.lineTo(756.662, 91.4465);
        p.lineTo(734.063, 101.922);
        p.bezierCurveTo(731.382, 100.601, 727.367, 90.0106, 731.352, 81.8075);
        p.bezierCurveTo(734.54, 75.2451, 740.996, 71.8144, 743.826, 70.9193);
        p.closePath();
        return p;
      },
    },
    {
      id: '14right',
      path: () => {
        const p = new Path2D();
        p.moveTo(778.651, 81.3106);
        p.lineTo(756.665, 91.4475);
        p.lineTo(769.829, 112.594);
        p.bezierCurveTo(772.796, 112.958, 781.957, 106.299, 783.119, 97.2535);
        p.bezierCurveTo(784.048, 90.0171, 780.528, 83.6097, 778.651, 81.3106);
        p.closePath();
        return p;
      },
    },
    {
      id: '14top',
      path: () => {
        const p = new Path2D();
        p.moveTo(778.648, 81.3137);
        p.lineTo(756.52, 91.9298);
        p.lineTo(743.826, 70.9235);
        p.bezierCurveTo(748.391, 69.3637, 753.202, 70.4164, 755.037, 71.1377);
        p.lineTo(770.565, 75.7712);
        p.bezierCurveTo(773.577, 76.6698, 777.209, 79.8406, 778.648, 81.3137);
        p.closePath();
        return p;
      },
    },
    {
      id: '14bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(769.214, 112.932);
        p.lineTo(756.521, 91.9261);
        p.lineTo(734.392, 102.542);
        p.bezierCurveTo(737.356, 106.349, 741.957, 108.104, 743.887, 108.506);
        p.lineTo(759.416, 113.139);
        p.bezierCurveTo(762.427, 114.038, 767.203, 113.376, 769.214, 112.932);
        p.closePath();
        return p;
      },
    },
    // 15번 치아
    {
      id: '15left',
      path: () => {
        const p = new Path2D();
        p.moveTo(796.731, 86.3854);
        p.lineTo(808.655, 107.455);
        p.lineTo(785.618, 116.93);
        p.bezierCurveTo(782.998, 115.493, 779.452, 104.737, 783.793, 96.7163);
        p.bezierCurveTo(787.265, 90.2999, 793.865, 87.1555, 796.731, 86.3854);
        p.closePath();
        return p;
      },
    },
    {
      id: '15right',
      path: () => {
        const p = new Path2D();
        p.moveTo(831.068, 98.2933);
        p.lineTo(808.659, 107.457);
        p.lineTo(820.883, 129.16);
        p.bezierCurveTo(823.831, 129.653, 833.275, 123.403, 834.832, 114.417);
        p.bezierCurveTo(836.078, 107.228, 832.842, 100.673, 831.068, 98.2933);
        p.closePath();
        return p;
      },
    },
    {
      id: '15top',
      path: () => {
        const p = new Path2D();
        p.moveTo(831.065, 98.2963);
        p.lineTo(808.492, 107.932);
        p.lineTo(796.732, 86.3896);
        p.bezierCurveTo(801.361, 85.0314, 806.12, 86.294, 807.922, 87.095);
        p.lineTo(823.233, 92.4048);
        p.bezierCurveTo(826.202, 93.4346, 829.691, 96.7615, 831.065, 98.2963);
        p.closePath();
        return p;
      },
    },
    {
      id: '15bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(820.254, 129.471);
        p.lineTo(808.493, 107.928);
        p.lineTo(785.92, 117.564);
        p.bezierCurveTo(788.715, 121.497, 793.234, 123.452, 795.144, 123.938);
        p.lineTo(810.455, 129.248);
        p.bezierCurveTo(813.425, 130.278, 818.225, 129.826, 820.254, 129.471);
        p.closePath();
        return p;
      },
    },
    // 16번 치아
    {
      id: '16left',
      path: () => {
        const p = new Path2D();
        p.moveTo(849.591, 102.984);
        p.lineTo(860.818, 124.433);
        p.lineTo(837.483, 133.148);
        p.bezierCurveTo(834.912, 131.625, 831.72, 120.759, 836.321, 112.885);
        p.bezierCurveTo(840.002, 106.586, 846.702, 103.66, 849.591, 102.984);
        p.closePath();
        return p;
      },
    },
    {
      id: '16right',
      path: () => {
        const p = new Path2D();
        p.moveTo(883.519, 116.011);
        p.lineTo(860.822, 124.435);
        p.lineTo(872.328, 146.527);
        p.bezierCurveTo(875.258, 147.117, 884.902, 141.179, 886.753, 132.249);
        p.bezierCurveTo(888.233, 125.105, 885.214, 118.447, 883.519, 116.011);
        p.closePath();
        return p;
      },
    },
    {
      id: '16top',
      path: () => {
        const p = new Path2D();
        p.moveTo(883.516, 116.014);
        p.lineTo(860.639, 124.904);
        p.lineTo(849.592, 102.988);
        p.bezierCurveTo(854.263, 101.782, 858.979, 103.2, 860.753, 104.06);
        p.lineTo(875.881, 109.869);
        p.bezierCurveTo(878.815, 110.995, 882.194, 114.435, 883.516, 116.014);
        p.closePath();
        return p;
      },
    },
    {
      id: '16bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(871.689, 146.817);
        p.lineTo(860.641, 124.901);
        p.lineTo(837.764, 133.792);
        p.bezierCurveTo(840.428, 137.814, 844.881, 139.916, 846.774, 140.465);
        p.lineTo(861.903, 146.273);
        p.bezierCurveTo(864.837, 147.4, 869.649, 147.105, 871.689, 146.817);
        p.closePath();
        return p;
      },
    },
    // 17번 치아
    {
      id: '17left',
      path: () => {
        const p = new Path2D();
        p.moveTo(840.04, 246.203);
        p.lineTo(862.213, 255.925);
        p.lineTo(849.448, 277.315);
        p.bezierCurveTo(846.489, 277.734, 837.205, 271.249, 835.873, 262.227);
        p.bezierCurveTo(834.808, 255.009, 838.207, 248.537, 840.04, 246.203);
        p.closePath();
        return p;
      },
    },
    {
      id: '17right',
      path: () => {
        const p = new Path2D();
        p.moveTo(874.665, 235.159);
        p.lineTo(862.216, 255.924);
        p.lineTo(885.008, 265.973);
        p.bezierCurveTo(887.664, 264.601, 891.479, 253.938, 887.34, 245.811);
        p.bezierCurveTo(884.029, 239.31, 877.51, 236.001, 874.665, 235.159);
        p.closePath();
        return p;
      },
    },
    {
      id: '17top',
      path: () => {
        const p = new Path2D();
        p.moveTo(874.664, 235.163);
        p.lineTo(862.368, 256.404);
        p.lineTo(840.043, 246.206);
        p.bezierCurveTo(842.935, 242.344, 847.502, 240.503, 849.424, 240.065);
        p.lineTo(864.863, 235.14);
        p.bezierCurveTo(867.858, 234.185, 872.645, 234.758, 874.664, 235.163);
        p.closePath();
        return p;
      },
    },
    {
      id: '17bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(884.691, 266.599);
        p.lineTo(862.366, 256.401);
        p.lineTo(850.07, 277.642);
        p.bezierCurveTo(854.663, 279.115, 859.453, 277.972, 861.274, 277.217);
        p.lineTo(876.713, 272.292);
        p.bezierCurveTo(879.707, 271.337, 883.279, 268.099, 884.691, 266.599);
        p.closePath();
        return p;
      },
    },
    // 18번 치아
    {
      id: '18left',
      path: () => {
        const p = new Path2D();
        p.moveTo(788.742, 263.978);
        p.lineTo(810.71, 274.155);
        p.lineTo(797.508, 295.278);
        p.bezierCurveTo(794.541, 295.636, 785.391, 288.961, 784.246, 279.913);
        p.bezierCurveTo(783.33, 272.675, 786.861, 266.274, 788.742, 263.978);
        p.closePath();
        return p;
      },
    },
    {
      id: '18right',
      path: () => {
        const p = new Path2D();
        p.moveTo(823.586, 253.649);
        p.lineTo(810.713, 274.153);
        p.lineTo(833.294, 284.67);
        p.bezierCurveTo(835.977, 283.353, 840.01, 272.77, 836.04, 264.56);
        p.bezierCurveTo(832.864, 257.992, 826.414, 254.549, 823.586, 253.649);
        p.closePath();
        return p;
      },
    },
    {
      id: '18top',
      path: () => {
        const p = new Path2D();
        p.moveTo(823.585, 253.653);
        p.lineTo(810.854, 274.637);
        p.lineTo(788.745, 263.981);
        p.bezierCurveTo(791.715, 260.18, 796.319, 258.433, 798.25, 258.035);
        p.lineTo(813.787, 253.429);
        p.bezierCurveTo(816.8, 252.536, 821.575, 253.206, 823.585, 253.653);
        p.closePath();
        return p;
      },
    },
    {
      id: '18bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(832.963, 285.289);
        p.lineTo(810.853, 274.633);
        p.lineTo(798.122, 295.617);
        p.bezierCurveTo(802.685, 297.185, 807.497, 296.141, 809.333, 295.423);
        p.lineTo(824.87, 290.817);
        p.bezierCurveTo(827.883, 289.924, 831.521, 286.759, 832.963, 285.289);
        p.closePath();
        return p;
      },
    },
    // 19번 치아
    {
      id: '19left',
      path: () => {
        const p = new Path2D();
        p.moveTo(736.647, 278.022);
        p.lineTo(758.1, 289.243);
        p.lineTo(743.897, 309.707);
        p.bezierCurveTo(740.917, 309.921, 732.099, 302.814, 731.39, 293.722);
        p.bezierCurveTo(730.823, 286.448, 734.659, 280.224, 736.647, 278.022);
        p.closePath();
        return p;
      },
    },
    {
      id: '19right',
      path: () => {
        const p = new Path2D();
        p.moveTo(771.948, 269.381);
        p.lineTo(758.104, 289.242);
        p.lineTo(780.152, 300.832);
        p.bezierCurveTo(782.895, 299.646, 787.433, 289.269, 783.863, 280.878);
        p.bezierCurveTo(781.006, 274.165, 774.729, 270.416, 771.948, 269.381);
        p.closePath();
        return p;
      },
    },
    {
      id: '19top',
      path: () => {
        const p = new Path2D();
        p.moveTo(771.947, 269.385);
        p.lineTo(758.222, 289.732);
        p.lineTo(736.65, 278.025);
        p.bezierCurveTo(739.8, 274.371, 744.483, 272.848, 746.43, 272.542);
        p.lineTo(762.171, 268.689);
        p.bezierCurveTo(765.224, 267.942, 769.96, 268.842, 771.947, 269.385);
        p.closePath();
        return p;
      },
    },
    {
      id: '19bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(779.792, 301.435);
        p.lineTo(758.221, 289.728);
        p.lineTo(744.495, 310.075);
        p.bezierCurveTo(748.977, 311.861, 753.834, 311.049, 755.702, 310.42);
        p.lineTo(771.443, 306.567);
        p.bezierCurveTo(774.496, 305.82, 778.281, 302.834, 779.792, 301.435);
        p.closePath();
        return p;
      },
    },
    // 20번 치아
    {
      id: '20left',
      path: () => {
        const p = new Path2D();
        p.moveTo(683.86, 289.532);
        p.lineTo(704.857, 301.585);
        p.lineTo(689.864, 321.477);
        p.bezierCurveTo(686.877, 321.575, 678.344, 314.127, 677.992, 305.014);
        p.bezierCurveTo(677.71, 297.724, 681.786, 291.655, 683.86, 289.532);
        p.closePath();
        return p;
      },
    },
    {
      id: '20right',
      path: () => {
        const p = new Path2D();
        p.moveTo(719.472, 282.281);
        p.lineTo(704.86, 301.584);
        p.lineTo(726.438, 314.029);
        p.bezierCurveTo(729.225, 312.951, 734.166, 302.76, 730.927, 294.235);
        p.bezierCurveTo(728.336, 287.415, 722.211, 283.424, 719.472, 282.281);
        p.closePath();
        return p;
      },
    },
    {
      id: '20top',
      path: () => {
        const p = new Path2D();
        p.moveTo(719.471, 282.285);
        p.lineTo(704.959, 302.078);
        p.lineTo(683.862, 289.536);
        p.bezierCurveTo(687.153, 286.008, 691.892, 284.669, 693.85, 284.44);
        p.lineTo(709.73, 281.207);
        p.bezierCurveTo(712.809, 280.58, 717.507, 281.664, 719.471, 282.285);
        p.closePath();
        return p;
      },
    },
    {
      id: '20bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(726.055, 314.617);
        p.lineTo(704.958, 302.074);
        p.lineTo(690.446, 321.868);
        p.bezierCurveTo(694.855, 323.828, 699.74, 323.207, 701.631, 322.652);
        p.lineTo(717.511, 319.419);
        p.bezierCurveTo(720.59, 318.791, 724.49, 315.956, 726.055, 314.617);
        p.closePath();
        return p;
      },
    },
    // 21번 치아
    {
      id: '21left',
      path: () => {
        const p = new Path2D();
        p.moveTo(630.531, 299.391);
        p.lineTo(650.812, 312.612);
        p.lineTo(634.719, 331.624);
        p.bezierCurveTo(631.731, 331.553, 623.633, 323.635, 623.797, 314.517);
        p.bezierCurveTo(623.927, 307.222, 628.34, 301.394, 630.531, 299.391);
        p.closePath();
        return p;
      },
    },
    {
      id: '21right',
      path: () => {
        const p = new Path2D();
        p.moveTo(666.496, 294.164);
        p.lineTo(650.816, 312.611);
        p.lineTo(671.656, 326.256);
        p.bezierCurveTo(674.5, 325.337, 680.009, 315.442, 677.257, 306.748);
        p.bezierCurveTo(675.055, 299.792, 669.165, 295.461, 666.496, 294.164);
        p.closePath();
        return p;
      },
    },
    {
      id: '21top',
      path: () => {
        const p = new Path2D();
        p.moveTo(666.494, 294.168);
        p.lineTo(650.887, 313.11);
        p.lineTo(630.533, 299.395);
        p.bezierCurveTo(634.018, 296.059, 638.825, 294.99, 640.793, 294.872);
        p.lineTo(656.83, 292.542);
        p.bezierCurveTo(659.94, 292.09, 664.569, 293.438, 666.494, 294.168);
        p.closePath();
        return p;
      },
    },
    {
      id: '21bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(671.24, 326.822);
        p.lineTo(650.886, 313.106);
        p.lineTo(635.278, 332.048);
        p.bezierCurveTo(639.569, 334.254, 644.481, 333.91, 646.401, 333.463);
        p.lineTo(662.438, 331.132);
        p.bezierCurveTo(665.548, 330.68, 669.602, 328.07, 671.24, 326.822);
        p.closePath();
        return p;
      },
    },
    // 22번 치아
    {
      id: '22left',
      path: () => {
        const p = new Path2D();
        p.moveTo(576.867, 305.347);
        p.lineTo(596.659, 319.291);
        p.lineTo(579.89, 337.71);
        p.bezierCurveTo(576.907, 337.531, 569.1, 329.327, 569.592, 320.22);
        p.bezierCurveTo(569.986, 312.935, 574.606, 307.27, 576.867, 305.347);
        p.closePath();
        return p;
      },
    },
    {
      id: '22right',
      path: () => {
        const p = new Path2D();
        p.moveTo(612.998, 301.422);
        p.lineTo(596.663, 319.291);
        p.lineTo(616.997, 333.679);
        p.bezierCurveTo(619.872, 332.863, 625.734, 323.173, 623.298, 314.385);
        p.bezierCurveTo(621.348, 307.354, 615.619, 302.813, 612.998, 301.422);
        p.closePath();
        return p;
      },
    },
    {
      id: '22top',
      path: () => {
        const p = new Path2D();
        p.moveTo(612.996, 301.426);
        p.lineTo(596.715, 319.792);
        p.lineTo(576.87, 305.351);
        p.bezierCurveTo(580.473, 302.143, 585.315, 301.248, 587.286, 301.202);
        p.lineTo(603.397, 299.451);
        p.bezierCurveTo(606.521, 299.112, 611.098, 300.626, 612.996, 301.426);
        p.closePath();
        return p;
      },
    },
    {
      id: '22bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(616.561, 334.229);
        p.lineTo(596.715, 319.788);
        p.lineTo(580.434, 338.154);
        p.bezierCurveTo(584.642, 340.513, 589.564, 340.347, 591.498, 339.969);
        p.lineTo(607.609, 338.219);
        p.bezierCurveTo(610.733, 337.879, 614.879, 335.417, 616.561, 334.229);
        p.closePath();
        return p;
      },
    },
    // 23번 치아
    {
      id: '23left',
      path: () => {
        const p = new Path2D();
        p.moveTo(523.548, 310.497);
        p.lineTo(542.554, 325.495);
        p.lineTo(524.809, 342.976);
        p.bezierCurveTo(521.84, 342.636, 514.49, 334.019, 515.476, 324.953);
        p.bezierCurveTo(516.265, 317.7, 521.187, 312.294, 523.548, 310.497);
        p.closePath();
        return p;
      },
    },
    {
      id: '23right',
      path: () => {
        const p = new Path2D();
        p.moveTo(559.839, 308.539);
        p.lineTo(542.558, 325.495);
        p.lineTo(562.08, 340.965);
        p.bezierCurveTo(564.995, 340.307, 571.375, 330.95, 569.419, 322.042);
        p.bezierCurveTo(567.855, 314.916, 562.38, 310.071, 559.839, 308.539);
        p.closePath();
        return p;
      },
    },
    {
      id: '23top',
      path: () => {
        const p = new Path2D();
        p.moveTo(559.837, 308.543);
        p.lineTo(542.583, 325.998);
        p.lineTo(523.551, 310.501);
        p.bezierCurveTo(527.323, 307.493, 532.206, 306.863, 534.177, 306.923);
        p.lineTo(550.359, 306.05);
        p.bezierCurveTo(553.497, 305.881, 557.985, 307.641, 559.837, 308.543);
        p.closePath();
        return p;
      },
    },
    {
      id: '23bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(561.615, 341.491);
        p.lineTo(542.583, 325.994);
        p.lineTo(525.328, 343.449);
        p.bezierCurveTo(529.402, 346.033, 534.325, 346.135, 536.278, 345.863);
        p.lineTo(552.46, 344.989);
        p.bezierCurveTo(555.598, 344.82, 559.871, 342.587, 561.615, 341.491);
        p.closePath();
        return p;
      },
    },
    // 24번 치아
    {
      id: '24left',
      path: () => {
        const p = new Path2D();
        p.moveTo(469.434, 310.328);
        p.lineTo(487.676, 326.246);
        p.lineTo(469.089, 342.83);
        p.bezierCurveTo(466.141, 342.343, 459.225, 333.374, 460.658, 324.367);
        p.bezierCurveTo(461.804, 317.162, 466.987, 312.006, 469.434, 310.328);
        p.closePath();
        return p;
      },
    },
    {
      id: '24right',
      path: () => {
        const p = new Path2D();
        p.moveTo(505.777, 310.165);
        p.lineTo(487.679, 326.246);
        p.lineTo(506.414, 342.662);
        p.bezierCurveTo(509.358, 342.149, 516.193, 333.118, 514.679, 324.125);
        p.bezierCurveTo(513.468, 316.93, 508.24, 311.82, 505.777, 310.165);
        p.closePath();
        return p;
      },
    },
    {
      id: '24top',
      path: () => {
        const p = new Path2D();
        p.moveTo(505.775, 310.169);
        p.lineTo(487.68, 326.75);
        p.lineTo(469.436, 310.332);
        p.bezierCurveTo(473.352, 307.514, 478.261, 307.125, 480.226, 307.283);
        p.lineTo(496.432, 307.21);
        p.bezierCurveTo(499.574, 307.196, 503.97, 309.177, 505.775, 310.169);
        p.closePath();
        return p;
      },
    },
    {
      id: '24bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(505.923, 343.164);
        p.lineTo(487.68, 326.746);
        p.lineTo(469.584, 343.327);
        p.bezierCurveTo(473.526, 346.11, 478.438, 346.454, 480.401, 346.279);
        p.lineTo(496.607, 346.206);
        p.bezierCurveTo(499.749, 346.192, 504.127, 344.172, 505.923, 343.164);
        p.closePath();
        return p;
      },
    },
    // 25번 치아
    {
      id: '25left',
      path: () => {
        const p = new Path2D();
        p.moveTo(415.128, 309.545);
        p.lineTo(432.658, 326.243);
        p.lineTo(413.367, 342.001);
        p.bezierCurveTo(410.442, 341.386, 403.924, 332.124, 405.748, 323.189);
        p.bezierCurveTo(407.207, 316.04, 412.609, 311.114, 415.128, 309.545);
        p.closePath();
        return p;
      },
    },
    {
      id: '25right',
      path: () => {
        const p = new Path2D();
        p.moveTo(451.443, 310.966);
        p.lineTo(432.662, 326.243);
        p.lineTo(450.663, 343.46);
        p.bezierCurveTo(453.627, 343.075, 460.848, 334.351, 459.728, 325.3);
        p.bezierCurveTo(458.832, 318.06, 453.831, 312.727, 451.443, 310.966);
        p.closePath();
        return p;
      },
    },
    {
      id: '25top',
      path: () => {
        const p = new Path2D();
        p.moveTo(451.441, 310.969);
        p.lineTo(432.64, 326.747);
        p.lineTo(415.129, 309.549);
        p.bezierCurveTo(419.164, 306.905, 424.086, 306.73, 426.042, 306.974);
        p.lineTo(442.235, 307.607);
        p.bezierCurveTo(445.375, 307.73, 449.681, 309.9, 451.441, 310.969);
        p.closePath();
        return p;
      },
    },
    {
      id: '25bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(450.151, 343.94);
        p.lineTo(432.64, 326.743);
        p.lineTo(413.84, 342.52);
        p.bezierCurveTo(417.656, 345.471, 422.548, 346.03, 424.518, 345.94);
        p.lineTo(440.711, 346.573);
        p.bezierCurveTo(443.851, 346.696, 448.313, 344.869, 450.151, 343.94);
        p.closePath();
        return p;
      },
    },
    // 26번 치아
    {
      id: '26left',
      path: () => {
        const p = new Path2D();
        p.moveTo(360.961, 307.153);
        p.lineTo(377.821, 324.528);
        p.lineTo(357.924, 339.515);
        p.bezierCurveTo(355.026, 338.785, 348.877, 329.274, 351.051, 320.417);
        p.bezierCurveTo(352.791, 313.332, 358.382, 308.622, 360.961, 307.153);
        p.closePath();
        return p;
      },
    },
    {
      id: '26right',
      path: () => {
        const p = new Path2D();
        p.moveTo(397.192, 310.001);
        p.lineTo(377.824, 324.528);
        p.lineTo(395.134, 342.44);
        p.bezierCurveTo(398.111, 342.172, 405.67, 333.739, 404.907, 324.651);
        p.bezierCurveTo(404.296, 317.381, 399.509, 311.855, 397.192, 310.001);
        p.closePath();
        return p;
      },
    },
    {
      id: '26top',
      path: () => {
        const p = new Path2D();
        p.moveTo(397.19, 310.005);
        p.lineTo(377.783, 325.03);
        p.lineTo(360.962, 307.157);
        p.bezierCurveTo(365.098, 304.674, 370.022, 304.693, 371.968, 305.013);
        p.lineTo(388.123, 306.283);
        p.bezierCurveTo(391.256, 306.529, 395.473, 308.867, 397.19, 310.005);
        p.closePath();
        return p;
      },
    },
    {
      id: '26bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(394.604, 342.899);
        p.lineTo(377.783, 325.026);
        p.lineTo(358.376, 340.052);
        p.bezierCurveTo(362.073, 343.151, 366.94, 343.901, 368.912, 343.889);
        p.lineTo(385.067, 345.159);
        p.bezierCurveTo(388.2, 345.405, 392.731, 343.755, 394.604, 342.899);
        p.closePath();
        return p;
      },
    },
    // 27번 치아
    {
      id: '27left',
      path: () => {
        const p = new Path2D();
        p.moveTo(307.5, 300.569);
        p.lineTo(323.24, 318.964);
        p.lineTo(302.445, 332.677);
        p.bezierCurveTo(299.598, 331.767, 294.057, 321.89, 296.781, 313.187);
        p.bezierCurveTo(298.96, 306.224, 304.835, 301.873, 307.5, 300.569);
        p.closePath();
        return p;
      },
    },
    {
      id: '27right',
      path: () => {
        const p = new Path2D();
        p.moveTo(343.482, 305.678);
        p.lineTo(323.244, 318.965);
        p.lineTo(339.399, 337.925);
        p.bezierCurveTo(342.386, 337.844, 350.459, 329.9, 350.265, 320.782);
        p.bezierCurveTo(350.111, 313.488, 345.679, 307.674, 343.482, 305.678);
        p.closePath();
        return p;
      },
    },
    {
      id: '27top',
      path: () => {
        const p = new Path2D();
        p.moveTo(343.48, 305.682);
        p.lineTo(323.171, 319.463);
        p.lineTo(307.502, 300.573);
        p.bezierCurveTo(311.785, 298.353, 316.698, 298.68, 318.62, 299.121);
        p.lineTo(334.664, 301.4);
        p.bezierCurveTo(337.776, 301.842, 341.838, 304.439, 343.48, 305.682);
        p.closePath();
        return p;
      },
    },
    {
      id: '27bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(338.841, 338.35);
        p.lineTo(323.171, 319.459);
        p.lineTo(302.862, 333.241);
        p.bezierCurveTo(306.358, 336.565, 311.169, 337.619, 313.137, 337.73);
        p.lineTo(329.181, 340.009);
        p.bezierCurveTo(332.293, 340.45, 336.917, 339.087, 338.841, 338.35);
        p.closePath();
        return p;
      },
    },
    // 28번 치아
    {
      id: '28left',
      path: () => {
        const p = new Path2D();
        p.moveTo(254.317, 292.669);
        p.lineTo(269.119, 311.828);
        p.lineTo(247.665, 324.485);
        p.bezierCurveTo(244.867, 323.434, 239.826, 313.293, 242.981, 304.736);
        p.bezierCurveTo(245.505, 297.891, 251.59, 293.839, 254.317, 292.669);
        p.closePath();
        return p;
      },
    },
    {
      id: '28right',
      path: () => {
        const p = new Path2D();
        p.moveTo(289.999, 299.569);
        p.lineTo(269.122, 311.828);
        p.lineTo(284.311, 331.571);
        p.bezierCurveTo(287.299, 331.64, 295.758, 324.109, 296.02, 314.993);
        p.bezierCurveTo(296.23, 307.7, 292.094, 301.672, 289.999, 299.569);
        p.closePath();
        return p;
      },
    },
    {
      id: '28top',
      path: () => {
        const p = new Path2D();
        p.moveTo(289.997, 299.573);
        p.lineTo(269.025, 312.323);
        p.lineTo(254.319, 292.673);
        p.bezierCurveTo(258.707, 290.67, 263.598, 291.242, 265.495, 291.779);
        p.lineTo(281.406, 294.856);
        p.bezierCurveTo(284.491, 295.452, 288.419, 298.249, 289.997, 299.573);
        p.closePath();
        return p;
      },
    },
    {
      id: '28bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(283.732, 331.968);
        p.lineTo(269.026, 312.319);
        p.lineTo(248.054, 325.069);
        p.bezierCurveTo(251.379, 328.564, 256.131, 329.856, 258.091, 330.066);
        p.lineTo(274.002, 333.142);
        p.bezierCurveTo(277.088, 333.739, 281.774, 332.608, 283.732, 331.968);
        p.closePath();
        return p;
      },
    },
    // 29번 치아
    {
      id: '29left',
      path: () => {
        const p = new Path2D();
        p.moveTo(201.699, 282.167);
        p.lineTo(215.817, 301.835);
        p.lineTo(193.93, 313.728);
        p.bezierCurveTo(191.171, 312.58, 186.49, 302.267, 189.945, 293.827);
        p.bezierCurveTo(192.708, 287.075, 198.933, 283.24, 201.699, 282.167);
        p.closePath();
        return p;
      },
    },
    {
      id: '29right',
      path: () => {
        const p = new Path2D();
        p.moveTo(237.116, 290.319);
        p.lineTo(215.82, 301.835);
        p.lineTo(230.304, 322.101);
        p.bezierCurveTo(233.288, 322.275, 242.007, 315.046, 242.59, 305.945);
        p.bezierCurveTo(243.056, 298.664, 239.135, 292.494, 237.116, 290.319);
        p.closePath();
        return p;
      },
    },
    {
      id: '29top',
      path: () => {
        const p = new Path2D();
        p.moveTo(237.113, 290.322);
        p.lineTo(215.706, 302.326);
        p.lineTo(201.7, 282.171);
        p.bezierCurveTo(206.157, 280.323, 211.025, 281.068, 212.901, 281.671);
        p.lineTo(228.694, 285.306);
        p.bezierCurveTo(231.757, 286.011, 235.583, 288.944, 237.113, 290.322);
        p.closePath();
        return p;
      },
    },
    {
      id: '29bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(229.712, 322.478);
        p.lineTo(215.707, 302.322);
        p.lineTo(194.299, 314.326);
        p.bezierCurveTo(197.499, 317.936, 202.202, 319.395, 204.154, 319.673);
        p.lineTo(219.946, 323.308);
        p.bezierCurveTo(223.009, 324.013, 227.733, 323.048, 229.712, 322.478);
        p.closePath();
        return p;
      },
    },
    // 30번 치아
    {
      id: '30left',
      path: () => {
        const p = new Path2D();
        p.moveTo(149.701, 268.559);
        p.lineTo(162.856, 288.883);
        p.lineTo(140.423, 299.71);
        p.bezierCurveTo(137.722, 298.43, 133.543, 287.904, 137.399, 279.64);
        p.bezierCurveTo(140.485, 273.029, 146.886, 269.498, 149.701, 268.559);
        p.closePath();
        return p;
      },
    },
    {
      id: '30right',
      path: () => {
        const p = new Path2D();
        p.moveTo(184.685, 278.406);
        p.lineTo(162.86, 288.884);
        p.lineTo(176.351, 309.823);
        p.bezierCurveTo(179.323, 310.14, 188.38, 303.34, 189.4, 294.277);
        p.bezierCurveTo(190.216, 287.027, 186.597, 280.676, 184.685, 278.406);
        p.closePath();
        return p;
      },
    },
    {
      id: '30top',
      path: () => {
        const p = new Path2D();
        p.moveTo(184.682, 278.409);
        p.lineTo(162.721, 289.369);
        p.lineTo(149.702, 268.563);
        p.bezierCurveTo(154.242, 266.932, 159.069, 267.91, 160.914, 268.603);
        p.lineTo(176.513, 272.993);
        p.bezierCurveTo(179.539, 273.845, 183.22, 276.959, 184.682, 278.409);
        p.closePath();
        return p;
      },
    },
    {
      id: '30bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(175.742, 310.171);
        p.lineTo(162.722, 289.365);
        p.lineTo(140.762, 300.325);
        p.bezierCurveTo(143.785, 304.085, 148.412, 305.768, 150.348, 306.14);
        p.lineTo(165.947, 310.531);
        p.bezierCurveTo(168.973, 311.382, 173.737, 310.646, 175.742, 310.171);
        p.closePath();
        return p;
      },
    },
    // 31번 치아
    {
      id: '31left',
      path: () => {
        const p = new Path2D();
        p.moveTo(98.0438, 252.63);
        p.lineTo(110.433, 273.43);
        p.lineTo(87.6122, 283.414);
        p.bezierCurveTo(84.9609, 282.035, 81.1764, 271.361, 85.338, 263.246);
        p.bezierCurveTo(88.6672, 256.754, 95.1957, 253.464, 98.0438, 252.63);
        p.closePath();
        return p;
      },
    },
    {
      id: '31right',
      path: () => {
        const p = new Path2D();
        p.moveTo(132.637, 263.773);
        p.lineTo(110.436, 273.431);
        p.lineTo(123.14, 294.858);
        p.bezierCurveTo(126.098, 295.285, 135.401, 288.827, 136.758, 279.808);
        p.bezierCurveTo(137.844, 272.594, 134.463, 266.112, 132.637, 263.773);
        p.closePath();
        return p;
      },
    },
    {
      id: '31top',
      path: () => {
        const p = new Path2D();
        p.moveTo(132.634, 263.776);
        p.lineTo(110.28, 273.91);
        p.lineTo(98.0444, 252.634);
        p.bezierCurveTo(102.642, 251.174, 107.429, 252.33, 109.247, 253.091);
        p.lineTo(124.672, 258.06);
        p.bezierCurveTo(127.664, 259.023, 131.226, 262.272, 132.634, 263.776);
        p.closePath();
        return p;
      },
    },
    {
      id: '31bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(122.517, 295.183);
        p.lineTo(110.281, 273.907);
        p.lineTo(87.9281, 284.041);
        p.bezierCurveTo(90.809, 287.911, 95.3708, 289.766, 97.2916, 290.209);
        p.lineTo(112.716, 295.178);
        p.bezierCurveTo(115.708, 296.141, 120.497, 295.582, 122.517, 295.183);
        p.closePath();
        return p;
      },
    },
    // 32번 치아
    {
      id: '32left',
      path: () => {
        const p = new Path2D();
        p.moveTo(47.0198, 234.505);
        p.lineTo(58.7392, 255.689);
        p.lineTo(35.6116, 264.941);
        p.bezierCurveTo(33.0057, 263.478, 29.5635, 252.688, 33.9818, 244.71);
        p.bezierCurveTo(37.5164, 238.327, 44.1465, 235.247, 47.0198, 234.505);
        p.closePath();
        return p;
      },
    },
    {
      id: '32right',
      path: () => {
        const p = new Path2D();
        p.moveTo(81.2395, 246.745);
        p.lineTo(58.7427, 255.691);
        p.lineTo(70.7561, 277.511);
        p.bezierCurveTo(73.6988, 278.033, 83.2033, 271.874, 84.8473, 262.904);
        p.bezierCurveTo(86.1625, 255.728, 82.9901, 249.141, 81.2395, 246.745);
        p.closePath();
        return p;
      },
    },
    {
      id: '32top',
      path: () => {
        const p = new Path2D();
        p.moveTo(81.2365, 246.748);
        p.lineTo(58.5713, 256.164);
        p.lineTo(47.0202, 234.509);
        p.bezierCurveTo(51.6623, 233.196, 56.4096, 234.504, 58.203, 235.323);
        p.lineTo(73.4616, 240.781);
        p.bezierCurveTo(76.4209, 241.839, 79.8779, 245.2, 81.2365, 246.748);
        p.closePath();
        return p;
      },
    },
    {
      id: '32bottom',
      path: () => {
        const p = new Path2D();
        p.moveTo(70.1236, 277.816);
        p.lineTo(58.5726, 256.161);
        p.lineTo(35.9074, 265.577);
        p.bezierCurveTo(38.6634, 269.537, 43.1637, 271.536, 45.0694, 272.041);
        p.lineTo(60.328, 277.499);
        p.bezierCurveTo(63.2872, 278.557, 68.0914, 278.151, 70.1236, 277.816);
        p.closePath();
        return p;
      },
    },
  ];

  const drawCanvas = () => {
    ctx = canvasRef.current?.getContext('2d') || null;
    if (!ctx) return;

    ctx.clearRect(0, 0, 920, 370);

    ctx.save();
    ctx.strokeStyle = 'rgba(0,0,0,0)';
    ctx.miterLimit = 4;
    ctx.font = '15px';
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.scale(1, 1);
    ctx.save();

    clickAreas.forEach((area) => {
      if (!ctx) return;
      ctx.fillStyle = defindFillColors(area.id);
      ctx.fill(area.path());
      ctx.stroke();
      ctx.restore();
      ctx.save();
    });

    // 1번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(41.4594, 88.0442);
    ctx.lineTo(45.4061, 98.6459);
    ctx.lineTo(43.195, 99.469);
    ctx.lineTo(40.0278, 90.9613);
    ctx.lineTo(39.9693, 90.9831);
    ctx.lineTo(38.1054, 93.4109);
    ctx.lineTo(37.3804, 91.4634);
    ctx.lineTo(39.3801, 88.8183);
    ctx.lineTo(41.4594, 88.0442);
    ctx.closePath();
    ctx.moveTo(51.9037, 96.3937);
    ctx.bezierCurveTo(49.4802, 97.2959, 47.2707, 96.6679, 46.6275, 94.94);
    ctx.bezierCurveTo(46.1314, 93.6075, 46.7531, 92.1423, 47.9946, 91.4467);
    ctx.lineTo(47.9674, 91.3735);
    ctx.bezierCurveTo(46.7361, 91.5818, 45.5883, 90.917, 45.1623, 89.7501);
    ctx.bezierCurveTo(44.5417, 88.1055, 45.6807, 86.306, 47.8479, 85.4992);
    ctx.bezierCurveTo(50.0151, 84.6924, 52.0537, 85.309, 52.6742, 86.9536);
    ctx.bezierCurveTo(53.1003, 88.1205, 52.6447, 89.3822, 51.6135, 90.0161);
    ctx.lineTo(51.6408, 90.0893);
    ctx.bezierCurveTo(53.0057, 89.8146, 54.4488, 90.5112, 54.9448, 91.8437);
    ctx.bezierCurveTo(55.5881, 93.5716, 54.3198, 95.4943, 51.9037, 96.3937);
    ctx.closePath();
    ctx.moveTo(48.8447, 93.8645);
    ctx.bezierCurveTo(49.1754, 94.7752, 50.1593, 95.1341, 51.2768, 94.7098);
    ctx.bezierCurveTo(52.3997, 94.3001, 52.9094, 93.3851, 52.5641, 92.4799);
    ctx.bezierCurveTo(52.2307, 91.562, 51.1892, 91.1828, 50.1102, 91.5761);
    ctx.bezierCurveTo(49.0367, 91.9841, 48.494, 92.9448, 48.8447, 93.8645);
    ctx.closePath();
    ctx.moveTo(47.3966, 89.1685);
    ctx.bezierCurveTo(47.7054, 90.0205, 48.5925, 90.3655, 49.5324, 90.0239);
    ctx.bezierCurveTo(50.4595, 89.6705, 50.9123, 88.8267, 50.6035, 87.9746);
    ctx.bezierCurveTo(50.2881, 87.15, 49.4139, 86.8169, 48.4694, 87.1685);
    ctx.bezierCurveTo(47.5176, 87.5228, 47.0813, 88.3438, 47.3966, 89.1685);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(26.797, 116.778);
    ctx.rotate(-0.35637703463547016);
    ctx.translate(-26.797, -116.778);
    ctx.beginPath();
    ctx.moveTo(46.297, 116.778);
    ctx.lineTo(61.297, 116.778);
    ctx.bezierCurveTo(
      72.06655262170047,
      116.778,
      80.797,
      125.50844737829955,
      80.797,
      136.27800000000002
    );
    ctx.lineTo(80.797, 136.27800000000002);
    ctx.bezierCurveTo(
      80.797,
      147.0475526217005,
      72.06655262170047,
      155.77800000000002,
      61.297,
      155.77800000000002
    );
    ctx.lineTo(46.297, 155.77800000000002);
    ctx.bezierCurveTo(
      35.52744737829952,
      155.77800000000002,
      26.797,
      147.0475526217005,
      26.797,
      136.27800000000002
    );
    ctx.lineTo(26.797, 136.27800000000002);
    ctx.bezierCurveTo(
      26.797,
      125.50844737829955,
      35.52744737829952,
      116.778,
      46.297,
      116.778
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(69.9875, 104.422);
    ctx.lineTo(47.2012, 147.151);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(36.6479, 116.833);
    ctx.lineTo(81.5944, 134.17);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(71.2647, 153.776);
    ctx.lineTo(75.2114, 164.378);
    ctx.lineTo(73.0003, 165.201);
    ctx.lineTo(69.8331, 156.693);
    ctx.lineTo(69.7746, 156.715);
    ctx.lineTo(67.9107, 159.143);
    ctx.lineTo(67.1857, 157.195);
    ctx.lineTo(69.1854, 154.55);
    ctx.lineTo(71.2647, 153.776);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 32번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(63.9894, 226.403);
    ctx.bezierCurveTo(61.6502, 225.566, 60.4334, 223.679, 61.055, 221.802);
    ctx.lineTo(63.2912, 222.602);
    ctx.bezierCurveTo(63.0438, 223.41, 63.6294, 224.208, 64.6566, 224.584);
    ctx.bezierCurveTo(65.7185, 224.955, 66.6867, 224.638, 66.9872, 223.775);
    ctx.bezierCurveTo(67.3051, 222.909, 66.7511, 222.022, 65.5031, 221.568);
    ctx.lineTo(64.5174, 221.215);
    ctx.lineTo(65.1016, 219.582);
    ctx.lineTo(66.0873, 219.935);
    ctx.bezierCurveTo(67.1392, 220.311, 68.08, 220, 68.3726, 219.159);
    ctx.bezierCurveTo(68.6721, 218.345, 68.2468, 217.562, 67.3026, 217.233);
    ctx.bezierCurveTo(66.3636, 216.889, 65.4018, 217.142, 65.0729, 217.946);
    ctx.lineTo(62.9544, 217.188);
    ctx.bezierCurveTo(63.6463, 215.369, 65.7493, 214.686, 67.934, 215.468);
    ctx.bezierCurveTo(70.1556, 216.262, 71.1632, 218.108, 70.5533, 219.79);
    ctx.bezierCurveTo(70.1344, 220.984, 69.0005, 221.533, 67.7325, 221.32);
    ctx.lineTo(67.701, 221.408);
    ctx.bezierCurveTo(69.1994, 222.16, 69.7206, 223.417, 69.2297, 224.743);
    ctx.bezierCurveTo(68.5919, 226.572, 66.3948, 227.263, 63.9894, 226.403);
    ctx.closePath();
    ctx.moveTo(69.7209, 228.287);
    ctx.lineTo(70.2798, 226.678);
    ctx.lineTo(75.3627, 224.53);
    ctx.bezierCurveTo(76.6952, 223.936, 77.4427, 223.54, 77.7452, 222.694);
    ctx.bezierCurveTo(78.0847, 221.745, 77.5816, 220.901, 76.6006, 220.559);
    ctx.bezierCurveTo(75.6028, 220.194, 74.7219, 220.592, 74.3635, 221.617);
    ctx.lineTo(72.2596, 220.865);
    ctx.bezierCurveTo(72.9969, 218.78, 75.0179, 218.001, 77.2616, 218.804);
    ctx.bezierCurveTo(79.5419, 219.62, 80.5916, 221.464, 79.9311, 223.31);
    ctx.bezierCurveTo(79.497, 224.524, 78.5533, 225.306, 75.6508, 226.508);
    ctx.lineTo(73.4693, 227.504);
    ctx.lineTo(73.4483, 227.563);
    ctx.lineTo(77.9943, 229.189);
    ctx.lineTo(77.3418, 231.013);
    ctx.lineTo(69.7209, 228.287);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(39.7174, 228.707);
    ctx.rotate(0.34350872206051597);
    ctx.translate(-39.7174, -228.707);
    ctx.beginPath();
    ctx.moveTo(59.2174, 228.707);
    ctx.lineTo(74.2174, 228.707);
    ctx.bezierCurveTo(
      84.98695262170047,
      228.707,
      93.7174,
      237.4374473782995,
      93.7174,
      248.207
    );
    ctx.lineTo(93.7174, 248.207);
    ctx.bezierCurveTo(
      93.7174,
      258.9765526217005,
      84.98695262170047,
      267.707,
      74.2174,
      267.707
    );
    ctx.lineTo(59.2174, 267.707);
    ctx.bezierCurveTo(
      48.44784737829952,
      267.707,
      39.7174,
      258.9765526217005,
      39.7174,
      248.207
    );
    ctx.lineTo(39.7174, 248.207);
    ctx.bezierCurveTo(
      39.7174,
      237.4374473782995,
      48.44784737829952,
      228.707,
      59.2174,
      228.707
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(80.7133, 247.076);
    ctx.lineTo(35.7605, 265.083);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(47.2168, 235.094);
    ctx.lineTo(70.43, 277.307);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(35.8763, 290.524);
    ctx.lineTo(36.5025, 288.774);
    ctx.lineTo(43.5146, 283.366);
    ctx.lineTo(46.2952, 284.361);
    ctx.lineTo(43.7956, 291.349);
    ctx.lineTo(45.1491, 291.833);
    ctx.lineTo(44.5123, 293.613);
    ctx.lineTo(43.1588, 293.129);
    ctx.lineTo(42.4852, 295.012);
    ctx.lineTo(40.3814, 294.26);
    ctx.lineTo(41.055, 292.377);
    ctx.lineTo(35.8763, 290.524);
    ctx.closePath();
    ctx.moveTo(38.7493, 289.544);
    ctx.lineTo(41.7211, 290.607);
    ctx.lineTo(43.3525, 286.046);
    ctx.lineTo(43.2495, 286.01);
    ctx.lineTo(38.7809, 289.456);
    ctx.lineTo(38.7493, 289.544);
    ctx.closePath();
    ctx.moveTo(49.2296, 297.591);
    ctx.bezierCurveTo(46.7948, 296.72, 45.5092, 294.816, 46.1302, 293.08);
    ctx.bezierCurveTo(46.609, 291.741, 48.0283, 291.021, 49.4261, 291.289);
    ctx.lineTo(49.4524, 291.215);
    ctx.bezierCurveTo(48.3764, 290.581, 47.9266, 289.334, 48.3524, 288.167);
    ctx.bezierCurveTo(48.937, 286.509, 50.9674, 285.866, 53.1448, 286.645);
    ctx.bezierCurveTo(55.3222, 287.424, 56.4844, 289.209, 55.8997, 290.866);
    ctx.bezierCurveTo(55.474, 292.033, 54.3128, 292.705, 53.1157, 292.526);
    ctx.lineTo(53.0894, 292.599);
    ctx.bezierCurveTo(54.3104, 293.268, 54.9655, 294.731, 54.4867, 296.069);
    ctx.bezierCurveTo(53.8657, 297.805, 51.6571, 298.459, 49.2296, 297.591);
    ctx.closePath();
    ctx.moveTo(48.5189, 293.686);
    ctx.bezierCurveTo(48.1853, 294.595, 48.7067, 295.504, 49.8348, 295.899);
    ctx.bezierCurveTo(50.9576, 296.309, 51.9369, 295.937, 52.2558, 295.022);
    ctx.bezierCurveTo(52.592, 294.106, 52.0397, 293.145, 50.9609, 292.751);
    ctx.bezierCurveTo(49.877, 292.371, 48.8431, 292.756, 48.5189, 293.686);
    ctx.closePath();
    ctx.moveTo(50.4361, 289.161);
    ctx.bezierCurveTo(50.1235, 290.012, 50.5798, 290.847, 51.5188, 291.191);
    ctx.bezierCurveTo(52.4556, 291.518, 53.3455, 291.164, 53.6581, 290.313);
    ctx.bezierCurveTo(53.948, 289.48, 53.4939, 288.662, 52.5449, 288.322);
    ctx.bezierCurveTo(51.5886, 287.98, 50.7261, 288.327, 50.4361, 289.161);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 31번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(115.698, 244.298);
    ctx.bezierCurveTo(113.334, 243.536, 112.057, 241.689, 112.619, 239.793);
    ctx.lineTo(114.879, 240.521);
    ctx.bezierCurveTo(114.658, 241.336, 115.269, 242.116, 116.307, 242.458);
    ctx.bezierCurveTo(117.38, 242.796, 118.338, 242.448, 118.611, 241.575);
    ctx.bezierCurveTo(118.901, 240.7, 118.319, 239.831, 117.057, 239.417);
    ctx.lineTo(116.061, 239.096);
    ctx.lineTo(116.592, 237.445);
    ctx.lineTo(117.589, 237.766);
    ctx.bezierCurveTo(118.652, 238.109, 119.583, 237.768, 119.848, 236.918);
    ctx.bezierCurveTo(120.122, 236.095, 119.672, 235.326, 118.717, 235.027);
    ctx.bezierCurveTo(117.768, 234.713, 116.815, 234.997, 116.512, 235.81);
    ctx.lineTo(114.37, 235.12);
    ctx.bezierCurveTo(115.004, 233.281, 117.084, 232.531, 119.292, 233.242);
    ctx.bezierCurveTo(121.538, 233.966, 122.604, 235.778, 122.048, 237.479);
    ctx.bezierCurveTo(121.667, 238.686, 120.552, 239.27, 119.277, 239.098);
    ctx.lineTo(119.249, 239.187);
    ctx.bezierCurveTo(120.77, 239.891, 121.331, 241.13, 120.883, 242.471);
    ctx.bezierCurveTo(120.304, 244.32, 118.13, 245.081, 115.698, 244.298);
    ctx.closePath();
    ctx.moveTo(129.596, 236.725);
    ctx.lineTo(126.127, 247.493);
    ctx.lineTo(123.881, 246.77);
    ctx.lineTo(126.665, 238.129);
    ctx.lineTo(126.605, 238.109);
    ctx.lineTo(123.638, 238.861);
    ctx.lineTo(124.275, 236.883);
    ctx.lineTo(127.484, 236.045);
    ctx.lineTo(129.596, 236.725);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(90.5603, 247.068);
    ctx.rotate(0.31161108465106757);
    ctx.translate(-90.5603, -247.068);
    ctx.beginPath();
    ctx.moveTo(110.0603, 247.068);
    ctx.lineTo(125.06029999999998, 247.068);
    ctx.bezierCurveTo(
      135.82985262170047,
      247.068,
      144.56029999999998,
      255.7984473782995,
      144.56029999999998,
      266.568
    );
    ctx.lineTo(144.56029999999998, 266.568);
    ctx.bezierCurveTo(
      144.56029999999998,
      277.33755262170047,
      135.82985262170047,
      286.068,
      125.06029999999998,
      286.068
    );
    ctx.lineTo(110.0603, 286.068);
    ctx.bezierCurveTo(
      99.29074737829953,
      286.068,
      90.5603,
      277.33755262170047,
      90.5603,
      266.568
    );
    ctx.lineTo(90.5603, 266.568);
    ctx.bezierCurveTo(
      90.5603,
      255.7984473782995,
      99.29074737829953,
      247.068,
      110.0603,
      247.068
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(132.121, 264.12);
    ctx.lineTo(87.7656, 283.551);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(98.2596, 253.213);
    ctx.lineTo(122.807, 294.664);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(89.1686, 309.13);
    ctx.lineTo(89.7386, 307.36);
    ctx.lineTo(96.5747, 301.732);
    ctx.lineTo(99.3856, 302.637);
    ctx.lineTo(97.1101, 309.702);
    ctx.lineTo(98.4784, 310.142);
    ctx.lineTo(97.8987, 311.942);
    ctx.lineTo(96.5305, 311.501);
    ctx.lineTo(95.9173, 313.405);
    ctx.lineTo(93.7905, 312.72);
    ctx.lineTo(94.4037, 310.816);
    ctx.lineTo(89.1686, 309.13);
    ctx.closePath();
    ctx.moveTo(92.0088, 308.059);
    ctx.lineTo(95.0131, 309.026);
    ctx.lineTo(96.4981, 304.416);
    ctx.lineTo(96.394, 304.382);
    ctx.lineTo(92.0376, 307.969);
    ctx.lineTo(92.0088, 308.059);
    ctx.closePath();
    ctx.moveTo(99.1595, 314.449);
    ctx.lineTo(106.551, 307.046);
    ctx.lineTo(106.575, 306.972);
    ctx.lineTo(101.295, 305.271);
    ctx.lineTo(101.884, 303.442);
    ctx.lineTo(109.484, 305.89);
    ctx.lineTo(108.885, 307.749);
    ctx.lineTo(101.48, 315.197);
    ctx.lineTo(99.1595, 314.449);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 30번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(165.59, 259.169);
    ctx.bezierCurveTo(163.198, 258.496, 161.854, 256.697, 162.344, 254.782);
    ctx.lineTo(164.631, 255.425);
    ctx.bezierCurveTo(164.44, 256.248, 165.079, 257.004, 166.13, 257.308);
    ctx.bezierCurveTo(167.215, 257.605, 168.159, 257.222, 168.399, 256.34);
    ctx.bezierCurveTo(168.656, 255.455, 168.042, 254.608, 166.766, 254.241);
    ctx.lineTo(165.758, 253.957);
    ctx.lineTo(166.228, 252.288);
    ctx.lineTo(167.236, 252.571);
    ctx.bezierCurveTo(168.311, 252.874, 169.228, 252.499, 169.462, 251.64);
    ctx.bezierCurveTo(169.705, 250.807, 169.226, 250.055, 168.261, 249.792);
    ctx.bezierCurveTo(167.301, 249.514, 166.359, 249.833, 166.086, 250.657);
    ctx.lineTo(163.921, 250.047);
    ctx.bezierCurveTo(164.485, 248.185, 166.536, 247.358, 168.77, 247.987);
    ctx.bezierCurveTo(171.041, 248.626, 172.173, 250.398, 171.681, 252.118);
    ctx.bezierCurveTo(171.346, 253.339, 170.252, 253.964, 168.973, 253.839);
    ctx.lineTo(168.947, 253.93);
    ctx.bezierCurveTo(170.494, 254.576, 171.101, 255.794, 170.703, 257.151);
    ctx.bezierCurveTo(170.193, 259.02, 168.049, 259.861, 165.59, 259.169);
    ctx.closePath();
    ctx.moveTo(175.622, 261.993);
    ctx.bezierCurveTo(172.892, 261.224, 171.816, 258.73, 172.82, 255.166);
    ctx.bezierCurveTo(173.821, 251.609, 176.057, 250.038, 178.771, 250.803);
    ctx.bezierCurveTo(181.486, 251.567, 182.575, 254.073, 181.573, 257.63);
    ctx.bezierCurveTo(180.566, 261.209, 178.344, 262.759, 175.622, 261.993);
    ctx.closePath();
    ctx.moveTo(175.136, 255.818);
    ctx.bezierCurveTo(174.414, 258.325, 174.883, 259.772, 176.147, 260.128);
    ctx.bezierCurveTo(177.41, 260.483, 178.558, 259.492, 179.257, 256.978);
    ctx.bezierCurveTo(179.965, 254.491, 179.504, 253.014, 178.251, 252.653);
    ctx.bezierCurveTo(176.993, 252.307, 175.837, 253.328, 175.136, 255.818);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(142.016, 263.279);
    ctx.rotate(0.27438495703528054);
    ctx.translate(-142.016, -263.279);
    ctx.beginPath();
    ctx.moveTo(161.516, 263.279);
    ctx.lineTo(176.516, 263.279);
    ctx.bezierCurveTo(
      187.28555262170048,
      263.279,
      196.016,
      272.0094473782995,
      196.016,
      282.779
    );
    ctx.lineTo(196.016, 282.779);
    ctx.bezierCurveTo(
      196.016,
      293.5485526217005,
      187.28555262170048,
      302.279,
      176.516,
      302.279
    );
    ctx.lineTo(161.516, 302.279);
    ctx.bezierCurveTo(
      150.7464473782995,
      302.279,
      142.016,
      293.5485526217005,
      142.016,
      282.779
    );
    ctx.lineTo(142.016, 282.779);
    ctx.bezierCurveTo(
      142.016,
      272.0094473782995,
      150.7464473782995,
      263.279,
      161.516,
      263.279
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(184.183, 278.773);
    ctx.lineTo(140.581, 299.841);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(149.939, 269.133);
    ctx.lineTo(176.012, 309.642);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(142.454, 325.214);
    ctx.lineTo(142.957, 323.425);
    ctx.lineTo(149.579, 317.546);
    ctx.lineTo(152.422, 318.346);
    ctx.lineTo(150.411, 325.49);
    ctx.lineTo(151.795, 325.88);
    ctx.lineTo(151.282, 327.7);
    ctx.lineTo(149.899, 327.31);
    ctx.lineTo(149.357, 329.235);
    ctx.lineTo(147.206, 328.63);
    ctx.lineTo(147.748, 326.705);
    ctx.lineTo(142.454, 325.214);
    ctx.closePath();
    ctx.moveTo(145.252, 324.038);
    ctx.lineTo(148.29, 324.893);
    ctx.lineTo(149.603, 320.231);
    ctx.lineTo(149.497, 320.201);
    ctx.lineTo(145.278, 323.948);
    ctx.lineTo(145.252, 324.038);
    ctx.closePath();
    ctx.moveTo(156.384, 331.376);
    ctx.bezierCurveTo(154.137, 330.735, 152.365, 328.784, 153.495, 324.8);
    ctx.bezierCurveTo(154.537, 321.125, 156.901, 319.445, 159.624, 320.211);
    ctx.bezierCurveTo(161.812, 320.827, 162.927, 322.634, 162.641, 324.485);
    ctx.lineTo(160.415, 323.859);
    ctx.bezierCurveTo(160.448, 323.048, 159.957, 322.399, 159.077, 322.151);
    ctx.bezierCurveTo(157.596, 321.734, 156.447, 322.847, 155.828, 324.986);
    ctx.lineTo(155.934, 325.016);
    ctx.bezierCurveTo(156.711, 324.187, 157.945, 323.926, 159.148, 324.265);
    ctx.bezierCurveTo(161.111, 324.817, 162.157, 326.694, 161.572, 328.745);
    ctx.bezierCurveTo(160.957, 330.958, 158.841, 332.075, 156.384, 331.376);
    ctx.closePath();
    ctx.moveTo(155.478, 327.095);
    ctx.bezierCurveTo(155.193, 328.167, 155.757, 329.276, 156.872, 329.582);
    ctx.bezierCurveTo(157.991, 329.904, 159.05, 329.285, 159.359, 328.187);
    ctx.bezierCurveTo(159.666, 327.097, 159.098, 326.028, 157.995, 325.71);
    ctx.bezierCurveTo(156.88, 325.404, 155.792, 326.039, 155.478, 327.095);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 29번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(213.072, 270.931);
    ctx.lineTo(213.438, 269.268);
    ctx.lineTo(218.235, 266.54);
    ctx.bezierCurveTo(219.489, 265.795, 220.185, 265.313, 220.386, 264.438);
    ctx.bezierCurveTo(220.612, 263.456, 220.014, 262.677, 219, 262.451);
    ctx.bezierCurveTo(217.966, 262.205, 217.138, 262.704, 216.902, 263.764);
    ctx.lineTo(214.724, 263.263);
    ctx.bezierCurveTo(215.213, 261.107, 217.129, 260.097, 219.451, 260.631);
    ctx.bezierCurveTo(221.811, 261.174, 223.069, 262.883, 222.629, 264.794);
    ctx.bezierCurveTo(222.34, 266.05, 221.494, 266.938, 218.753, 268.471);
    ctx.lineTo(216.703, 269.715);
    ctx.lineTo(216.689, 269.776);
    ctx.lineTo(221.394, 270.859);
    ctx.lineTo(220.959, 272.747);
    ctx.lineTo(213.072, 270.931);
    ctx.closePath();
    ctx.moveTo(229.181, 262.871);
    ctx.bezierCurveTo(231.463, 263.404, 233.312, 265.265, 232.39, 269.269);
    ctx.bezierCurveTo(231.535, 273.017, 229.253, 274.816, 226.482, 274.178);
    ctx.bezierCurveTo(224.266, 273.668, 223.064, 271.925, 223.273, 270.009);
    ctx.lineTo(225.526, 270.527);
    ctx.bezierCurveTo(225.529, 271.386, 226.051, 272.011, 226.934, 272.214);
    ctx.bezierCurveTo(228.434, 272.559, 229.536, 271.394, 230.05, 269.196);
    ctx.lineTo(229.958, 269.175);
    ctx.bezierCurveTo(229.216, 270.03, 227.994, 270.358, 226.769, 270.076);
    ctx.bezierCurveTo(224.789, 269.62, 223.671, 267.791, 224.129, 265.732);
    ctx.bezierCurveTo(224.639, 263.517, 226.693, 262.29, 229.181, 262.871);
    ctx.closePath();
    ctx.moveTo(226.381, 266.187);
    ctx.bezierCurveTo(226.131, 267.275, 226.736, 268.304, 227.87, 268.566);
    ctx.bezierCurveTo(228.989, 268.823, 230.054, 268.13, 230.298, 267.072);
    ctx.bezierCurveTo(230.525, 266.018, 229.907, 264.938, 228.782, 264.671);
    ctx.bezierCurveTo(227.654, 264.419, 226.618, 265.087, 226.381, 266.187);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(193.769, 277.263);
    ctx.rotate(0.22624179494826896);
    ctx.translate(-193.769, -277.263);
    ctx.beginPath();
    ctx.moveTo(213.269, 277.263);
    ctx.lineTo(228.269, 277.263);
    ctx.bezierCurveTo(
      239.0385526217005,
      277.263,
      247.769,
      285.9934473782995,
      247.769,
      296.763
    );
    ctx.lineTo(247.769, 296.763);
    ctx.bezierCurveTo(
      247.769,
      307.53255262170046,
      239.0385526217005,
      316.263,
      228.269,
      316.263
    );
    ctx.lineTo(213.269, 316.263);
    ctx.bezierCurveTo(
      202.49944737829952,
      316.263,
      193.769,
      307.53255262170046,
      193.769,
      296.763
    );
    ctx.lineTo(193.769, 296.763);
    ctx.bezierCurveTo(
      193.769,
      285.9934473782995,
      202.49944737829952,
      277.263,
      213.269,
      277.263
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(236.632, 290.709);
    ctx.lineTo(194.095, 313.852);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(201.964, 282.729);
    ctx.lineTo(229.957, 321.936);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(197.187, 339.105);
    ctx.lineTo(197.604, 337.293);
    ctx.lineTo(203.935, 331.103);
    ctx.lineTo(206.813, 331.765);
    ctx.lineTo(205.148, 338.998);
    ctx.lineTo(206.549, 339.32);
    ctx.lineTo(206.125, 341.163);
    ctx.lineTo(204.724, 340.84);
    ctx.lineTo(204.275, 342.789);
    ctx.lineTo(202.098, 342.288);
    ctx.lineTo(202.546, 340.339);
    ctx.lineTo(197.187, 339.105);
    ctx.closePath();
    ctx.moveTo(199.925, 337.796);
    ctx.lineTo(203.001, 338.504);
    ctx.lineTo(204.088, 333.783);
    ctx.lineTo(203.981, 333.759);
    ctx.lineTo(199.946, 337.704);
    ctx.lineTo(199.925, 337.796);
    ctx.closePath();
    ctx.moveTo(211.184, 344.54);
    ctx.bezierCurveTo(208.884, 344.011, 207.514, 342.268, 207.914, 340.356);
    ctx.lineTo(210.137, 340.868);
    ctx.bezierCurveTo(209.992, 341.74, 210.64, 342.515, 211.601, 342.728);
    ctx.bezierCurveTo(212.741, 342.998, 213.741, 342.379, 213.998, 341.227);
    ctx.bezierCurveTo(214.276, 340.057, 213.634, 339.051, 212.478, 338.777);
    ctx.bezierCurveTo(211.648, 338.586, 210.998, 338.765, 210.528, 339.306);
    ctx.lineTo(208.458, 338.83);
    ctx.lineTo(210.513, 332.617);
    ctx.lineTo(217.304, 334.18);
    ctx.lineTo(216.873, 336.053);
    ctx.lineTo(211.97, 334.924);
    ctx.lineTo(211.024, 337.641);
    ctx.lineTo(211.115, 337.662);
    ctx.bezierCurveTo(211.643, 337.214, 212.572, 337.011, 213.537, 337.241);
    ctx.bezierCurveTo(215.564, 337.7, 216.723, 339.562, 216.235, 341.678);
    ctx.bezierCurveTo(215.722, 343.909, 213.666, 345.111, 211.184, 344.54);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 28번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(265.287, 281.04);
    ctx.lineTo(265.595, 279.365);
    ctx.lineTo(270.293, 276.47);
    ctx.bezierCurveTo(271.519, 275.681, 272.198, 275.175, 272.369, 274.293);
    ctx.bezierCurveTo(272.56, 273.304, 271.935, 272.546, 270.913, 272.357);
    ctx.bezierCurveTo(269.871, 272.147, 269.061, 272.675, 268.863, 273.743);
    ctx.lineTo(266.669, 273.318);
    ctx.bezierCurveTo(267.081, 271.146, 268.96, 270.069, 271.3, 270.522);
    ctx.bezierCurveTo(273.677, 270.982, 274.995, 272.645, 274.623, 274.57);
    ctx.bezierCurveTo(274.378, 275.836, 273.564, 276.753, 270.878, 278.381);
    ctx.lineTo(268.873, 279.697);
    ctx.lineTo(268.861, 279.758);
    ctx.lineTo(273.601, 280.675);
    ctx.lineTo(273.234, 282.577);
    ctx.lineTo(265.287, 281.04);
    ctx.closePath();
    ctx.moveTo(279.018, 283.855);
    ctx.bezierCurveTo(276.479, 283.364, 274.919, 281.678, 275.269, 279.867);
    ctx.bezierCurveTo(275.539, 278.471, 276.833, 277.544, 278.255, 277.596);
    ctx.lineTo(278.27, 277.519);
    ctx.bezierCurveTo(277.11, 277.056, 276.476, 275.891, 276.719, 274.673);
    ctx.bezierCurveTo(277.045, 272.946, 278.955, 272.002, 281.225, 272.441);
    ctx.bezierCurveTo(283.496, 272.88, 284.915, 274.468, 284.589, 276.195);
    ctx.bezierCurveTo(284.346, 277.413, 283.3, 278.253, 282.09, 278.258);
    ctx.lineTo(282.075, 278.335);
    ctx.bezierCurveTo(283.383, 278.81, 284.253, 280.156, 283.983, 281.552);
    ctx.bezierCurveTo(283.633, 283.363, 281.549, 284.344, 279.018, 283.855);
    ctx.closePath();
    ctx.moveTo(277.722, 280.103);
    ctx.bezierCurveTo(277.531, 281.053, 278.184, 281.871, 279.359, 282.09);
    ctx.bezierCurveTo(280.531, 282.325, 281.443, 281.809, 281.619, 280.856);
    ctx.bezierCurveTo(281.812, 279.899, 281.12, 279.033, 279.994, 278.808);
    ctx.bezierCurveTo(278.865, 278.597, 277.902, 279.135, 277.722, 280.103);
    ctx.closePath();
    ctx.moveTo(278.93, 275.339);
    ctx.bezierCurveTo(278.75, 276.228, 279.328, 276.984, 280.308, 277.181);
    ctx.bezierCurveTo(281.284, 277.362, 282.11, 276.877, 282.29, 275.989);
    ctx.bezierCurveTo(282.45, 275.121, 281.876, 274.381, 280.887, 274.19);
    ctx.bezierCurveTo(279.89, 273.997, 279.09, 274.471, 278.93, 275.339);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(246.219, 288.047);
    ctx.rotate(0.19102105064302338);
    ctx.translate(-246.219, -288.047);
    ctx.beginPath();
    ctx.moveTo(265.719, 288.047);
    ctx.lineTo(280.719, 288.047);
    ctx.bezierCurveTo(
      291.4885526217005,
      288.047,
      300.219,
      296.77744737829954,
      300.219,
      307.547
    );
    ctx.lineTo(300.219, 307.547);
    ctx.bezierCurveTo(
      300.219,
      318.3165526217005,
      291.4885526217005,
      327.047,
      280.719,
      327.047
    );
    ctx.lineTo(265.719, 327.047);
    ctx.bezierCurveTo(
      254.9494473782995,
      327.047,
      246.219,
      318.3165526217005,
      246.219,
      307.547
    );
    ctx.lineTo(246.219, 307.547);
    ctx.bezierCurveTo(
      246.219,
      296.77744737829954,
      254.9494473782995,
      288.047,
      265.719,
      288.047
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(289.53, 299.976);
    ctx.lineTo(247.834, 324.602);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(254.602, 293.222);
    ctx.lineTo(283.958, 331.418);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(251.322, 349.636);
    ctx.lineTo(251.675, 347.811);
    ctx.lineTo(257.784, 341.401);
    ctx.lineTo(260.683, 341.962);
    ctx.lineTo(259.274, 349.249);
    ctx.lineTo(260.686, 349.522);
    ctx.lineTo(260.327, 351.378);
    ctx.lineTo(258.915, 351.105);
    ctx.lineTo(258.536, 353.068);
    ctx.lineTo(256.342, 352.644);
    ctx.lineTo(256.722, 350.681);
    ctx.lineTo(251.322, 349.636);
    ctx.closePath();
    ctx.moveTo(254.012, 348.231);
    ctx.lineTo(257.111, 348.83);
    ctx.lineTo(258.031, 344.075);
    ctx.lineTo(257.924, 344.054);
    ctx.lineTo(254.03, 348.139);
    ctx.lineTo(254.012, 348.231);
    ctx.closePath();
    ctx.moveTo(261.646, 351.633);
    ctx.lineTo(261.999, 349.807);
    ctx.lineTo(268.108, 343.398);
    ctx.lineTo(271.008, 343.958);
    ctx.lineTo(269.599, 351.245);
    ctx.lineTo(271.01, 351.518);
    ctx.lineTo(270.651, 353.374);
    ctx.lineTo(269.24, 353.101);
    ctx.lineTo(268.86, 355.065);
    ctx.lineTo(266.666, 354.641);
    ctx.lineTo(267.046, 352.677);
    ctx.lineTo(261.646, 351.633);
    ctx.closePath();
    ctx.moveTo(264.337, 350.228);
    ctx.lineTo(267.436, 350.827);
    ctx.lineTo(268.355, 346.071);
    ctx.lineTo(268.248, 346.05);
    ctx.lineTo(264.355, 350.136);
    ctx.lineTo(264.337, 350.228);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 27번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(318.866, 288.547);
    ctx.lineTo(319.09, 286.859);
    ctx.lineTo(323.637, 283.733);
    ctx.bezierCurveTo(324.823, 282.883, 325.475, 282.345, 325.602, 281.455);
    ctx.bezierCurveTo(325.743, 280.457, 325.081, 279.732, 324.051, 279.594);
    ctx.bezierCurveTo(323, 279.436, 322.217, 280.004, 322.072, 281.08);
    ctx.lineTo(319.86, 280.766);
    ctx.bezierCurveTo(320.163, 278.576, 321.986, 277.406, 324.346, 277.742);
    ctx.bezierCurveTo(326.743, 278.082, 328.142, 279.677, 327.867, 281.619);
    ctx.bezierCurveTo(327.685, 282.895, 326.918, 283.851, 324.317, 285.613);
    ctx.lineTo(322.38, 287.026);
    ctx.lineTo(322.371, 287.088);
    ctx.lineTo(327.151, 287.767);
    ctx.lineTo(326.879, 289.685);
    ctx.lineTo(318.866, 288.547);
    ctx.closePath();
    ctx.moveTo(328.967, 289.982);
    ctx.lineTo(334.995, 281.432);
    ctx.lineTo(335.006, 281.354);
    ctx.lineTo(329.514, 280.575);
    ctx.lineTo(329.784, 278.672);
    ctx.lineTo(337.69, 279.794);
    ctx.lineTo(337.415, 281.728);
    ctx.lineTo(331.381, 290.324);
    ctx.lineTo(328.967, 289.982);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(299.181, 296.357);
    ctx.rotate(0.14106868025339447);
    ctx.translate(-299.181, -296.357);
    ctx.beginPath();
    ctx.moveTo(318.681, 296.357);
    ctx.lineTo(333.681, 296.357);
    ctx.bezierCurveTo(
      344.45055262170047,
      296.357,
      353.181,
      305.08744737829954,
      353.181,
      315.857
    );
    ctx.lineTo(353.181, 315.857);
    ctx.bezierCurveTo(
      353.181,
      326.6265526217005,
      344.45055262170047,
      335.357,
      333.681,
      335.357
    );
    ctx.lineTo(318.681, 335.357);
    ctx.bezierCurveTo(
      307.9114473782995,
      335.357,
      299.181,
      326.6265526217005,
      299.181,
      315.857
    );
    ctx.lineTo(299.181, 315.857);
    ctx.bezierCurveTo(
      299.181,
      305.08744737829954,
      307.9114473782995,
      296.357,
      318.681,
      296.357
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(343.033, 306.108);
    ctx.lineTo(302.619, 332.786);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(307.812, 301.107);
    ctx.lineTo(339.038, 337.79);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(307.848, 357.685);
    ctx.lineTo(308.109, 355.844);
    ctx.lineTo(313.891, 349.137);
    ctx.lineTo(316.815, 349.552);
    ctx.lineTo(315.771, 356.9);
    ctx.lineTo(317.194, 357.103);
    ctx.lineTo(316.929, 358.974);
    ctx.lineTo(315.505, 358.772);
    ctx.lineTo(315.224, 360.752);
    ctx.lineTo(313.012, 360.438);
    ctx.lineTo(313.293, 358.458);
    ctx.lineTo(307.848, 357.685);
    ctx.closePath();
    ctx.moveTo(310.465, 356.147);
    ctx.lineTo(313.59, 356.591);
    ctx.lineTo(314.271, 351.795);
    ctx.lineTo(314.163, 351.78);
    ctx.lineTo(310.478, 356.054);
    ctx.lineTo(310.465, 356.147);
    ctx.closePath();
    ctx.moveTo(322.287, 361.913);
    ctx.bezierCurveTo(319.828, 361.564, 318.256, 359.96, 318.488, 357.996);
    ctx.lineTo(320.839, 358.33);
    ctx.bezierCurveTo(320.759, 359.171, 321.493, 359.836, 322.575, 359.997);
    ctx.bezierCurveTo(323.69, 360.148, 324.575, 359.642, 324.696, 358.736);
    ctx.bezierCurveTo(324.833, 357.824, 324.112, 357.067, 322.798, 356.872);
    ctx.lineTo(321.762, 356.725);
    ctx.lineTo(322.005, 355.008);
    ctx.lineTo(323.042, 355.155);
    ctx.bezierCurveTo(324.148, 355.312, 325.007, 354.819, 325.125, 353.936);
    ctx.bezierCurveTo(325.254, 353.079, 324.68, 352.397, 323.689, 352.265);
    ctx.bezierCurveTo(322.7, 352.116, 321.809, 352.558, 321.648, 353.411);
    ctx.lineTo(319.421, 353.094);
    ctx.bezierCurveTo(319.733, 351.174, 321.656, 350.082, 323.953, 350.408);
    ctx.bezierCurveTo(326.289, 350.74, 327.647, 352.345, 327.388, 354.115);
    ctx.bezierCurveTo(327.217, 355.37, 326.217, 356.135, 324.932, 356.181);
    ctx.lineTo(324.919, 356.274);
    ctx.bezierCurveTo(326.538, 356.709, 327.301, 357.836, 327.087, 359.233);
    ctx.bezierCurveTo(326.83, 361.154, 324.817, 362.272, 322.287, 361.913);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 26번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(370.554, 294.366);
    ctx.lineTo(370.672, 292.667);
    ctx.lineTo(375.015, 289.262);
    ctx.bezierCurveTo(376.145, 288.34, 376.763, 287.762, 376.833, 286.866);
    ctx.bezierCurveTo(376.912, 285.861, 376.206, 285.179, 375.169, 285.105);
    ctx.bezierCurveTo(374.111, 285.014, 373.365, 285.629, 373.287, 286.713);
    ctx.lineTo(371.06, 286.537);
    ctx.bezierCurveTo(371.225, 284.333, 372.972, 283.052, 375.347, 283.238);
    ctx.bezierCurveTo(377.762, 283.428, 379.258, 284.933, 379.104, 286.888);
    ctx.bezierCurveTo(379.003, 288.173, 378.297, 289.175, 375.811, 291.096);
    ctx.lineTo(373.967, 292.628);
    ctx.lineTo(373.962, 292.69);
    ctx.lineTo(378.775, 293.068);
    ctx.lineTo(378.623, 295);
    ctx.lineTo(370.554, 294.366);
    ctx.closePath();
    ctx.moveTo(384.639, 295.63);
    ctx.bezierCurveTo(382.311, 295.439, 380.193, 293.869, 380.525, 289.742);
    ctx.bezierCurveTo(380.833, 285.934, 382.824, 283.826, 385.644, 284.048);
    ctx.bezierCurveTo(387.91, 284.226, 389.355, 285.781, 389.435, 287.653);
    ctx.lineTo(387.13, 287.472);
    ctx.bezierCurveTo(387.005, 286.67, 386.397, 286.129, 385.486, 286.057);
    ctx.bezierCurveTo(383.951, 285.936, 383.041, 287.252, 382.851, 289.47);
    ctx.lineTo(382.96, 289.479);
    ctx.bezierCurveTo(383.561, 288.515, 384.72, 288.019, 385.966, 288.117);
    ctx.bezierCurveTo(387.999, 288.276, 389.391, 289.914, 389.216, 292.04);
    ctx.bezierCurveTo(389.044, 294.33, 387.185, 295.838, 384.639, 295.63);
    ctx.closePath();
    ctx.moveTo(382.918, 291.607);
    ctx.bezierCurveTo(382.847, 292.714, 383.616, 293.692, 384.769, 293.775);
    ctx.bezierCurveTo(385.929, 293.874, 386.847, 293.06, 386.937, 291.923);
    ctx.bezierCurveTo(387.026, 290.794, 386.261, 289.856, 385.116, 289.758);
    ctx.bezierCurveTo(383.963, 289.675, 383.02, 290.51, 382.918, 291.607);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(352.394, 303.471);
    ctx.rotate(0.07844626669183793);
    ctx.translate(-352.394, -303.471);
    ctx.beginPath();
    ctx.moveTo(371.894, 303.471);
    ctx.lineTo(386.894, 303.471);
    ctx.bezierCurveTo(
      397.6635526217005,
      303.471,
      406.394,
      312.2014473782995,
      406.394,
      322.971
    );
    ctx.lineTo(406.394, 322.971);
    ctx.bezierCurveTo(
      406.394,
      333.7405526217005,
      397.6635526217005,
      342.471,
      386.894,
      342.471
    );
    ctx.lineTo(371.894, 342.471);
    ctx.bezierCurveTo(
      361.1244473782995,
      342.471,
      352.394,
      333.7405526217005,
      352.394,
      322.971
    );
    ctx.lineTo(352.394, 322.971);
    ctx.bezierCurveTo(
      352.394,
      312.2014473782995,
      361.1244473782995,
      303.471,
      371.894,
      303.471
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(396.771, 310.458);
    ctx.lineTo(358.105, 339.613);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(361.305, 307.67);
    ctx.lineTo(394.766, 342.327);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(364.882, 364.136);
    ctx.lineTo(365.028, 362.282);
    ctx.lineTo(370.378, 355.226);
    ctx.lineTo(373.322, 355.458);
    ctx.lineTo(372.741, 362.857);
    ctx.lineTo(374.174, 362.97);
    ctx.lineTo(374.025, 364.854);
    ctx.lineTo(372.592, 364.742);
    ctx.lineTo(372.436, 366.736);
    ctx.lineTo(370.208, 366.561);
    ctx.lineTo(370.365, 364.567);
    ctx.lineTo(364.882, 364.136);
    ctx.closePath();
    ctx.moveTo(367.398, 362.437);
    ctx.lineTo(370.544, 362.684);
    ctx.lineTo(370.924, 357.855);
    ctx.lineTo(370.815, 357.847);
    ctx.lineTo(367.405, 362.344);
    ctx.lineTo(367.398, 362.437);
    ctx.closePath();
    ctx.moveTo(375.395, 366.968);
    ctx.lineTo(375.513, 365.269);
    ctx.lineTo(379.856, 361.865);
    ctx.bezierCurveTo(380.986, 360.943, 381.604, 360.364, 381.674, 359.469);
    ctx.bezierCurveTo(381.753, 358.464, 381.047, 357.781, 380.01, 357.708);
    ctx.bezierCurveTo(378.952, 357.617, 378.206, 358.232, 378.128, 359.315);
    ctx.lineTo(375.901, 359.14);
    ctx.bezierCurveTo(376.066, 356.935, 377.813, 355.654, 380.188, 355.841);
    ctx.bezierCurveTo(382.603, 356.031, 384.099, 357.535, 383.945, 359.49);
    ctx.bezierCurveTo(383.844, 360.775, 383.138, 361.778, 380.652, 363.698);
    ctx.lineTo(378.808, 365.23);
    ctx.lineTo(378.803, 365.293);
    ctx.lineTo(383.616, 365.671);
    ctx.lineTo(383.464, 367.603);
    ctx.lineTo(375.395, 366.968);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 25번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(424.71, 296.41);
    ctx.lineTo(424.761, 294.707);
    ctx.lineTo(428.967, 291.134);
    ctx.bezierCurveTo(430.06, 290.169, 430.654, 289.566, 430.689, 288.669);
    ctx.bezierCurveTo(430.729, 287.662, 429.996, 287.007, 428.957, 286.975);
    ctx.bezierCurveTo(427.896, 286.925, 427.175, 287.569, 427.14, 288.655);
    ctx.lineTo(424.908, 288.568);
    ctx.bezierCurveTo(424.986, 286.358, 426.681, 285.009, 429.062, 285.102);
    ctx.bezierCurveTo(431.482, 285.197, 433.036, 286.642, 432.959, 288.601);
    ctx.bezierCurveTo(432.909, 289.889, 432.243, 290.919, 429.835, 292.935);
    ctx.lineTo(428.052, 294.539);
    ctx.lineTo(428.049, 294.601);
    ctx.lineTo(432.874, 294.79);
    ctx.lineTo(432.798, 296.726);
    ctx.lineTo(424.71, 296.41);
    ctx.closePath();
    ctx.moveTo(438.616, 297.11);
    ctx.bezierCurveTo(436.258, 297.018, 434.587, 295.561, 434.624, 293.607);
    ctx.lineTo(436.904, 293.697);
    ctx.bezierCurveTo(436.924, 294.581, 437.704, 295.221, 438.688, 295.252);
    ctx.bezierCurveTo(439.859, 295.305, 440.727, 294.511, 440.765, 293.332);
    ctx.bezierCurveTo(440.82, 292.13, 440.001, 291.261, 438.815, 291.207);
    ctx.bezierCurveTo(437.964, 291.174, 437.358, 291.47, 436.998, 292.09);
    ctx.lineTo(434.875, 292.007);
    ctx.lineTo(435.738, 285.52);
    ctx.lineTo(442.702, 285.792);
    ctx.lineTo(442.627, 287.713);
    ctx.lineTo(437.599, 287.516);
    ctx.lineTo(437.175, 290.361);
    ctx.lineTo(437.269, 290.365);
    ctx.bezierCurveTo(437.704, 289.826, 438.579, 289.454, 439.57, 289.501);
    ctx.bezierCurveTo(441.647, 289.574, 443.132, 291.188, 443.047, 293.358);
    ctx.bezierCurveTo(442.957, 295.646, 441.16, 297.209, 438.616, 297.11);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(406.423, 306.202);
    ctx.rotate(0.03909642244222418);
    ctx.translate(-406.423, -306.202);
    ctx.beginPath();
    ctx.moveTo(425.923, 306.202);
    ctx.lineTo(440.923, 306.202);
    ctx.bezierCurveTo(
      451.6925526217005,
      306.202,
      460.423,
      314.9324473782995,
      460.423,
      325.702
    );
    ctx.lineTo(460.423, 325.702);
    ctx.bezierCurveTo(
      460.423,
      336.4715526217005,
      451.6925526217005,
      345.202,
      440.923,
      345.202
    );
    ctx.lineTo(425.923, 345.202);
    ctx.bezierCurveTo(
      415.1534473782995,
      345.202,
      406.423,
      336.4715526217005,
      406.423,
      325.702
    );
    ctx.lineTo(406.423, 325.702);
    ctx.bezierCurveTo(
      406.423,
      314.9324473782995,
      415.1534473782995,
      306.202,
      425.923,
      306.202
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(451.04, 311.439);
    ctx.lineTo(413.551, 342.092);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(415.492, 310.048);
    ctx.lineTo(450.291, 343.362);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(422.786, 366.388);
    ctx.lineTo(422.859, 364.53);
    ctx.lineTo(427.928, 357.269);
    ctx.lineTo(430.879, 357.385);
    ctx.lineTo(430.589, 364.801);
    ctx.lineTo(432.025, 364.857);
    ctx.lineTo(431.951, 366.746);
    ctx.lineTo(430.515, 366.69);
    ctx.lineTo(430.437, 368.689);
    ctx.lineTo(428.204, 368.601);
    ctx.lineTo(428.282, 366.603);
    ctx.lineTo(422.786, 366.388);
    ctx.closePath();
    ctx.moveTo(425.233, 364.591);
    ctx.lineTo(428.387, 364.715);
    ctx.lineTo(428.577, 359.875);
    ctx.lineTo(428.467, 359.871);
    ctx.lineTo(425.237, 364.498);
    ctx.lineTo(425.233, 364.591);
    ctx.closePath();
    ctx.moveTo(438.56, 357.685);
    ctx.lineTo(438.118, 368.989);
    ctx.lineTo(435.761, 368.897);
    ctx.lineTo(436.115, 359.826);
    ctx.lineTo(436.053, 359.823);
    ctx.lineTo(433.398, 361.346);
    ctx.lineTo(433.479, 359.269);
    ctx.lineTo(436.343, 357.598);
    ctx.lineTo(438.56, 357.685);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 24번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(477.936, 296.79);
    ctx.lineTo(477.913, 295.087);
    ctx.lineTo(481.958, 291.334);
    ctx.bezierCurveTo(483.008, 290.321, 483.576, 289.694, 483.572, 288.795);
    ctx.bezierCurveTo(483.567, 287.788, 482.807, 287.166, 481.768, 287.179);
    ctx.bezierCurveTo(480.705, 287.175, 480.013, 287.85, 480.026, 288.936);
    ctx.lineTo(477.791, 288.946);
    ctx.bezierCurveTo(477.774, 286.736, 479.408, 285.314, 481.791, 285.303);
    ctx.bezierCurveTo(484.212, 285.293, 485.828, 286.668, 485.837, 288.629);
    ctx.bezierCurveTo(485.843, 289.918, 485.222, 290.976, 482.904, 293.095);
    ctx.lineTo(481.193, 294.775);
    ctx.lineTo(481.193, 294.837);
    ctx.lineTo(486.021, 294.816);
    ctx.lineTo(486.03, 296.753);
    ctx.lineTo(477.936, 296.79);
    ctx.closePath();
    ctx.moveTo(487.255, 294.748);
    ctx.lineTo(487.247, 292.888);
    ctx.lineTo(491.994, 285.414);
    ctx.lineTo(494.947, 285.401);
    ctx.lineTo(494.981, 292.822);
    ctx.lineTo(496.418, 292.816);
    ctx.lineTo(496.427, 294.707);
    ctx.lineTo(494.989, 294.713);
    ctx.lineTo(494.998, 296.713);
    ctx.lineTo(492.764, 296.723);
    ctx.lineTo(492.755, 294.723);
    ctx.lineTo(487.255, 294.748);
    ctx.closePath();
    ctx.moveTo(489.621, 292.846);
    ctx.lineTo(492.778, 292.832);
    ctx.lineTo(492.756, 287.989);
    ctx.lineTo(492.647, 287.989);
    ctx.lineTo(489.621, 292.753);
    ctx.lineTo(489.621, 292.846);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(460.593, 307.368);
    ctx.rotate(-0.0044887948499116965);
    ctx.translate(-460.593, -307.368);
    ctx.beginPath();
    ctx.moveTo(480.093, 307.368);
    ctx.lineTo(495.0930000000001, 307.368);
    ctx.bezierCurveTo(
      505.86255262170056,
      307.368,
      514.5930000000001,
      316.0984473782995,
      514.5930000000001,
      326.868
    );
    ctx.lineTo(514.5930000000001, 326.868);
    ctx.bezierCurveTo(
      514.5930000000001,
      337.6375526217005,
      505.86255262170056,
      346.368,
      495.0930000000001,
      346.368
    );
    ctx.lineTo(480.093, 346.368);
    ctx.bezierCurveTo(
      469.32344737829953,
      346.368,
      460.593,
      337.6375526217005,
      460.593,
      326.868
    );
    ctx.lineTo(460.593, 326.868);
    ctx.bezierCurveTo(
      460.593,
      316.0984473782995,
      469.32344737829953,
      307.368,
      480.093,
      307.368
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(505.395, 310.655);
    ctx.lineTo(469.278, 342.912);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(469.821, 310.815);
    ctx.lineTo(506.038, 342.581);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(483.947, 368.92);
    ctx.bezierCurveTo(481.463, 368.931, 479.676, 367.572, 479.62, 365.595);
    ctx.lineTo(481.995, 365.585);
    ctx.bezierCurveTo(482.038, 366.428, 482.86, 366.979, 483.954, 366.982);
    ctx.bezierCurveTo(485.079, 366.969, 485.881, 366.341, 485.869, 365.427);
    ctx.bezierCurveTo(485.873, 364.505, 485.05, 363.86, 483.721, 363.858);
    ctx.lineTo(482.675, 363.863);
    ctx.lineTo(482.667, 362.128);
    ctx.lineTo(483.714, 362.124);
    ctx.bezierCurveTo(484.831, 362.119, 485.609, 361.506, 485.597, 360.615);
    ctx.bezierCurveTo(485.601, 359.748, 484.935, 359.157, 483.935, 359.17);
    ctx.bezierCurveTo(482.935, 359.166, 482.117, 359.732, 482.082, 360.6);
    ctx.lineTo(479.832, 360.61);
    ctx.bezierCurveTo(479.862, 358.664, 481.606, 357.305, 483.926, 357.295);
    ctx.bezierCurveTo(486.286, 357.284, 487.862, 358.675, 487.862, 360.464);
    ctx.bezierCurveTo(487.876, 361.73, 486.997, 362.632, 485.733, 362.865);
    ctx.lineTo(485.733, 362.958);
    ctx.bezierCurveTo(487.398, 363.154, 488.317, 364.158, 488.307, 365.572);
    ctx.bezierCurveTo(488.332, 367.509, 486.502, 368.908, 483.947, 368.92);
    ctx.closePath();
    ctx.moveTo(494.802, 357.402);
    ctx.lineTo(494.853, 368.714);
    ctx.lineTo(492.493, 368.725);
    ctx.lineTo(492.453, 359.647);
    ctx.lineTo(492.39, 359.647);
    ctx.lineTo(489.804, 361.284);
    ctx.lineTo(489.794, 359.206);
    ctx.lineTo(492.583, 357.412);
    ctx.lineTo(494.802, 357.402);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 23번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(531.87, 296.529);
    ctx.lineTo(531.763, 294.829);
    ctx.lineTo(535.618, 290.881);
    ctx.bezierCurveTo(536.617, 289.818, 537.153, 289.163, 537.104, 288.266);
    ctx.bezierCurveTo(537.05, 287.259, 536.26, 286.676, 535.223, 286.74);
    ctx.bezierCurveTo(534.161, 286.789, 533.503, 287.498, 533.569, 288.582);
    ctx.lineTo(531.338, 288.702);
    ctx.bezierCurveTo(531.211, 286.495, 532.773, 284.994, 535.153, 284.866);
    ctx.bezierCurveTo(537.571, 284.735, 539.253, 286.03, 539.358, 287.988);
    ctx.bezierCurveTo(539.428, 289.275, 538.86, 290.362, 536.649, 292.593);
    ctx.lineTo(535.023, 294.355);
    ctx.lineTo(535.027, 294.418);
    ctx.lineTo(539.848, 294.158);
    ctx.lineTo(539.952, 296.092);
    ctx.lineTo(531.87, 296.529);
    ctx.closePath();
    ctx.moveTo(545.811, 295.933);
    ctx.bezierCurveTo(543.331, 296.067, 541.478, 294.797, 541.325, 292.826);
    ctx.lineTo(543.696, 292.698);
    ctx.bezierCurveTo(543.781, 293.539, 544.63, 294.048, 545.723, 293.997);
    ctx.bezierCurveTo(546.845, 293.929, 547.615, 293.261, 547.558, 292.349);
    ctx.bezierCurveTo(547.516, 291.428, 546.662, 290.825, 545.336, 290.889);
    ctx.lineTo(544.29, 290.945);
    ctx.lineTo(544.197, 289.213);
    ctx.lineTo(545.242, 289.157);
    ctx.bezierCurveTo(546.358, 289.097, 547.105, 288.446, 547.049, 287.557);
    ctx.bezierCurveTo(547.01, 286.691, 546.315, 286.134, 545.317, 286.195);
    ctx.bezierCurveTo(544.318, 286.241, 543.529, 286.847, 543.537, 287.715);
    ctx.lineTo(541.29, 287.836);
    ctx.bezierCurveTo(541.225, 285.892, 542.899, 284.448, 545.216, 284.323);
    ctx.bezierCurveTo(547.572, 284.196, 549.216, 285.508, 549.304, 287.295);
    ctx.bezierCurveTo(549.38, 288.558, 548.547, 289.503, 547.295, 289.797);
    ctx.lineTo(547.3, 289.891);
    ctx.bezierCurveTo(548.973, 290.004, 549.94, 290.961, 550.001, 292.374);
    ctx.bezierCurveTo(550.121, 294.308, 548.362, 295.795, 545.811, 295.933);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(514.571, 307.977);
    ctx.rotate(-0.05390589021124646);
    ctx.translate(-514.571, -307.977);
    ctx.beginPath();
    ctx.moveTo(534.071, 307.977);
    ctx.lineTo(549.071, 307.977);
    ctx.bezierCurveTo(
      559.8405526217005,
      307.977,
      568.571,
      316.7074473782995,
      568.571,
      327.477
    );
    ctx.lineTo(568.571, 327.477);
    ctx.bezierCurveTo(
      568.571,
      338.24655262170046,
      559.8405526217005,
      346.977,
      549.071,
      346.977
    );
    ctx.lineTo(534.071, 346.977);
    ctx.bezierCurveTo(
      523.3014473782996,
      346.977,
      514.571,
      338.24655262170046,
      514.571,
      327.477
    );
    ctx.lineTo(514.571, 327.477);
    ctx.bezierCurveTo(
      514.571,
      316.7074473782995,
      523.3014473782996,
      307.977,
      534.071,
      307.977
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(559.482, 309.048);
    ctx.lineTo(525.001, 343.05);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(523.958, 310.965);
    ctx.lineTo(561.7, 340.903);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(539.939, 368.354);
    ctx.bezierCurveTo(537.458, 368.488, 535.606, 367.219, 535.453, 365.248);
    ctx.lineTo(537.824, 365.12);
    ctx.bezierCurveTo(537.909, 365.96, 538.758, 366.47, 539.85, 366.419);
    ctx.bezierCurveTo(540.973, 366.35, 541.743, 365.683, 541.686, 364.771);
    ctx.bezierCurveTo(541.644, 363.85, 540.79, 363.246, 539.464, 363.31);
    ctx.lineTo(538.418, 363.367);
    ctx.lineTo(538.325, 361.635);
    ctx.lineTo(539.37, 361.578);
    ctx.bezierCurveTo(540.486, 361.518, 541.233, 360.867, 541.177, 359.979);
    ctx.bezierCurveTo(541.138, 359.112, 540.443, 358.555, 539.445, 358.617);
    ctx.bezierCurveTo(538.446, 358.663, 537.657, 359.269, 537.665, 360.137);
    ctx.lineTo(535.418, 360.258);
    ctx.bezierCurveTo(535.353, 358.313, 537.027, 356.87, 539.344, 356.745);
    ctx.bezierCurveTo(541.7, 356.617, 543.343, 357.929, 543.432, 359.716);
    ctx.bezierCurveTo(543.508, 360.979, 542.675, 361.924, 541.423, 362.219);
    ctx.lineTo(541.428, 362.312);
    ctx.bezierCurveTo(543.101, 362.425, 544.068, 363.382, 544.129, 364.795);
    ctx.bezierCurveTo(544.249, 366.729, 542.49, 368.217, 539.939, 368.354);
    ctx.closePath();
    ctx.moveTo(545.953, 367.873);
    ctx.lineTo(545.846, 366.174);
    ctx.lineTo(549.701, 362.226);
    ctx.bezierCurveTo(550.7, 361.163, 551.236, 360.508, 551.188, 359.611);
    ctx.bezierCurveTo(551.133, 358.604, 550.343, 358.021, 549.306, 358.085);
    ctx.bezierCurveTo(548.244, 358.134, 547.586, 358.843, 547.653, 359.926);
    ctx.lineTo(545.421, 360.047);
    ctx.bezierCurveTo(545.295, 357.84, 546.857, 356.339, 549.236, 356.211);
    ctx.bezierCurveTo(551.654, 356.08, 553.336, 357.374, 553.442, 359.332);
    ctx.bezierCurveTo(553.511, 360.62, 552.944, 361.706, 550.733, 363.938);
    ctx.lineTo(549.106, 365.7);
    ctx.lineTo(549.11, 365.763);
    ctx.lineTo(553.931, 365.503);
    ctx.lineTo(554.035, 367.437);
    ctx.lineTo(545.953, 367.873);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 22번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(584.418, 290.948);
    ctx.lineTo(584.219, 289.256);
    ctx.lineTo(587.854, 285.105);
    ctx.bezierCurveTo(588.794, 283.989, 589.293, 283.306, 589.196, 282.413);
    ctx.bezierCurveTo(589.087, 281.411, 588.266, 280.871, 587.234, 280.991);
    ctx.bezierCurveTo(586.177, 281.098, 585.559, 281.841, 585.684, 282.92);
    ctx.lineTo(583.462, 283.161);
    ctx.bezierCurveTo(583.216, 280.964, 584.694, 279.381, 587.063, 279.124);
    ctx.bezierCurveTo(589.471, 278.862, 591.22, 280.063, 591.432, 282.013);
    ctx.bezierCurveTo(591.571, 283.294, 591.064, 284.41, 588.977, 286.759);
    ctx.lineTo(587.449, 288.606);
    ctx.lineTo(587.456, 288.669);
    ctx.lineTo(592.255, 288.147);
    ctx.lineTo(592.465, 290.073);
    ctx.lineTo(584.418, 290.948);
    ctx.closePath();
    ctx.moveTo(594.127, 289.893);
    ctx.lineTo(593.927, 288.201);
    ctx.lineTo(597.563, 284.05);
    ctx.bezierCurveTo(598.502, 282.934, 599.002, 282.251, 598.905, 281.358);
    ctx.bezierCurveTo(598.796, 280.356, 597.975, 279.816, 596.943, 279.936);
    ctx.bezierCurveTo(595.886, 280.043, 595.267, 280.787, 595.392, 281.865);
    ctx.lineTo(593.171, 282.107);
    ctx.bezierCurveTo(592.924, 279.909, 594.402, 278.326, 596.771, 278.069);
    ctx.bezierCurveTo(599.179, 277.807, 600.928, 279.008, 601.14, 280.958);
    ctx.bezierCurveTo(601.279, 282.239, 600.772, 283.355, 598.685, 285.704);
    ctx.lineTo(597.157, 287.552);
    ctx.lineTo(597.164, 287.614);
    ctx.lineTo(601.964, 287.092);
    ctx.lineTo(602.173, 289.018);
    ctx.lineTo(594.127, 289.893);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(567.767, 303.319);
    ctx.rotate(-0.10823013584419597);
    ctx.translate(-567.767, -303.319);
    ctx.beginPath();
    ctx.moveTo(587.267, 303.319);
    ctx.lineTo(602.267, 303.319);
    ctx.bezierCurveTo(
      613.0365526217005,
      303.319,
      621.767,
      312.04944737829953,
      621.767,
      322.819
    );
    ctx.lineTo(621.767, 322.819);
    ctx.bezierCurveTo(
      621.767,
      333.5885526217005,
      613.0365526217005,
      342.319,
      602.267,
      342.319
    );
    ctx.lineTo(587.267, 342.319);
    ctx.bezierCurveTo(
      576.4974473782996,
      342.319,
      567.767,
      333.5885526217005,
      567.767,
      322.819
    );
    ctx.lineTo(567.767, 322.819);
    ctx.bezierCurveTo(
      567.767,
      312.04944737829953,
      576.4974473782996,
      303.319,
      587.267,
      303.319
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(612.669, 301.949);
    ctx.lineTo(580.086, 337.773);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(577.302, 305.792);
    ctx.lineTo(616.614, 333.637);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(595.878, 362.283);
    ctx.bezierCurveTo(593.409, 362.552, 591.49, 361.385, 591.23, 359.425);
    ctx.lineTo(593.591, 359.168);
    ctx.bezierCurveTo(593.721, 360.003, 594.597, 360.466, 595.685, 360.355);
    ctx.bezierCurveTo(596.802, 360.226, 597.535, 359.518, 597.428, 358.61);
    ctx.bezierCurveTo(597.336, 357.693, 596.451, 357.137, 595.13, 357.272);
    ctx.lineTo(594.089, 357.385);
    ctx.lineTo(593.902, 355.661);
    ctx.lineTo(594.942, 355.548);
    ctx.bezierCurveTo(596.053, 355.427, 596.764, 354.737, 596.66, 353.853);
    ctx.bezierCurveTo(596.574, 352.99, 595.85, 352.471, 594.856, 352.587);
    ctx.bezierCurveTo(593.861, 352.687, 593.107, 353.335, 593.161, 354.201);
    ctx.lineTo(590.925, 354.444);
    ctx.bezierCurveTo(590.753, 352.506, 592.347, 350.974, 594.654, 350.723);
    ctx.bezierCurveTo(596.999, 350.468, 598.712, 351.689, 598.897, 353.468);
    ctx.bezierCurveTo(599.042, 354.725, 598.261, 355.714, 597.027, 356.076);
    ctx.lineTo(597.037, 356.169);
    ctx.bezierCurveTo(598.714, 356.191, 599.731, 357.095, 599.868, 358.502);
    ctx.bezierCurveTo(600.093, 360.426, 598.418, 362.007, 595.878, 362.283);
    ctx.closePath();
    ctx.moveTo(606.037, 361.179);
    ctx.bezierCurveTo(603.568, 361.448, 601.649, 360.281, 601.389, 358.321);
    ctx.lineTo(603.75, 358.065);
    ctx.bezierCurveTo(603.88, 358.899, 604.755, 359.362, 605.844, 359.252);
    ctx.bezierCurveTo(606.961, 359.122, 607.694, 358.414, 607.587, 357.506);
    ctx.bezierCurveTo(607.495, 356.589, 606.61, 356.033, 605.289, 356.168);
    ctx.lineTo(604.248, 356.282);
    ctx.lineTo(604.061, 354.557);
    ctx.lineTo(605.101, 354.444);
    ctx.bezierCurveTo(606.212, 354.324, 606.923, 353.633, 606.819, 352.749);
    ctx.bezierCurveTo(606.733, 351.886, 606.009, 351.367, 605.015, 351.483);
    ctx.bezierCurveTo(604.02, 351.583, 603.266, 352.231, 603.32, 353.097);
    ctx.lineTo(601.084, 353.341);
    ctx.bezierCurveTo(600.912, 351.402, 602.506, 349.87, 604.813, 349.619);
    ctx.bezierCurveTo(607.158, 349.364, 608.871, 350.585, 609.056, 352.364);
    ctx.bezierCurveTo(609.2, 353.622, 608.42, 354.61, 607.186, 354.972);
    ctx.lineTo(607.196, 355.065);
    ctx.bezierCurveTo(608.873, 355.088, 609.89, 355.991, 610.027, 357.398);
    ctx.bezierCurveTo(610.252, 359.323, 608.577, 360.903, 606.037, 361.179);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 21번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(638.546, 284.584);
    ctx.lineTo(638.286, 282.901);
    ctx.lineTo(641.769, 278.622);
    ctx.bezierCurveTo(642.668, 277.472, 643.142, 276.772, 643.013, 275.883);
    ctx.bezierCurveTo(642.868, 274.886, 642.029, 274.376, 641.001, 274.533);
    ctx.bezierCurveTo(639.949, 274.678, 639.357, 275.443, 639.521, 276.517);
    ctx.lineTo(637.31, 276.838);
    ctx.bezierCurveTo(636.984, 274.651, 638.405, 273.016, 640.763, 272.673);
    ctx.bezierCurveTo(643.159, 272.325, 644.951, 273.462, 645.233, 275.402);
    ctx.bezierCurveTo(645.418, 276.678, 644.951, 277.812, 642.951, 280.234);
    ctx.lineTo(641.49, 282.136);
    ctx.lineTo(641.499, 282.197);
    ctx.lineTo(646.277, 281.503);
    ctx.lineTo(646.556, 283.42);
    ctx.lineTo(638.546, 284.584);
    ctx.closePath();
    ctx.moveTo(651.408, 271.284);
    ctx.lineTo(653.035, 282.479);
    ctx.lineTo(650.7, 282.818);
    ctx.lineTo(649.394, 273.834);
    ctx.lineTo(649.333, 273.843);
    ctx.lineTo(646.999, 275.825);
    ctx.lineTo(646.701, 273.768);
    ctx.lineTo(649.212, 271.603);
    ctx.lineTo(651.408, 271.284);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(621.362, 297.692);
    ctx.rotate(-0.1443205777157103);
    ctx.translate(-621.362, -297.692);
    ctx.beginPath();
    ctx.moveTo(640.862, 297.692);
    ctx.lineTo(655.862, 297.692);
    ctx.bezierCurveTo(
      666.6315526217004,
      297.692,
      675.362,
      306.4224473782995,
      675.362,
      317.192
    );
    ctx.lineTo(675.362, 317.192);
    ctx.bezierCurveTo(
      675.362,
      327.9615526217005,
      666.6315526217004,
      336.692,
      655.862,
      336.692
    );
    ctx.lineTo(640.862, 336.692);
    ctx.bezierCurveTo(
      630.0924473782995,
      336.692,
      621.362,
      327.9615526217005,
      621.362,
      317.192
    );
    ctx.lineTo(621.362, 317.192);
    ctx.bezierCurveTo(
      621.362,
      306.4224473782995,
      630.0924473782995,
      297.692,
      640.862,
      297.692
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(666.186, 294.703);
    ctx.lineTo(634.917, 331.68);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(630.981, 299.82);
    ctx.lineTo(671.272, 326.228);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(651.584, 355.604);
    ctx.bezierCurveTo(649.125, 355.961, 647.166, 354.865, 646.835, 352.915);
    ctx.lineTo(649.185, 352.574);
    ctx.bezierCurveTo(649.345, 353.403, 650.237, 353.834, 651.32, 353.684);
    ctx.bezierCurveTo(652.433, 353.515, 653.139, 352.781, 653, 351.877);
    ctx.bezierCurveTo(652.875, 350.964, 651.97, 350.44, 650.654, 350.623);
    ctx.lineTo(649.618, 350.774);
    ctx.lineTo(649.369, 349.058);
    ctx.lineTo(650.405, 348.907);
    ctx.bezierCurveTo(651.511, 348.746, 652.196, 348.031, 652.06, 347.151);
    ctx.bezierCurveTo(651.943, 346.291, 651.201, 345.799, 650.212, 345.951);
    ctx.bezierCurveTo(649.221, 346.087, 648.491, 346.762, 648.577, 347.625);
    ctx.lineTo(646.35, 347.949);
    ctx.bezierCurveTo(646.109, 346.018, 647.646, 344.429, 649.943, 344.095);
    ctx.bezierCurveTo(652.277, 343.756, 654.032, 344.914, 654.282, 346.686);
    ctx.bezierCurveTo(654.472, 347.937, 653.727, 348.953, 652.508, 349.359);
    ctx.lineTo(652.521, 349.452);
    ctx.bezierCurveTo(654.197, 349.414, 655.246, 350.28, 655.434, 351.681);
    ctx.bezierCurveTo(655.729, 353.596, 654.112, 355.237, 651.584, 355.604);
    ctx.closePath();
    ctx.moveTo(657.056, 352.63);
    ctx.lineTo(656.789, 350.79);
    ctx.lineTo(660.449, 342.726);
    ctx.lineTo(663.371, 342.302);
    ctx.lineTo(664.438, 349.646);
    ctx.lineTo(665.861, 349.44);
    ctx.lineTo(666.133, 351.311);
    ctx.lineTo(664.71, 351.517);
    ctx.lineTo(664.998, 353.497);
    ctx.lineTo(662.787, 353.818);
    ctx.lineTo(662.499, 351.839);
    ctx.lineTo(657.056, 352.63);
    ctx.closePath();
    ctx.moveTo(659.135, 350.417);
    ctx.lineTo(662.258, 349.963);
    ctx.lineTo(661.562, 345.17);
    ctx.lineTo(661.453, 345.186);
    ctx.lineTo(659.121, 350.325);
    ctx.lineTo(659.135, 350.417);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 20번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(689.556, 274.595);
    ctx.lineTo(689.201, 272.93);
    ctx.lineTo(692.437, 268.46);
    ctx.bezierCurveTo(693.269, 267.262, 693.703, 266.535, 693.524, 265.655);
    ctx.bezierCurveTo(693.323, 264.667, 692.455, 264.206, 691.439, 264.421);
    ctx.bezierCurveTo(690.396, 264.626, 689.849, 265.423, 690.073, 266.485);
    ctx.lineTo(687.884, 266.931);
    ctx.bezierCurveTo(687.435, 264.766, 688.76, 263.053, 691.095, 262.578);
    ctx.bezierCurveTo(693.469, 262.094, 695.321, 263.128, 695.713, 265.05);
    ctx.bezierCurveTo(695.97, 266.313, 695.568, 267.471, 693.708, 270.003);
    ctx.lineTo(692.357, 271.984);
    ctx.lineTo(692.369, 272.045);
    ctx.lineTo(697.1, 271.082);
    ctx.lineTo(697.487, 272.98);
    ctx.lineTo(689.556, 274.595);
    ctx.closePath();
    ctx.moveTo(703.459, 271.924);
    ctx.bezierCurveTo(700.68, 272.49, 698.582, 270.764, 697.844, 267.135);
    ctx.bezierCurveTo(697.106, 263.514, 698.376, 261.095, 701.139, 260.532);
    ctx.bezierCurveTo(703.903, 259.97, 706.017, 261.7, 706.754, 265.321);
    ctx.bezierCurveTo(707.496, 268.965, 706.23, 271.359, 703.459, 271.924);
    ctx.closePath();
    ctx.moveTo(700.201, 266.655);
    ctx.bezierCurveTo(700.707, 269.215, 701.786, 270.287, 703.072, 270.025);
    ctx.bezierCurveTo(704.358, 269.763, 704.925, 268.356, 704.397, 265.801);
    ctx.bezierCurveTo(703.888, 263.265, 702.803, 262.163, 701.523, 262.416);
    ctx.bezierCurveTo(700.246, 262.684, 699.685, 264.121, 700.201, 266.655);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(674.61, 288.354);
    ctx.rotate(-0.20088216091679137);
    ctx.translate(-674.61, -288.354);
    ctx.beginPath();
    ctx.moveTo(694.11, 288.354);
    ctx.lineTo(709.11, 288.354);
    ctx.bezierCurveTo(
      719.8795526217004,
      288.354,
      728.61,
      297.0844473782995,
      728.61,
      307.854
    );
    ctx.lineTo(728.61, 307.854);
    ctx.bezierCurveTo(
      728.61,
      318.62355262170047,
      719.8795526217004,
      327.354,
      709.11,
      327.354
    );
    ctx.lineTo(694.11, 327.354);
    ctx.bezierCurveTo(
      683.3404473782996,
      327.354,
      674.61,
      318.62355262170047,
      674.61,
      307.854
    );
    ctx.lineTo(674.61, 307.854);
    ctx.bezierCurveTo(
      674.61,
      297.0844473782995,
      683.3404473782996,
      288.354,
      694.11,
      288.354
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(719.193, 282.836);
    ctx.lineTo(690.064, 321.521);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(684.334, 289.935);
    ctx.lineTo(726.053, 314.023);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(708.057, 344.465);
    ctx.bezierCurveTo(705.623, 344.961, 703.604, 343.976, 703.164, 342.049);
    ctx.lineTo(705.491, 341.575);
    ctx.bezierCurveTo(705.698, 342.394, 706.612, 342.774, 707.686, 342.563);
    ctx.bezierCurveTo(708.787, 342.331, 709.45, 341.558, 709.26, 340.664);
    ctx.bezierCurveTo(709.084, 339.759, 708.151, 339.287, 706.848, 339.545);
    ctx.lineTo(705.822, 339.754);
    ctx.lineTo(705.476, 338.054);
    ctx.lineTo(706.502, 337.845);
    ctx.bezierCurveTo(707.596, 337.622, 708.24, 336.869, 708.055, 335.998);
    ctx.bezierCurveTo(707.89, 335.147, 707.121, 334.698, 706.142, 334.905);
    ctx.bezierCurveTo(705.161, 335.097, 704.469, 335.812, 704.604, 336.669);
    ctx.lineTo(702.399, 337.118);
    ctx.bezierCurveTo(702.049, 335.204, 703.494, 333.53, 705.768, 333.067);
    ctx.bezierCurveTo(708.08, 332.597, 709.898, 333.654, 710.247, 335.408);
    ctx.bezierCurveTo(710.507, 336.647, 709.821, 337.703, 708.627, 338.178);
    ctx.lineTo(708.645, 338.27);
    ctx.bezierCurveTo(710.316, 338.137, 711.413, 338.942, 711.68, 340.331);
    ctx.bezierCurveTo(712.082, 342.226, 710.56, 343.955, 708.057, 344.465);
    ctx.closePath();
    ctx.moveTo(718.04, 342.432);
    ctx.bezierCurveTo(715.728, 342.903, 713.758, 341.885, 713.33, 339.979);
    ctx.lineTo(715.566, 339.524);
    ctx.bezierCurveTo(715.796, 340.378, 716.706, 340.814, 717.669, 340.61);
    ctx.bezierCurveTo(718.819, 340.384, 719.472, 339.406, 719.229, 338.251);
    ctx.bezierCurveTo(718.997, 337.071, 717.996, 336.422, 716.831, 336.651);
    ctx.bezierCurveTo(715.996, 336.821, 715.478, 337.253, 715.275, 337.94);
    ctx.lineTo(713.193, 338.364);
    ctx.lineTo(712.49, 331.858);
    ctx.lineTo(719.319, 330.468);
    ctx.lineTo(719.702, 332.351);
    ctx.lineTo(714.772, 333.355);
    ctx.lineTo(715.036, 336.219);
    ctx.lineTo(715.128, 336.2);
    ctx.bezierCurveTo(715.423, 335.574, 716.184, 335.005, 717.158, 334.814);
    ctx.bezierCurveTo(719.193, 334.392, 721.019, 335.607, 721.452, 337.735);
    ctx.bezierCurveTo(721.909, 339.978, 720.535, 341.924, 718.04, 342.432);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 19번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(745.257, 250.369);
    ctx.lineTo(747.946, 261.357);
    ctx.lineTo(745.655, 261.918);
    ctx.lineTo(743.496, 253.101);
    ctx.lineTo(743.435, 253.116);
    ctx.lineTo(741.302, 255.311);
    ctx.lineTo(740.808, 253.292);
    ctx.lineTo(743.101, 250.897);
    ctx.lineTo(745.257, 250.369);
    ctx.closePath();
    ctx.moveTo(751.776, 248.613);
    ctx.bezierCurveTo(754.054, 248.063, 756.542, 248.894, 757.519, 252.885);
    ctx.bezierCurveTo(758.44, 256.617, 757.211, 259.251, 754.449, 259.927);
    ctx.bezierCurveTo(752.241, 260.467, 750.383, 259.45, 749.708, 257.645);
    ctx.lineTo(751.954, 257.095);
    ctx.bezierCurveTo(752.342, 257.86, 753.089, 258.184, 753.97, 257.969);
    ctx.bezierCurveTo(755.464, 257.603, 755.925, 256.067, 755.396, 253.872);
    ctx.lineTo(755.305, 253.894);
    ctx.bezierCurveTo(755.026, 254.992, 754.082, 255.834, 752.861, 256.133);
    ctx.bezierCurveTo(750.888, 256.616, 749.067, 255.485, 748.55, 253.44);
    ctx.bezierCurveTo(748.009, 251.232, 749.293, 249.213, 751.776, 248.613);
    ctx.closePath();
    ctx.moveTo(750.766, 252.833);
    ctx.bezierCurveTo(751.032, 253.918, 752.035, 254.566, 753.165, 254.289);
    ctx.bezierCurveTo(754.281, 254.016, 754.921, 252.918, 754.663, 251.863);
    ctx.bezierCurveTo(754.391, 250.82, 753.354, 250.133, 752.229, 250.4);
    ctx.bezierCurveTo(751.108, 250.682, 750.483, 251.744, 750.766, 252.833);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(727.359, 277.207);
    ctx.rotate(-0.24005956663630806);
    ctx.translate(-727.359, -277.207);
    ctx.beginPath();
    ctx.moveTo(746.859, 277.207);
    ctx.lineTo(761.859, 277.207);
    ctx.bezierCurveTo(
      772.6285526217005,
      277.207,
      781.359,
      285.9374473782995,
      781.359,
      296.707
    );
    ctx.lineTo(781.359, 296.707);
    ctx.bezierCurveTo(
      781.359,
      307.4765526217005,
      772.6285526217005,
      316.207,
      761.859,
      316.207
    );
    ctx.lineTo(746.859, 316.207);
    ctx.bezierCurveTo(
      736.0894473782996,
      316.207,
      727.359,
      307.4765526217005,
      727.359,
      296.707
    );
    ctx.lineTo(727.359, 296.707);
    ctx.bezierCurveTo(
      727.359,
      285.9374473782995,
      736.0894473782996,
      277.207,
      746.859,
      277.207
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(771.691, 269.947);
    ctx.lineTo(744.1, 309.743);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(737.137, 278.405);
    ctx.lineTo(779.767, 300.841);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(762.977, 331.965);
    ctx.bezierCurveTo(760.564, 332.555, 758.509, 331.651, 757.994, 329.742);
    ctx.lineTo(760.3, 329.178);
    ctx.bezierCurveTo(760.539, 329.988, 761.468, 330.332, 762.532, 330.079);
    ctx.bezierCurveTo(763.623, 329.804, 764.256, 329.006, 764.031, 328.12);
    ctx.bezierCurveTo(763.819, 327.222, 762.868, 326.788, 761.576, 327.096);
    ctx.lineTo(760.56, 327.345);
    ctx.lineTo(760.147, 325.66);
    ctx.lineTo(761.164, 325.411);
    ctx.bezierCurveTo(762.249, 325.145, 762.863, 324.368, 762.644, 323.505);
    ctx.bezierCurveTo(762.445, 322.66, 761.659, 322.242, 760.69, 322.487);
    ctx.bezierCurveTo(759.716, 322.717, 759.053, 323.458, 759.222, 324.31);
    ctx.lineTo(757.036, 324.845);
    ctx.bezierCurveTo(756.612, 322.946, 757.99, 321.217, 760.244, 320.666);
    ctx.bezierCurveTo(762.536, 320.105, 764.393, 321.09, 764.811, 322.829);
    ctx.bezierCurveTo(765.12, 324.057, 764.476, 325.139, 763.3, 325.66);
    ctx.lineTo(763.323, 325.751);
    ctx.bezierCurveTo(764.987, 325.553, 766.115, 326.315, 766.436, 327.692);
    ctx.bezierCurveTo(766.911, 329.57, 765.459, 331.357, 762.977, 331.965);
    ctx.closePath();
    ctx.moveTo(773.085, 329.491);
    ctx.bezierCurveTo(770.814, 330.038, 768.312, 329.211, 767.335, 325.187);
    ctx.bezierCurveTo(766.434, 321.475, 767.665, 318.849, 770.412, 318.177);
    ctx.bezierCurveTo(772.621, 317.636, 774.48, 318.661, 775.142, 320.413);
    ctx.lineTo(772.896, 320.963);
    ctx.bezierCurveTo(772.526, 320.241, 771.779, 319.917, 770.892, 320.134);
    ctx.bezierCurveTo(769.397, 320.5, 768.944, 322.035, 769.458, 324.201);
    ctx.lineTo(769.564, 324.175);
    ctx.bezierCurveTo(769.833, 323.072, 770.779, 322.237, 771.993, 321.94);
    ctx.bezierCurveTo(773.974, 321.455, 775.808, 322.575, 776.308, 324.648);
    ctx.bezierCurveTo(776.861, 326.877, 775.569, 328.891, 773.085, 329.491);
    ctx.closePath();
    ctx.moveTo(770.191, 326.21);
    ctx.bezierCurveTo(770.47, 327.283, 771.507, 327.971, 772.628, 327.688);
    ctx.bezierCurveTo(773.761, 327.419, 774.378, 326.359, 774.107, 325.251);
    ctx.bezierCurveTo(773.837, 324.151, 772.817, 323.5, 771.7, 323.765);
    ctx.bezierCurveTo(770.579, 324.048, 769.944, 325.136, 770.191, 326.21);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 18번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(796.011, 235.944);
    ctx.lineTo(799.226, 246.79);
    ctx.lineTo(796.964, 247.46);
    ctx.lineTo(794.384, 238.757);
    ctx.lineTo(794.324, 238.774);
    ctx.lineTo(792.299, 241.07);
    ctx.lineTo(791.708, 239.077);
    ctx.lineTo(793.884, 236.574);
    ctx.lineTo(796.011, 235.944);
    ctx.closePath();
    ctx.moveTo(805.862, 244.986);
    ctx.bezierCurveTo(803.383, 245.721, 801.221, 244.944, 800.697, 243.176);
    ctx.bezierCurveTo(800.293, 241.813, 801.013, 240.393, 802.299, 239.784);
    ctx.lineTo(802.277, 239.709);
    ctx.bezierCurveTo(801.034, 239.833, 799.935, 239.091, 799.589, 237.898);
    ctx.bezierCurveTo(799.082, 236.215, 800.341, 234.497, 802.558, 233.84);
    ctx.bezierCurveTo(804.775, 233.183, 806.767, 233.937, 807.274, 235.62);
    ctx.bezierCurveTo(807.62, 236.813, 807.079, 238.041, 806.007, 238.603);
    ctx.lineTo(806.029, 238.678);
    ctx.bezierCurveTo(807.41, 238.497, 808.802, 239.29, 809.206, 240.653);
    ctx.bezierCurveTo(809.73, 242.421, 808.334, 244.253, 805.862, 244.986);
    ctx.closePath();
    ctx.moveTo(802.983, 242.254);
    ctx.bezierCurveTo(803.25, 243.185, 804.208, 243.61, 805.351, 243.263);
    ctx.bezierCurveTo(806.5, 242.931, 807.07, 242.053, 806.788, 241.126);
    ctx.bezierCurveTo(806.518, 240.187, 805.504, 239.738, 804.401, 240.057);
    ctx.bezierCurveTo(803.302, 240.391, 802.695, 241.312, 802.983, 242.254);
    ctx.closePath();
    ctx.moveTo(801.858, 237.47);
    ctx.bezierCurveTo(802.108, 238.341, 802.969, 238.746, 803.93, 238.469);
    ctx.bezierCurveTo(804.879, 238.18, 805.389, 237.369, 805.139, 236.498);
    ctx.bezierCurveTo(804.88, 235.653, 804.031, 235.261, 803.064, 235.548);
    ctx.bezierCurveTo(802.091, 235.837, 801.599, 236.626, 801.858, 237.47);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(779.425, 263.611);
    ctx.rotate(-0.28818003944304377);
    ctx.translate(-779.425, -263.611);
    ctx.beginPath();
    ctx.moveTo(798.925, 263.611);
    ctx.lineTo(813.925, 263.611);
    ctx.bezierCurveTo(
      824.6945526217004,
      263.611,
      833.425,
      272.3414473782995,
      833.425,
      283.111
    );
    ctx.lineTo(833.425, 283.111);
    ctx.bezierCurveTo(
      833.425,
      293.8805526217005,
      824.6945526217004,
      302.611,
      813.925,
      302.611
    );
    ctx.lineTo(798.925, 302.611);
    ctx.bezierCurveTo(
      788.1554473782995,
      302.611,
      779.425,
      293.8805526217005,
      779.425,
      283.111
    );
    ctx.lineTo(779.425, 283.111);
    ctx.bezierCurveTo(
      779.425,
      272.3414473782995,
      788.1554473782995,
      263.611,
      798.925,
      263.611
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(823.357, 254.227);
    ctx.lineTo(797.712, 295.304);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(789.249, 264.338);
    ctx.lineTo(832.91, 284.697);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(818.116, 316.45);
    ctx.bezierCurveTo(815.734, 317.156, 813.637, 316.352, 813.031, 314.47);
    ctx.lineTo(815.308, 313.795);
    ctx.bezierCurveTo(815.585, 314.593, 816.529, 314.891, 817.58, 314.588);
    ctx.bezierCurveTo(818.656, 314.261, 819.25, 313.433, 818.983, 312.559);
    ctx.bezierCurveTo(818.728, 311.673, 817.758, 311.284, 816.482, 311.654);
    ctx.lineTo(815.478, 311.952);
    ctx.lineTo(814.985, 310.289);
    ctx.lineTo(815.989, 309.991);
    ctx.bezierCurveTo(817.06, 309.674, 817.636, 308.867, 817.376, 308.016);
    ctx.bezierCurveTo(817.137, 307.182, 816.331, 306.802, 815.375, 307.093);
    ctx.bezierCurveTo(814.414, 307.37, 813.787, 308.142, 813.996, 308.985);
    ctx.lineTo(811.839, 309.624);
    ctx.bezierCurveTo(811.323, 307.748, 812.617, 305.955, 814.842, 305.296);
    ctx.bezierCurveTo(817.104, 304.625, 819.007, 305.519, 819.508, 307.237);
    ctx.bezierCurveTo(819.875, 308.448, 819.284, 309.56, 818.135, 310.137);
    ctx.lineTo(818.161, 310.227);
    ctx.bezierCurveTo(819.815, 309.949, 820.977, 310.656, 821.364, 312.016);
    ctx.bezierCurveTo(821.93, 313.869, 820.565, 315.724, 818.116, 316.45);
    ctx.closePath();
    ctx.moveTo(824.273, 314.462);
    ctx.lineTo(826.196, 304.179);
    ctx.lineTo(826.173, 304.104);
    ctx.lineTo(820.855, 305.68);
    ctx.lineTo(820.309, 303.838);
    ctx.lineTo(827.964, 301.569);
    ctx.lineTo(828.519, 303.441);
    ctx.lineTo(826.61, 313.769);
    ctx.lineTo(824.273, 314.462);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 17번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(847.208, 217.873);
    ctx.lineTo(850.645, 228.65);
    ctx.lineTo(848.397, 229.367);
    ctx.lineTo(845.639, 220.718);
    ctx.lineTo(845.579, 220.737);
    ctx.lineTo(843.602, 223.074);
    ctx.lineTo(842.97, 221.094);
    ctx.lineTo(845.094, 218.547);
    ctx.lineTo(847.208, 217.873);
    ctx.closePath();
    ctx.moveTo(853.563, 227.72);
    ctx.lineTo(855.273, 217.399);
    ctx.lineTo(855.249, 217.325);
    ctx.lineTo(849.965, 219.01);
    ctx.lineTo(849.381, 217.179);
    ctx.lineTo(856.988, 214.753);
    ctx.lineTo(857.581, 216.614);
    ctx.lineTo(855.885, 226.979);
    ctx.lineTo(853.563, 227.72);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(830.718, 246.027);
    ctx.rotate(-0.3087557259948049);
    ctx.translate(-830.718, -246.027);
    ctx.beginPath();
    ctx.moveTo(850.218, 246.027);
    ctx.lineTo(865.218, 246.027);
    ctx.bezierCurveTo(
      875.9875526217004,
      246.027,
      884.718,
      254.7574473782995,
      884.718,
      265.527
    );
    ctx.lineTo(884.718, 265.527);
    ctx.bezierCurveTo(
      884.718,
      276.29655262170047,
      875.9875526217004,
      285.027,
      865.218,
      285.027
    );
    ctx.lineTo(850.218, 285.027);
    ctx.bezierCurveTo(
      839.4484473782995,
      285.027,
      830.718,
      276.29655262170047,
      830.718,
      265.527
    );
    ctx.lineTo(830.718, 265.527);
    ctx.bezierCurveTo(
      830.718,
      254.7574473782995,
      839.4484473782995,
      246.027,
      850.218,
      246.027
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(874.447, 235.741);
    ctx.lineTo(849.653, 277.337);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(840.555, 246.552);
    ctx.lineTo(884.625, 266.008);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(870.011, 298.211);
    ctx.bezierCurveTo(867.644, 298.966, 865.532, 298.205, 864.886, 296.336);
    ctx.lineTo(867.149, 295.614);
    ctx.bezierCurveTo(867.443, 296.406, 868.393, 296.685, 869.437, 296.36);
    ctx.bezierCurveTo(870.507, 296.011, 871.083, 295.171, 870.798, 294.303);
    ctx.bezierCurveTo(870.525, 293.422, 869.547, 293.053, 868.279, 293.45);
    ctx.lineTo(867.282, 293.768);
    ctx.lineTo(866.755, 292.115);
    ctx.lineTo(867.752, 291.797);
    ctx.bezierCurveTo(868.816, 291.458, 869.376, 290.64, 869.098, 289.794);
    ctx.bezierCurveTo(868.841, 288.965, 868.028, 288.601, 867.078, 288.913);
    ctx.bezierCurveTo(866.123, 289.209, 865.512, 289.994, 865.739, 290.832);
    ctx.lineTo(863.595, 291.516);
    ctx.bezierCurveTo(863.041, 289.651, 864.298, 287.831, 866.508, 287.126);
    ctx.bezierCurveTo(868.756, 286.409, 870.677, 287.264, 871.213, 288.971);
    ctx.bezierCurveTo(871.605, 290.175, 871.037, 291.299, 869.9, 291.899);
    ctx.lineTo(869.929, 291.989);
    ctx.bezierCurveTo(871.576, 291.676, 872.753, 292.359, 873.168, 293.711);
    ctx.bezierCurveTo(873.771, 295.552, 872.445, 297.435, 870.011, 298.211);
    ctx.closePath();
    ctx.moveTo(879.806, 295.087);
    ctx.bezierCurveTo(877.342, 295.873, 875.165, 295.14, 874.605, 293.384);
    ctx.bezierCurveTo(874.173, 292.029, 874.864, 290.595, 876.137, 289.959);
    ctx.lineTo(876.113, 289.885);
    ctx.bezierCurveTo(874.873, 290.034, 873.759, 289.316, 873.389, 288.13);
    ctx.bezierCurveTo(872.847, 286.457, 874.07, 284.714, 876.274, 284.012);
    ctx.bezierCurveTo(878.477, 283.309, 880.484, 284.022, 881.025, 285.694);
    ctx.bezierCurveTo(881.395, 286.88, 880.88, 288.118, 879.82, 288.703);
    ctx.lineTo(879.844, 288.777);
    ctx.bezierCurveTo(881.22, 288.568, 882.628, 289.332, 883.06, 290.687);
    ctx.bezierCurveTo(883.621, 292.443, 882.262, 294.303, 879.806, 295.087);
    ctx.closePath();
    ctx.moveTo(876.871, 292.415);
    ctx.bezierCurveTo(877.158, 293.34, 878.124, 293.746, 879.26, 293.375);
    ctx.bezierCurveTo(880.401, 293.019, 880.954, 292.129, 880.652, 291.209);
    ctx.bezierCurveTo(880.363, 290.276, 879.341, 289.848, 878.244, 290.189);
    ctx.bezierCurveTo(877.152, 290.546, 876.564, 291.479, 876.871, 292.415);
    ctx.closePath();
    ctx.moveTo(875.648, 287.655);
    ctx.bezierCurveTo(875.916, 288.521, 876.786, 288.908, 877.741, 288.611);
    ctx.bezierCurveTo(878.684, 288.302, 879.176, 287.481, 878.908, 286.615);
    ctx.bezierCurveTo(878.632, 285.777, 877.775, 285.402, 876.815, 285.709);
    ctx.bezierCurveTo(875.847, 286.017, 875.372, 286.816, 875.648, 287.655);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 2번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(94.2951, 69.379);
    ctx.lineTo(97.9015, 80.1013);
    ctx.lineTo(95.6652, 80.8534);
    ctx.lineTo(92.7712, 72.2489);
    ctx.lineTo(92.7119, 72.2689);
    ctx.lineTo(90.7715, 74.6359);
    ctx.lineTo(90.1091, 72.6662);
    ctx.lineTo(92.1922, 70.0863);
    ctx.lineTo(94.2951, 69.379);
    ctx.closePath();
    ctx.moveTo(100.804, 79.125);
    ctx.lineTo(102.352, 68.7791);
    ctx.lineTo(102.328, 68.7051);
    ctx.lineTo(97.07, 70.4734);
    ctx.lineTo(96.4574, 68.6518);
    ctx.lineTo(104.025, 66.1064);
    ctx.lineTo(104.648, 67.9576);
    ctx.lineTo(103.114, 78.3479);
    ctx.lineTo(100.804, 79.125);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(78.2492, 97.7891);
    ctx.rotate(-0.32445321728724186);
    ctx.translate(-78.2492, -97.7891);
    ctx.beginPath();
    ctx.moveTo(97.7492, 97.7891);
    ctx.lineTo(112.7492, 97.7891);
    ctx.bezierCurveTo(
      123.51875262170047,
      97.7891,
      132.2492,
      106.51954737829954,
      132.2492,
      117.2891
    );
    ctx.lineTo(132.2492, 117.28910000000002);
    ctx.bezierCurveTo(
      132.2492,
      128.0586526217005,
      123.51875262170047,
      136.78910000000002,
      112.7492,
      136.78910000000002
    );
    ctx.lineTo(97.7492, 136.78910000000002);
    ctx.bezierCurveTo(
      86.97964737829953,
      136.78910000000002,
      78.2492,
      128.0586526217005,
      78.2492,
      117.28910000000002
    );
    ctx.lineTo(78.2492, 117.2891);
    ctx.bezierCurveTo(
      78.2492,
      106.51954737829954,
      86.97964737829953,
      97.7891,
      97.7492,
      97.7891
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(121.812, 86.818);
    ctx.lineTo(97.6735, 128.798);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(88.0933, 98.1589);
    ctx.lineTo(132.464, 116.921);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(119.551, 148.783);
    ctx.lineTo(118.993, 147.174);
    ctx.lineTo(121.653, 142.339);
    ctx.bezierCurveTo(122.332, 141.048, 122.673, 140.273, 122.387, 139.422);
    ctx.bezierCurveTo(122.065, 138.467, 121.148, 138.116, 120.166, 138.455);
    ctx.bezierCurveTo(119.156, 138.786, 118.711, 139.644, 119.065, 140.671);
    ctx.lineTo(116.947, 141.383);
    ctx.bezierCurveTo(116.235, 139.29, 117.339, 137.427, 119.597, 136.667);
    ctx.bezierCurveTo(121.893, 135.895, 123.859, 136.693, 124.484, 138.552);
    ctx.bezierCurveTo(124.895, 139.773, 124.639, 140.972, 123.105, 143.714);
    ctx.lineTo(122.009, 145.846);
    ctx.lineTo(122.029, 145.906);
    ctx.lineTo(126.605, 144.366);
    ctx.lineTo(127.223, 146.203);
    ctx.lineTo(119.551, 148.783);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 3번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(147.538, 53.1237);
    ctx.lineTo(150.489, 64.0446);
    ctx.lineTo(148.211, 64.66);
    ctx.lineTo(145.843, 55.8962);
    ctx.lineTo(145.783, 55.9125);
    ctx.lineTo(143.703, 58.1578);
    ctx.lineTo(143.161, 56.1516);
    ctx.lineTo(145.396, 53.7025);
    ctx.lineTo(147.538, 53.1237);
    ctx.closePath();
    ctx.moveTo(157.287, 62.3696);
    ctx.bezierCurveTo(155.03, 62.9713, 152.508, 62.2041, 151.436, 58.2048);
    ctx.bezierCurveTo(150.447, 54.5147, 151.615, 51.8603, 154.345, 51.1226);
    ctx.bezierCurveTo(156.54, 50.5296, 158.423, 51.5097, 159.127, 53.2456);
    ctx.lineTo(156.895, 53.8488);
    ctx.bezierCurveTo(156.508, 53.136, 155.753, 52.83, 154.871, 53.0684);
    ctx.bezierCurveTo(153.385, 53.4699, 152.969, 55.0147, 153.535, 57.1683);
    ctx.lineTo(153.64, 57.1397);
    ctx.bezierCurveTo(153.883, 56.0303, 154.809, 55.1732, 156.015, 54.8472);
    ctx.bezierCurveTo(157.984, 54.3153, 159.844, 55.3906, 160.393, 57.4517);
    ctx.bezierCurveTo(161, 59.667, 159.755, 61.7108, 157.287, 62.3696);
    ctx.closePath();
    ctx.moveTo(154.315, 59.1586);
    ctx.bezierCurveTo(154.62, 60.2255, 155.673, 60.8878, 156.787, 60.5786);
    ctx.bezierCurveTo(157.913, 60.2826, 158.505, 59.2082, 158.207, 58.107);
    ctx.bezierCurveTo(157.912, 57.0134, 156.876, 56.3868, 155.766, 56.6788);
    ctx.bezierCurveTo(154.651, 56.9879, 154.043, 58.0911, 154.315, 59.1586);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(130.285, 80.3802);
    ctx.rotate(-0.26389378290154264);
    ctx.translate(-130.285, -80.3802);
    ctx.beginPath();
    ctx.moveTo(149.785, 80.3802);
    ctx.lineTo(164.785, 80.3802);
    ctx.bezierCurveTo(
      175.55455262170048,
      80.3802,
      184.285,
      89.11064737829953,
      184.285,
      99.8802
    );
    ctx.lineTo(184.285, 99.8802);
    ctx.bezierCurveTo(
      184.285,
      110.64975262170047,
      175.55455262170048,
      119.3802,
      164.785,
      119.3802
    );
    ctx.lineTo(149.785, 119.3802);
    ctx.bezierCurveTo(
      139.0154473782995,
      119.3802,
      130.285,
      110.64975262170047,
      130.285,
      99.8802
    );
    ctx.lineTo(130.285, 99.8802);
    ctx.bezierCurveTo(
      130.285,
      89.11064737829953,
      139.0154473782995,
      80.3802,
      149.785,
      80.3802
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(174.432, 72.0658);
    ctx.lineTo(147.797, 112.508);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(140.088, 81.3452);
    ctx.lineTo(183.242, 102.759);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(172.025, 132.969);
    ctx.bezierCurveTo(169.627, 133.617, 167.55, 132.762, 166.99, 130.866);
    ctx.lineTo(169.282, 130.247);
    ctx.bezierCurveTo(169.54, 131.051, 170.477, 131.373, 171.535, 131.095);
    ctx.bezierCurveTo(172.619, 130.794, 173.233, 129.981, 172.987, 129.1);
    ctx.bezierCurveTo(172.754, 128.208, 171.793, 127.796, 170.508, 128.135);
    ctx.lineTo(169.498, 128.408);
    ctx.lineTo(169.045, 126.734);
    ctx.lineTo(170.056, 126.461);
    ctx.bezierCurveTo(171.135, 126.169, 171.73, 125.377, 171.49, 124.519);
    ctx.bezierCurveTo(171.271, 123.68, 170.475, 123.28, 169.512, 123.549);
    ctx.bezierCurveTo(168.545, 123.802, 167.899, 124.559, 168.088, 125.406);
    ctx.lineTo(165.916, 125.993);
    ctx.bezierCurveTo(165.446, 124.105, 166.783, 122.344, 169.023, 121.739);
    ctx.bezierCurveTo(171.301, 121.123, 173.181, 122.064, 173.64, 123.793);
    ctx.bezierCurveTo(173.978, 125.013, 173.36, 126.11, 172.198, 126.659);
    ctx.lineTo(172.222, 126.749);
    ctx.bezierCurveTo(173.881, 126.512, 175.027, 127.246, 175.38, 128.615);
    ctx.bezierCurveTo(175.901, 130.482, 174.491, 132.303, 172.025, 132.969);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 4번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(202.202, 39.865);
    ctx.lineTo(204.625, 50.9149);
    ctx.lineTo(202.32, 51.4203);
    ctx.lineTo(200.376, 42.5529);
    ctx.lineTo(200.315, 42.5663);
    ctx.lineTo(198.129, 44.7092);
    ctx.lineTo(197.684, 42.6793);
    ctx.lineTo(200.034, 40.3403);
    ctx.lineTo(202.202, 39.865);
    ctx.closePath();
    ctx.moveTo(211.282, 49.6149);
    ctx.bezierCurveTo(208.978, 50.1203, 206.993, 49.1318, 206.537, 47.2324);
    ctx.lineTo(208.765, 46.7437);
    ctx.bezierCurveTo(209.008, 47.5943, 209.924, 48.0171, 210.884, 47.7986);
    ctx.bezierCurveTo(212.03, 47.5552, 212.67, 46.5673, 212.409, 45.4166);
    ctx.bezierCurveTo(212.159, 44.2398, 211.148, 43.6057, 209.987, 43.8524);
    ctx.bezierCurveTo(209.155, 44.0348, 208.643, 44.4749, 208.451, 45.165);
    ctx.lineTo(206.375, 45.6202);
    ctx.lineTo(205.575, 39.1253);
    ctx.lineTo(212.382, 37.6325);
    ctx.lineTo(212.793, 39.5097);
    ctx.lineTo(207.879, 40.5875);
    ctx.lineTo(208.186, 43.4474);
    ctx.lineTo(208.278, 43.4273);
    ctx.bezierCurveTo(208.563, 42.7968, 209.316, 42.2159, 210.287, 42.011);
    ctx.bezierCurveTo(212.315, 41.5582, 214.159, 42.7455, 214.624, 44.8669);
    ctx.bezierCurveTo(215.114, 47.1028, 213.77, 49.0693, 211.282, 49.6149);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(183.66, 66.2621);
    ctx.rotate(-0.2158867564961866);
    ctx.translate(-183.66, -66.2621);
    ctx.beginPath();
    ctx.moveTo(203.16, 66.2621);
    ctx.lineTo(218.16, 66.2621);
    ctx.bezierCurveTo(
      228.92955262170048,
      66.2621,
      237.66,
      74.99254737829953,
      237.66,
      85.7621
    );
    ctx.lineTo(237.66, 85.7621);
    ctx.bezierCurveTo(
      237.66,
      96.53165262170047,
      228.92955262170048,
      105.2621,
      218.16,
      105.2621
    );
    ctx.lineTo(203.16, 105.2621);
    ctx.bezierCurveTo(
      192.3904473782995,
      105.2621,
      183.66,
      96.53165262170047,
      183.66,
      85.7621
    );
    ctx.lineTo(183.66, 85.7621);
    ctx.bezierCurveTo(
      183.66,
      74.99254737829953,
      192.3904473782995,
      66.2621,
      203.16,
      66.2621
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(228.155, 60.0757);
    ctx.lineTo(199.61, 99.1932);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(193.406, 67.6964);
    ctx.lineTo(235.482, 91.1559);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(218.094, 119.625);
    ctx.lineTo(217.695, 117.808);
    ctx.lineTo(220.769, 109.504);
    ctx.lineTo(223.654, 108.872);
    ctx.lineTo(225.244, 116.121);
    ctx.lineTo(226.648, 115.813);
    ctx.lineTo(227.053, 117.66);
    ctx.lineTo(225.649, 117.968);
    ctx.lineTo(226.077, 119.921);
    ctx.lineTo(223.895, 120.4);
    ctx.lineTo(223.466, 118.446);
    ctx.lineTo(218.094, 119.625);
    ctx.closePath();
    ctx.moveTo(220.009, 117.269);
    ctx.lineTo(223.092, 116.593);
    ctx.lineTo(222.054, 111.862);
    ctx.lineTo(221.947, 111.885);
    ctx.lineTo(219.989, 117.178);
    ctx.lineTo(220.009, 117.269);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 5번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(257.068, 30.2614);
    ctx.lineTo(259.061, 41.3969);
    ctx.lineTo(256.739, 41.8126);
    ctx.lineTo(255.139, 32.8765);
    ctx.lineTo(255.078, 32.8876);
    ctx.lineTo(252.811, 34.9442);
    ctx.lineTo(252.445, 32.8986);
    ctx.lineTo(254.884, 30.6524);
    ctx.lineTo(257.068, 30.2614);
    ctx.closePath();
    ctx.moveTo(261.108, 38.9987);
    ctx.lineTo(260.781, 37.1684);
    ctx.lineTo(264.174, 28.9894);
    ctx.lineTo(267.081, 28.469);
    ctx.lineTo(268.389, 35.7748);
    ctx.lineTo(269.804, 35.5215);
    ctx.lineTo(270.137, 37.3825);
    ctx.lineTo(268.722, 37.6358);
    ctx.lineTo(269.074, 39.6045);
    ctx.lineTo(266.875, 39.9982);
    ctx.lineTo(266.522, 38.0295);
    ctx.lineTo(261.108, 38.9987);
    ctx.closePath();
    ctx.moveTo(263.113, 36.7191);
    ctx.lineTo(266.22, 36.163);
    ctx.lineTo(265.366, 31.395);
    ctx.lineTo(265.259, 31.4143);
    ctx.lineTo(263.097, 36.6268);
    ctx.lineTo(263.113, 36.7191);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(238.01, 55.8322);
    ctx.rotate(-0.1771334657849045);
    ctx.translate(-238.01, -55.8322);
    ctx.beginPath();
    ctx.moveTo(257.51, 55.8322);
    ctx.lineTo(272.51, 55.8322);
    ctx.bezierCurveTo(
      283.2795526217005,
      55.8322,
      292.01,
      64.56264737829953,
      292.01,
      75.3322
    );
    ctx.lineTo(292.01, 75.3322);
    ctx.bezierCurveTo(
      292.01,
      86.10175262170047,
      283.2795526217005,
      94.8322,
      272.51,
      94.8322
    );
    ctx.lineTo(257.51, 94.8322);
    ctx.bezierCurveTo(
      246.7404473782995,
      94.8322,
      238.01,
      86.10175262170047,
      238.01,
      75.3322
    );
    ctx.lineTo(238.01, 75.3322);
    ctx.bezierCurveTo(
      238.01,
      64.56264737829953,
      246.7404473782995,
      55.8322,
      257.51,
      55.8322
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(282.711, 51.3744);
    ctx.lineTo(252.672, 89.3566);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(247.693, 57.643);
    ctx.lineTo(288.829, 82.7151);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(275.006, 111.846);
    ctx.bezierCurveTo(272.683, 112.262, 270.739, 111.197, 270.356, 109.281);
    ctx.lineTo(272.602, 108.879);
    ctx.bezierCurveTo(272.811, 109.739, 273.711, 110.197, 274.678, 110.016);
    ctx.bezierCurveTo(275.833, 109.817, 276.51, 108.854, 276.295, 107.694);
    ctx.bezierCurveTo(276.09, 106.509, 275.105, 105.836, 273.934, 106.038);
    ctx.bezierCurveTo(273.096, 106.188, 272.568, 106.608, 272.349, 107.29);
    ctx.lineTo(270.257, 107.664);
    ctx.lineTo(269.709, 101.143);
    ctx.lineTo(276.569, 99.9151);
    ctx.lineTo(276.907, 101.807);
    ctx.lineTo(271.955, 102.693);
    ctx.lineTo(272.151, 105.563);
    ctx.lineTo(272.243, 105.547);
    ctx.bezierCurveTo(272.553, 104.928, 273.328, 104.376, 274.306, 104.209);
    ctx.bezierCurveTo(276.35, 103.835, 278.146, 105.093, 278.529, 107.231);
    ctx.bezierCurveTo(278.932, 109.484, 277.513, 111.397, 275.006, 111.846);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 6번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(314.209, 21.7509);
    ctx.lineTo(315.522, 32.9869);
    ctx.lineTo(313.178, 33.2608);
    ctx.lineTo(312.125, 24.2441);
    ctx.lineTo(312.062, 24.2513);
    ctx.lineTo(309.675, 26.1665);
    ctx.lineTo(309.434, 24.1024);
    ctx.lineTo(312.005, 22.0085);
    ctx.lineTo(314.209, 21.7509);
    ctx.closePath();
    ctx.moveTo(322.306, 32.3512);
    ctx.bezierCurveTo(319.839, 32.6396, 317.911, 31.4885, 317.635, 29.5307);
    ctx.lineTo(319.994, 29.255);
    ctx.bezierCurveTo(320.131, 30.0885, 321.01, 30.5442, 322.097, 30.425);
    ctx.bezierCurveTo(323.214, 30.2866, 323.94, 29.5725, 323.826, 28.6655);
    ctx.bezierCurveTo(323.727, 27.7489, 322.837, 27.2001, 321.517, 27.3465);
    ctx.lineTo(320.477, 27.4681);
    ctx.lineTo(320.276, 25.7454);
    ctx.lineTo(321.316, 25.6239);
    ctx.bezierCurveTo(322.425, 25.4942, 323.13, 24.7982, 323.019, 23.9145);
    ctx.bezierCurveTo(322.926, 23.0523, 322.198, 22.5397, 321.206, 22.6635);
    ctx.bezierCurveTo(320.211, 22.7719, 319.462, 23.4258, 319.524, 24.2916);
    ctx.lineTo(317.289, 24.5529);
    ctx.bezierCurveTo(317.102, 22.6162, 318.683, 21.0706, 320.988, 20.8012);
    ctx.bezierCurveTo(323.331, 20.5273, 325.053, 21.734, 325.253, 23.5118);
    ctx.bezierCurveTo(325.408, 24.768, 324.635, 25.7629, 323.405, 26.1348);
    ctx.lineTo(323.416, 26.2279);
    ctx.bezierCurveTo(325.092, 26.2365, 326.117, 27.1314, 326.265, 28.5377);
    ctx.bezierCurveTo(326.506, 30.4603, 324.844, 32.0546, 322.306, 32.3512);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(293.136, 46.175);
    ctx.rotate(-0.11635603977563116);
    ctx.translate(-293.136, -46.175);
    ctx.beginPath();
    ctx.moveTo(312.636, 46.175);
    ctx.lineTo(327.636, 46.175);
    ctx.bezierCurveTo(
      338.4055526217005,
      46.175,
      347.136,
      54.90544737829952,
      347.136,
      65.675
    );
    ctx.lineTo(347.136, 65.675);
    ctx.bezierCurveTo(
      347.136,
      76.44455262170047,
      338.4055526217005,
      85.175,
      327.636,
      85.175
    );
    ctx.lineTo(312.636, 85.175);
    ctx.bezierCurveTo(
      301.86644737829954,
      85.175,
      293.136,
      76.44455262170047,
      293.136,
      65.675
    );
    ctx.lineTo(293.136, 65.675);
    ctx.bezierCurveTo(
      293.136,
      54.90544737829952,
      301.86644737829954,
      46.175,
      312.636,
      46.175
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(338.026, 44.4406);
    ctx.lineTo(305.735, 80.528);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(302.691, 48.5706);
    ctx.lineTo(342.228, 76.095);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(326.878, 104.307);
    ctx.bezierCurveTo(324.557, 104.57, 322.176, 103.441, 321.703, 99.3273);
    ctx.bezierCurveTo(321.267, 95.5319, 322.813, 93.0781, 325.622, 92.7497);
    ctx.bezierCurveTo(327.88, 92.4858, 329.599, 93.7322, 330.04, 95.5527);
    ctx.lineTo(327.743, 95.8211);
    ctx.bezierCurveTo(327.465, 95.0592, 326.764, 94.6456, 325.856, 94.7517);
    ctx.bezierCurveTo(324.327, 94.9304, 323.689, 96.3973, 323.932, 98.6106);
    ctx.lineTo(324.04, 98.5979);
    ctx.bezierCurveTo(324.443, 97.5361, 325.485, 96.8245, 326.726, 96.6793);
    ctx.bezierCurveTo(328.752, 96.4426, 330.434, 97.7798, 330.674, 99.8991);
    ctx.bezierCurveTo(330.948, 102.18, 329.417, 104.018, 326.878, 104.307);
    ctx.closePath();
    ctx.moveTo(324.411, 100.694);
    ctx.bezierCurveTo(324.555, 101.794, 325.5, 102.604, 326.647, 102.462);
    ctx.bezierCurveTo(327.804, 102.335, 328.548, 101.359, 328.415, 100.226);
    ctx.bezierCurveTo(328.284, 99.1009, 327.352, 98.3288, 326.21, 98.4544);
    ctx.bezierCurveTo(325.063, 98.5964, 324.299, 99.5981, 324.411, 100.694);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 7번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(371.617, 16.2611);
    ctx.lineTo(372.125, 27.5623);
    ctx.lineTo(369.768, 27.6681);
    ctx.lineTo(369.361, 18.5991);
    ctx.lineTo(369.298, 18.6019);
    ctx.lineTo(366.78, 20.3416);
    ctx.lineTo(366.687, 18.2655);
    ctx.lineTo(369.401, 16.3606);
    ctx.lineTo(371.617, 16.2611);
    ctx.closePath();
    ctx.moveTo(374.747, 27.4446);
    ctx.lineTo(374.655, 25.7438);
    ctx.lineTo(378.546, 21.831);
    ctx.bezierCurveTo(379.554, 20.7769, 380.096, 20.127, 380.056, 19.2295);
    ctx.bezierCurveTo(380.011, 18.2227, 379.226, 17.6323, 378.188, 17.6867);
    ctx.bezierCurveTo(377.126, 17.7265, 376.462, 18.4289, 376.518, 19.5134);
    ctx.lineTo(374.286, 19.6136);
    ctx.bezierCurveTo(374.179, 17.4052, 375.755, 15.919, 378.135, 15.8122);
    ctx.bezierCurveTo(380.554, 15.7036, 382.224, 17.0128, 382.312, 18.9718);
    ctx.bezierCurveTo(382.37, 20.2595, 381.793, 21.3412, 379.562, 23.5529);
    ctx.lineTo(377.92, 25.3001);
    ctx.lineTo(377.922, 25.3626);
    ctx.lineTo(382.746, 25.146);
    ctx.lineTo(382.833, 27.0816);
    ctx.lineTo(374.747, 27.4446);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(348.854, 39.1175);
    ctx.rotate(-0.04485897603353386);
    ctx.translate(-348.854, -39.1175);
    ctx.beginPath();
    ctx.moveTo(368.354, 39.1175);
    ctx.lineTo(383.354, 39.1175);
    ctx.bezierCurveTo(
      394.12355262170047,
      39.1175,
      402.854,
      47.84794737829952,
      402.854,
      58.6175
    );
    ctx.lineTo(402.854, 58.61750000000001);
    ctx.bezierCurveTo(
      402.854,
      69.38705262170048,
      394.12355262170047,
      78.1175,
      383.354,
      78.1175
    );
    ctx.lineTo(368.354, 78.1175);
    ctx.bezierCurveTo(
      357.5844473782995,
      78.1175,
      348.854,
      69.38705262170048,
      348.854,
      58.61750000000001
    );
    ctx.lineTo(348.854, 58.6175);
    ctx.bezierCurveTo(
      348.854,
      47.84794737829952,
      357.5844473782995,
      39.1175,
      368.354,
      39.1175
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(393.753, 40.5943);
    ctx.lineTo(358.966, 74.2828);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(358.214, 42.1896);
    ctx.lineTo(395.683, 72.468);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(374.916, 99.5095);
    ctx.lineTo(379.26, 89.9926);
    ctx.lineTo(379.256, 89.9146);
    ctx.lineTo(373.715, 90.1633);
    ctx.lineTo(373.629, 88.2434);
    ctx.lineTo(381.605, 87.8853);
    ctx.lineTo(381.693, 89.8365);
    ctx.lineTo(377.351, 99.4002);
    ctx.lineTo(374.916, 99.5095);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 8번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(429.168, 13.5641);
    ctx.lineTo(429.317, 24.8756);
    ctx.lineTo(426.958, 24.9069);
    ctx.lineTo(426.838, 15.8295);
    ctx.lineTo(426.775, 15.8304);
    ctx.lineTo(424.203, 17.4895);
    ctx.lineTo(424.176, 15.4116);
    ctx.lineTo(426.949, 13.5935);
    ctx.lineTo(429.168, 13.5641);
    ctx.closePath();
    ctx.moveTo(436.667, 13.4649);
    ctx.lineTo(436.817, 24.7764);
    ctx.lineTo(434.457, 24.8076);
    ctx.lineTo(434.337, 15.7303);
    ctx.lineTo(434.275, 15.7311);
    ctx.lineTo(431.703, 17.3903);
    ctx.lineTo(431.675, 15.3124);
    ctx.lineTo(434.448, 13.4942);
    ctx.lineTo(436.667, 13.4649);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(404.193, 35.7091);
    ctx.rotate(-0.013233871786784403);
    ctx.translate(-404.193, -35.7091);
    ctx.beginPath();
    ctx.moveTo(423.693, 35.7091);
    ctx.lineTo(438.693, 35.7091);
    ctx.bezierCurveTo(
      449.46255262170047,
      35.7091,
      458.193,
      44.43954737829952,
      458.193,
      55.2091
    );
    ctx.lineTo(458.193, 55.20910000000001);
    ctx.bezierCurveTo(
      458.193,
      65.97865262170048,
      449.46255262170047,
      74.7091,
      438.693,
      74.7091
    );
    ctx.lineTo(423.693, 74.7091);
    ctx.bezierCurveTo(
      412.9234473782995,
      74.7091,
      404.193,
      65.97865262170048,
      404.193,
      55.20910000000001
    );
    ctx.lineTo(404.193, 55.2091);
    ctx.bezierCurveTo(
      404.193,
      44.43954737829952,
      412.9234473782995,
      35.7091,
      423.693,
      35.7091
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(449.022, 38.6049);
    ctx.lineTo(413.188, 71.1766);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(413.451, 39.0757);
    ctx.lineTo(449.944, 70.5237);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(431.647, 97.0074);
    ctx.bezierCurveTo(429.061, 97.0416, 427.192, 95.7069, 427.167, 93.8633);
    ctx.bezierCurveTo(427.149, 92.4415, 428.227, 91.2709, 429.63, 91.0336);
    ctx.lineTo(429.629, 90.9555);
    ctx.bezierCurveTo(428.4, 90.7373, 427.542, 89.7252, 427.534, 88.483);
    ctx.bezierCurveTo(427.503, 86.7254, 429.181, 85.414, 431.493, 85.3834);
    ctx.bezierCurveTo(433.805, 85.3528, 435.518, 86.6193, 435.549, 88.3769);
    ctx.bezierCurveTo(435.557, 89.6191, 434.704, 90.6539, 433.52, 90.904);
    ctx.lineTo(433.521, 90.9821);
    ctx.bezierCurveTo(434.898, 91.1826, 436.023, 92.3241, 436.042, 93.7458);
    ctx.bezierCurveTo(436.066, 95.5894, 434.225, 96.9733, 431.647, 97.0074);
    ctx.closePath();
    ctx.moveTo(429.617, 93.5965);
    ctx.bezierCurveTo(429.622, 94.5652, 430.428, 95.2343, 431.623, 95.2107);
    ctx.bezierCurveTo(432.819, 95.2027, 433.606, 94.5125, 433.586, 93.5439);
    ctx.bezierCurveTo(433.581, 92.5674, 432.727, 91.8598, 431.579, 91.8672);
    ctx.bezierCurveTo(430.431, 91.8902, 429.596, 92.6123, 429.617, 93.5965);
    ctx.closePath();
    ctx.moveTo(429.834, 88.6869);
    ctx.bezierCurveTo(429.838, 89.5932, 430.557, 90.2166, 431.557, 90.2111);
    ctx.bezierCurveTo(432.549, 90.1902, 433.259, 89.5479, 433.255, 88.6416);
    ctx.bezierCurveTo(433.236, 87.759, 432.524, 87.1512, 431.517, 87.1645);
    ctx.bezierCurveTo(430.501, 87.178, 429.814, 87.8043, 429.834, 88.6869);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 9번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(478.985, 24.5868);
    ctx.lineTo(479.028, 22.8842);
    ctx.lineTo(483.216, 19.2912);
    ctx.bezierCurveTo(484.304, 18.3201, 484.896, 17.715, 484.927, 16.8171);
    ctx.bezierCurveTo(484.961, 15.8098, 484.225, 15.1592, 483.186, 15.1315);
    ctx.bezierCurveTo(482.125, 15.0873, 481.407, 15.7349, 481.377, 16.8205);
    ctx.lineTo(479.144, 16.7439);
    ctx.bezierCurveTo(479.212, 14.534, 480.9, 13.177, 483.282, 13.2586);
    ctx.bezierCurveTo(485.702, 13.3416, 487.263, 14.7787, 487.196, 16.7385);
    ctx.bezierCurveTo(487.152, 18.0268, 486.491, 19.0595, 484.092, 21.0879);
    ctx.lineTo(482.317, 22.6999);
    ctx.lineTo(482.315, 22.7623);
    ctx.lineTo(487.14, 22.9277);
    ctx.lineTo(487.074, 24.8641);
    ctx.lineTo(478.985, 24.5868);
    ctx.closePath();
    ctx.moveTo(494.005, 13.7825);
    ctx.lineTo(493.617, 25.0884);
    ctx.lineTo(491.259, 25.0075);
    ctx.lineTo(491.57, 15.9347);
    ctx.lineTo(491.508, 15.9326);
    ctx.lineTo(488.86, 17.4678);
    ctx.lineTo(488.931, 15.3909);
    ctx.lineTo(491.787, 13.7065);
    ctx.lineTo(494.005, 13.7825);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(459.746, 34.4336);
    ctx.rotate(0.03426203494712508);
    ctx.translate(-459.746, -34.4336);
    ctx.beginPath();
    ctx.moveTo(479.246, 34.4336);
    ctx.lineTo(494.246, 34.4336);
    ctx.bezierCurveTo(
      505.01555262170046,
      34.4336,
      513.746,
      43.16404737829952,
      513.746,
      53.9336
    );
    ctx.lineTo(513.746, 53.9336);
    ctx.bezierCurveTo(
      513.746,
      64.70315262170047,
      505.01555262170046,
      73.4336,
      494.246,
      73.4336
    );
    ctx.lineTo(479.246, 73.4336);
    ctx.bezierCurveTo(
      468.4764473782995,
      73.4336,
      459.746,
      64.70315262170047,
      459.746,
      53.9336
    );
    ctx.lineTo(459.746, 53.9336);
    ctx.bezierCurveTo(
      459.746,
      43.16404737829952,
      468.4764473782995,
      34.4336,
      479.246,
      34.4336
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(504.387, 39.4545);
    ctx.lineTo(467.047, 70.2881);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(468.833, 38.2358);
    ctx.lineTo(503.793, 71.3811);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(484.532, 85.3438);
    ctx.bezierCurveTo(486.874, 85.4319, 489.044, 86.9055, 488.903, 91.0124);
    ctx.bezierCurveTo(488.779, 94.8542, 486.882, 97.0561, 484.04, 96.9587);
    ctx.bezierCurveTo(481.768, 96.8808, 480.255, 95.3985, 480.095, 93.4778);
    ctx.lineTo(482.406, 93.557);
    ctx.bezierCurveTo(482.572, 94.3991, 483.203, 94.9132, 484.109, 94.9443);
    ctx.bezierCurveTo(485.647, 94.997, 486.507, 93.6428, 486.592, 91.3866);
    ctx.lineTo(486.498, 91.3834);
    ctx.bezierCurveTo(485.933, 92.3646, 484.796, 92.9198, 483.539, 92.8767);
    ctx.bezierCurveTo(481.509, 92.8071, 480.062, 91.2254, 480.119, 89.1167);
    ctx.bezierCurveTo(480.197, 86.8446, 481.979, 85.2484, 484.532, 85.3438);
    ctx.closePath();
    ctx.moveTo(482.417, 89.1329);
    ctx.bezierCurveTo(482.378, 90.2494, 483.169, 91.1442, 484.332, 91.1841);
    ctx.bezierCurveTo(485.48, 91.2234, 486.393, 90.3401, 486.43, 89.2548);
    ctx.bezierCurveTo(486.452, 88.1768, 485.64, 87.2344, 484.485, 87.187);
    ctx.bezierCurveTo(483.329, 87.1552, 482.44, 88.008, 482.417, 89.1329);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 10번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(534.029, 26.3499);
    ctx.lineTo(534.15, 24.6509);
    ctx.lineTo(538.497, 21.2526);
    ctx.bezierCurveTo(539.629, 20.3321, 540.247, 19.7546, 540.319, 18.859);
    ctx.bezierCurveTo(540.399, 17.8544, 539.694, 17.1709, 538.657, 17.0958);
    ctx.bezierCurveTo(537.599, 17.0033, 536.852, 17.6175, 536.773, 18.7006);
    ctx.lineTo(534.546, 18.5224);
    ctx.bezierCurveTo(534.715, 16.3179, 536.463, 15.0392, 538.838, 15.2293);
    ctx.bezierCurveTo(541.252, 15.4225, 542.746, 16.9293, 542.59, 18.884);
    ctx.bezierCurveTo(542.487, 20.169, 541.78, 21.1704, 539.291, 23.0873);
    ctx.lineTo(537.444, 24.6168);
    ctx.lineTo(537.439, 24.6791);
    ctx.lineTo(542.252, 25.0643);
    ctx.lineTo(542.097, 26.9956);
    ctx.lineTo(534.029, 26.3499);
    ctx.closePath();
    ctx.moveTo(543.764, 27.129);
    ctx.lineTo(543.884, 25.43);
    ctx.lineTo(548.231, 22.0317);
    ctx.bezierCurveTo(549.363, 21.1112, 549.982, 20.5337, 550.053, 19.6381);
    ctx.bezierCurveTo(550.134, 18.6335, 549.428, 17.95, 548.392, 17.8749);
    ctx.bezierCurveTo(547.333, 17.7824, 546.587, 18.3966, 546.508, 19.4797);
    ctx.lineTo(544.28, 19.3015);
    ctx.bezierCurveTo(544.449, 17.097, 546.197, 15.8183, 548.572, 16.0084);
    ctx.bezierCurveTo(550.987, 16.2016, 552.481, 17.7084, 552.324, 19.6631);
    ctx.bezierCurveTo(552.221, 20.9481, 551.514, 21.9495, 549.025, 23.8664);
    ctx.lineTo(547.178, 25.3959);
    ctx.lineTo(547.173, 25.4582);
    ctx.lineTo(551.986, 25.8434);
    ctx.lineTo(551.832, 27.7747);
    ctx.lineTo(543.764, 27.129);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(515.358, 35.3891);
    ctx.rotate(0.07986487030785892);
    ctx.translate(-515.358, -35.3891);
    ctx.beginPath();
    ctx.moveTo(534.858, 35.3891);
    ctx.lineTo(549.858, 35.3891);
    ctx.bezierCurveTo(
      560.6275526217004,
      35.3891,
      569.358,
      44.11954737829952,
      569.358,
      54.8891
    );
    ctx.lineTo(569.358, 54.8891);
    ctx.bezierCurveTo(
      569.358,
      65.65865262170047,
      560.6275526217004,
      74.3891,
      549.858,
      74.3891
    );
    ctx.lineTo(534.858, 74.3891);
    ctx.bezierCurveTo(
      524.0884473782995,
      74.3891,
      515.358,
      65.65865262170047,
      515.358,
      54.8891
    );
    ctx.lineTo(515.358, 54.8891);
    ctx.bezierCurveTo(
      515.358,
      44.11954737829952,
      524.0884473782995,
      35.3891,
      534.858,
      35.3891
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(559.724, 42.4399);
    ctx.lineTo(521.017, 71.5392);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(524.263, 39.6017);
    ctx.lineTo(557.675, 74.3062);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(534.545, 87.2727);
    ctx.lineTo(533.643, 98.5492);
    ctx.lineTo(531.291, 98.361);
    ctx.lineTo(532.015, 89.3118);
    ctx.lineTo(531.953, 89.3068);
    ctx.lineTo(529.238, 90.7197);
    ctx.lineTo(529.404, 88.6482);
    ctx.lineTo(532.334, 87.0957);
    ctx.lineTo(534.545, 87.2727);
    ctx.closePath();
    ctx.moveTo(540.624, 99.2646);
    ctx.bezierCurveTo(537.797, 99.0384, 536.26, 96.7992, 536.555, 93.1079);
    ctx.bezierCurveTo(536.85, 89.4244, 538.74, 87.4517, 541.551, 87.6767);
    ctx.bezierCurveTo(544.362, 87.9017, 545.915, 90.1499, 545.62, 93.8334);
    ctx.bezierCurveTo(545.323, 97.5403, 543.443, 99.4903, 540.624, 99.2646);
    ctx.closePath();
    ctx.moveTo(538.954, 93.2999);
    ctx.bezierCurveTo(538.73, 95.8997, 539.47, 97.2286, 540.778, 97.3333);
    ctx.bezierCurveTo(542.087, 97.438, 543.021, 96.2431, 543.221, 93.6414);
    ctx.bezierCurveTo(543.435, 91.0644, 542.698, 89.7043, 541.398, 89.5924);
    ctx.bezierCurveTo(540.097, 89.4961, 539.16, 90.7222, 538.954, 93.2999);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 11번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(590.697, 30.9906);
    ctx.lineTo(590.949, 29.3061);
    ctx.lineTo(595.547, 26.2559);
    ctx.bezierCurveTo(596.747, 25.4262, 597.408, 24.8985, 597.549, 24.0112);
    ctx.bezierCurveTo(597.708, 23.0159, 597.057, 22.2796, 596.03, 22.1242);
    ctx.bezierCurveTo(594.982, 21.9497, 594.19, 22.5041, 594.027, 23.5778);
    ctx.lineTo(591.82, 23.227);
    ctx.bezierCurveTo(592.16, 21.0422, 594.002, 19.9033, 596.355, 20.2774);
    ctx.bezierCurveTo(598.747, 20.6576, 600.119, 22.276, 599.811, 24.2126);
    ctx.bezierCurveTo(599.609, 25.4856, 598.826, 26.4291, 596.196, 28.1468);
    ctx.lineTo(594.236, 29.5281);
    ctx.lineTo(594.226, 29.5898);
    ctx.lineTo(598.994, 30.3479);
    ctx.lineTo(598.69, 32.2613);
    ctx.lineTo(590.697, 30.9906);
    ctx.closePath();
    ctx.moveTo(604.452, 33.3356);
    ctx.bezierCurveTo(601.999, 32.9455, 600.454, 31.3156, 600.718, 29.3562);
    ctx.lineTo(603.064, 29.7291);
    ctx.bezierCurveTo(602.97, 30.5685, 603.693, 31.2451, 604.772, 31.4246);
    ctx.bezierCurveTo(605.884, 31.5935, 606.777, 31.1026, 606.913, 30.1986);
    ctx.bezierCurveTo(607.065, 29.2894, 606.357, 28.5202, 605.046, 28.304);
    ctx.lineTo(604.013, 28.1396);
    ctx.lineTo(604.285, 26.4267);
    ctx.lineTo(605.319, 26.5911);
    ctx.bezierCurveTo(606.422, 26.7665, 607.289, 26.2873, 607.421, 25.4065);
    ctx.bezierCurveTo(607.565, 24.5513, 607.003, 23.8607, 606.014, 23.7114);
    ctx.bezierCurveTo(605.028, 23.5467, 604.129, 23.9734, 603.954, 24.8237);
    ctx.lineTo(601.732, 24.4705);
    ctx.bezierCurveTo(602.076, 22.5554, 604.017, 21.4954, 606.308, 21.8597);
    ctx.bezierCurveTo(608.638, 22.2301, 609.97, 23.8577, 609.681, 25.6234);
    ctx.bezierCurveTo(609.49, 26.8745, 608.477, 27.6232, 607.192, 27.6483);
    ctx.lineTo(607.177, 27.7409);
    ctx.bezierCurveTo(608.788, 28.2027, 609.533, 29.3415, 609.296, 30.7356);
    ctx.bezierCurveTo(609.007, 32.6515, 606.975, 33.7367, 604.452, 33.3356);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(571.379, 38.5515);
    ctx.rotate(0.157654020536321);
    ctx.translate(-571.379, -38.5515);
    ctx.beginPath();
    ctx.moveTo(590.879, 38.5515);
    ctx.lineTo(605.879, 38.5515);
    ctx.bezierCurveTo(
      616.6485526217004,
      38.5515,
      625.379,
      47.28194737829952,
      625.379,
      58.0515
    );
    ctx.lineTo(625.379, 58.051500000000004);
    ctx.bezierCurveTo(
      625.379,
      68.82105262170047,
      616.6485526217004,
      77.5515,
      605.879,
      77.5515
    );
    ctx.lineTo(590.879, 77.5515);
    ctx.bezierCurveTo(
      580.1094473782996,
      77.5515,
      571.379,
      68.82105262170047,
      571.379,
      58.051500000000004
    );
    ctx.lineTo(571.379, 58.0515);
    ctx.bezierCurveTo(
      571.379,
      47.28194737829952,
      580.1094473782996,
      38.5515,
      590.879,
      38.5515
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(615.064, 49.0287);
    ctx.lineTo(574.213, 75.0322);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(579.93, 43.4435);
    ctx.lineTo(610.544, 80.6394);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(588.452, 92.0834);
    ctx.lineTo(586.676, 103.256);
    ctx.lineTo(584.346, 102.885);
    ctx.lineTo(585.771, 93.9196);
    ctx.lineTo(585.71, 93.9098);
    ctx.lineTo(582.893, 95.1074);
    ctx.lineTo(583.219, 93.0551);
    ctx.lineTo(586.261, 91.735);
    ctx.lineTo(588.452, 92.0834);
    ctx.closePath();
    ctx.moveTo(595.859, 93.2609);
    ctx.lineTo(594.083, 104.433);
    ctx.lineTo(591.753, 104.063);
    ctx.lineTo(593.178, 95.0971);
    ctx.lineTo(593.117, 95.0873);
    ctx.lineTo(590.3, 96.2849);
    ctx.lineTo(590.626, 94.2326);
    ctx.lineTo(593.668, 92.9126);
    ctx.lineTo(595.859, 93.2609);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 12번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(645.184, 38.0158);
    ctx.lineTo(645.484, 36.3392);
    ctx.lineTo(650.168, 33.4217);
    ctx.bezierCurveTo(651.391, 32.6266, 652.067, 32.118, 652.234, 31.2351);
    ctx.bezierCurveTo(652.42, 30.2448, 651.791, 29.4902, 650.769, 29.3055);
    ctx.bezierCurveTo(649.726, 29.1011, 648.918, 29.6326, 648.725, 30.7012);
    ctx.lineTo(646.529, 30.2874);
    ctx.bezierCurveTo(646.931, 28.1133, 648.805, 27.0275, 651.147, 27.4687);
    ctx.bezierCurveTo(653.527, 27.9172, 654.852, 29.5741, 654.489, 31.5011);
    ctx.bezierCurveTo(654.25, 32.7679, 653.441, 33.6886, 650.762, 35.3304);
    ctx.lineTo(648.764, 36.6551);
    ctx.lineTo(648.752, 36.7165);
    ctx.lineTo(653.497, 37.6106);
    ctx.lineTo(653.138, 39.5146);
    ctx.lineTo(645.184, 38.0158);
    ctx.closePath();
    ctx.moveTo(654.721, 37.7777);
    ctx.lineTo(655.066, 35.9505);
    ctx.lineTo(661.144, 29.5116);
    ctx.lineTo(664.046, 30.0585);
    ctx.lineTo(662.672, 37.352);
    ctx.lineTo(664.085, 37.6182);
    ctx.lineTo(663.735, 39.4761);
    ctx.lineTo(662.322, 39.2099);
    ctx.lineTo(661.952, 41.1753);
    ctx.lineTo(659.756, 40.7616);
    ctx.lineTo(660.126, 38.7962);
    ctx.lineTo(654.721, 37.7777);
    ctx.closePath();
    ctx.moveTo(657.405, 36.3596);
    ctx.lineTo(660.507, 36.944);
    ctx.lineTo(661.404, 32.1841);
    ctx.lineTo(661.297, 32.1638);
    ctx.lineTo(657.423, 36.2675);
    ctx.lineTo(657.405, 36.3596);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(626.15, 45.1139);
    ctx.rotate(0.1862510657973229);
    ctx.translate(-626.15, -45.1139);
    ctx.beginPath();
    ctx.moveTo(645.65, 45.1139);
    ctx.lineTo(660.65, 45.1139);
    ctx.bezierCurveTo(
      671.4195526217004,
      45.1139,
      680.15,
      53.844347378299524,
      680.15,
      64.6139
    );
    ctx.lineTo(680.15, 64.6139);
    ctx.bezierCurveTo(
      680.15,
      75.38345262170047,
      671.4195526217004,
      84.1139,
      660.65,
      84.1139
    );
    ctx.lineTo(645.65, 84.1139);
    ctx.bezierCurveTo(
      634.8804473782996,
      84.1139,
      626.15,
      75.38345262170047,
      626.15,
      64.6139
    );
    ctx.lineTo(626.15, 64.6139);
    ctx.bezierCurveTo(
      626.15,
      53.844347378299524,
      634.8804473782996,
      45.1139,
      645.65,
      45.1139
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(669.517, 56.8358);
    ctx.lineTo(627.939, 81.6606);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(634.557, 50.2483);
    ctx.lineTo(664.095, 88.3043);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(640.211, 98.8343);
    ctx.lineTo(638.116, 109.951);
    ctx.lineTo(635.798, 109.514);
    ctx.lineTo(637.479, 100.593);
    ctx.lineTo(637.417, 100.582);
    ctx.lineTo(634.568, 101.698);
    ctx.lineTo(634.952, 99.6559);
    ctx.lineTo(638.031, 98.4234);
    ctx.lineTo(640.211, 98.8343);
    ctx.closePath();
    ctx.moveTo(640.696, 110.437);
    ctx.lineTo(640.996, 108.761);
    ctx.lineTo(645.68, 105.843);
    ctx.bezierCurveTo(646.903, 105.048, 647.579, 104.539, 647.745, 103.657);
    ctx.bezierCurveTo(647.932, 102.666, 647.303, 101.912, 646.28, 101.727);
    ctx.bezierCurveTo(645.238, 101.522, 644.43, 102.054, 644.237, 103.123);
    ctx.lineTo(642.041, 102.709);
    ctx.bezierCurveTo(642.443, 100.535, 644.317, 99.4489, 646.658, 99.8901);
    ctx.bezierCurveTo(649.038, 100.339, 650.364, 101.996, 650.001, 103.923);
    ctx.bezierCurveTo(649.762, 105.189, 648.953, 106.11, 646.274, 107.752);
    ctx.lineTo(644.275, 109.077);
    ctx.lineTo(644.264, 109.138);
    ctx.lineTo(649.008, 110.032);
    ctx.lineTo(648.65, 111.936);
    ctx.lineTo(640.696, 110.437);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 13번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(700.655, 48.2528);
    ctx.lineTo(701.016, 46.5885);
    ctx.lineTo(705.805, 43.8465);
    ctx.bezierCurveTo(707.057, 43.0973, 707.751, 42.6141, 707.95, 41.7379);
    ctx.bezierCurveTo(708.173, 40.7552, 707.573, 39.9779, 706.558, 39.7554);
    ctx.bezierCurveTo(705.524, 39.5125, 704.697, 40.0137, 704.464, 41.0744);
    ctx.lineTo(702.285, 40.5796);
    ctx.bezierCurveTo(702.767, 38.4218, 704.68, 37.4062, 707.004, 37.9338);
    ctx.bezierCurveTo(709.365, 38.4702, 710.629, 40.1751, 710.194, 42.0873);
    ctx.bezierCurveTo(709.909, 43.3444, 709.066, 44.2345, 706.328, 45.7759);
    ctx.lineTo(704.282, 47.0256);
    ctx.lineTo(704.268, 47.0866);
    ctx.lineTo(708.976, 48.1558);
    ctx.lineTo(708.547, 50.0452);
    ctx.lineTo(700.655, 48.2528);
    ctx.closePath();
    ctx.moveTo(714.196, 51.4882);
    ctx.bezierCurveTo(711.895, 50.9657, 710.52, 49.2273, 710.914, 47.314);
    ctx.lineTo(713.139, 47.8192);
    ctx.bezierCurveTo(712.996, 48.6922, 713.646, 49.4646, 714.608, 49.675);
    ctx.bezierCurveTo(715.749, 49.9421, 716.748, 49.3197, 717.001, 48.1676);
    ctx.bezierCurveTo(717.275, 46.9961, 716.63, 45.9923, 715.474, 45.7217);
    ctx.bezierCurveTo(714.643, 45.5331, 713.993, 45.714, 713.526, 46.2567);
    ctx.lineTo(711.453, 45.7861);
    ctx.lineTo(713.49, 39.5672);
    ctx.lineTo(720.286, 41.1105);
    ctx.lineTo(719.861, 42.9846);
    ctx.lineTo(714.954, 41.8704);
    ctx.lineTo(714.016, 44.5896);
    ctx.lineTo(714.108, 44.6104);
    ctx.bezierCurveTo(714.634, 44.1611, 715.562, 43.9553, 716.528, 44.1826);
    ctx.bezierCurveTo(718.556, 44.6352, 719.721, 46.4939, 719.24, 48.6118);
    ctx.bezierCurveTo(718.733, 50.844, 716.68, 52.0522, 714.196, 51.4882);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(680.883, 54.5301);
    ctx.rotate(0.22330615114641447);
    ctx.translate(-680.883, -54.5301);
    ctx.beginPath();
    ctx.moveTo(700.383, 54.5301);
    ctx.lineTo(715.383, 54.5301);
    ctx.bezierCurveTo(
      726.1525526217005,
      54.5301,
      734.883,
      63.26054737829953,
      734.883,
      74.0301
    );
    ctx.lineTo(734.883, 74.0301);
    ctx.bezierCurveTo(
      734.883,
      84.79965262170047,
      726.1525526217005,
      93.5301,
      715.383,
      93.5301
    );
    ctx.lineTo(700.383, 93.5301);
    ctx.bezierCurveTo(
      689.6134473782996,
      93.5301,
      680.883,
      84.79965262170047,
      680.883,
      74.0301
    );
    ctx.lineTo(680.883, 74.0301);
    ctx.bezierCurveTo(
      680.883,
      63.26054737829953,
      689.6134473782996,
      54.5301,
      700.383,
      54.5301
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(723.786, 67.8506);
    ctx.lineTo(681.317, 91.118);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(689.094, 59.9724);
    ctx.lineTo(717.202, 99.0966);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(692.944, 108.734);
    ctx.lineTo(690.439, 119.766);
    ctx.lineTo(688.138, 119.244);
    ctx.lineTo(690.149, 110.391);
    ctx.lineTo(690.088, 110.377);
    ctx.lineTo(687.198, 111.387);
    ctx.lineTo(687.659, 109.361);
    ctx.lineTo(690.78, 108.243);
    ctx.lineTo(692.944, 108.734);
    ctx.closePath();
    ctx.moveTo(697.048, 121.427);
    ctx.bezierCurveTo(694.625, 120.877, 693.191, 119.149, 693.583, 117.211);
    ctx.lineTo(695.899, 117.737);
    ctx.bezierCurveTo(695.75, 118.569, 696.427, 119.291, 697.492, 119.541);
    ctx.bezierCurveTo(698.591, 119.783, 699.514, 119.351, 699.709, 118.458);
    ctx.bezierCurveTo(699.92, 117.561, 699.264, 116.747, 697.971, 116.445);
    ctx.lineTo(696.95, 116.214);
    ctx.lineTo(697.334, 114.522);
    ctx.lineTo(698.355, 114.754);
    ctx.bezierCurveTo(699.444, 115.001, 700.341, 114.58, 700.531, 113.71);
    ctx.bezierCurveTo(700.73, 112.866, 700.214, 112.14, 699.237, 111.926);
    ctx.bezierCurveTo(698.264, 111.697, 697.339, 112.064, 697.109, 112.901);
    ctx.lineTo(694.915, 112.403);
    ctx.bezierCurveTo(695.384, 110.514, 697.39, 109.584, 699.653, 110.098);
    ctx.bezierCurveTo(701.953, 110.62, 703.175, 112.332, 702.771, 114.075);
    ctx.bezierCurveTo(702.498, 115.311, 701.439, 115.991, 700.154, 115.932);
    ctx.lineTo(700.134, 116.023);
    ctx.bezierCurveTo(701.711, 116.59, 702.379, 117.775, 702.051, 119.151);
    ctx.bezierCurveTo(701.637, 121.043, 699.539, 121.993, 697.048, 121.427);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 14번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(755.89, 60.431);
    ctx.lineTo(756.362, 58.7945);
    ctx.lineTo(761.323, 56.3776);
    ctx.bezierCurveTo(762.622, 55.7134, 763.347, 55.2776, 763.604, 54.4167);
    ctx.bezierCurveTo(763.892, 53.4509, 763.344, 52.6353, 762.346, 52.3457);
    ctx.bezierCurveTo(761.331, 52.0345, 760.472, 52.4795, 760.169, 53.5223);
    ctx.lineTo(758.028, 52.8835);
    ctx.bezierCurveTo(758.653, 50.7626, 760.629, 49.8766, 762.913, 50.5579);
    ctx.bezierCurveTo(765.233, 51.2504, 766.38, 53.0357, 765.819, 54.9147);
    ctx.bezierCurveTo(765.451, 56.15, 764.55, 56.9819, 761.716, 58.3376);
    ctx.lineTo(759.591, 59.4482);
    ctx.lineTo(759.573, 59.5081);
    ctx.lineTo(764.2, 60.8886);
    ctx.lineTo(763.646, 62.7452);
    ctx.lineTo(755.89, 60.431);
    ctx.closePath();
    ctx.moveTo(769.396, 64.6239);
    ctx.bezierCurveTo(767.16, 63.9485, 765.418, 61.9696, 766.61, 58.004);
    ctx.bezierCurveTo(767.71, 54.3455, 770.099, 52.7024, 772.809, 53.511);
    ctx.bezierCurveTo(774.988, 54.161, 776.074, 55.9853, 775.76, 57.8318);
    ctx.lineTo(773.544, 57.1706);
    ctx.bezierCurveTo(773.59, 56.3609, 773.109, 55.7038, 772.233, 55.4425);
    ctx.bezierCurveTo(770.758, 55.0024, 769.592, 56.0974, 768.94, 58.2266);
    ctx.lineTo(769.045, 58.2578);
    ctx.bezierCurveTo(769.835, 57.4418, 771.073, 57.1997, 772.271, 57.5571);
    ctx.bezierCurveTo(774.225, 58.1401, 775.241, 60.0333, 774.624, 62.0749);
    ctx.bezierCurveTo(773.975, 64.2781, 771.842, 65.3618, 769.396, 64.6239);
    ctx.closePath();
    ctx.moveTo(768.557, 60.3299);
    ctx.bezierCurveTo(768.255, 61.3974, 768.802, 62.5145, 769.912, 62.8377);
    ctx.bezierCurveTo(771.026, 63.178, 772.094, 62.5755, 772.42, 61.4825);
    ctx.bezierCurveTo(772.744, 60.397, 772.193, 59.3195, 771.095, 58.9836);
    ctx.bezierCurveTo(769.985, 58.6605, 768.887, 59.2788, 768.557, 60.3299);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(736.223, 65.5203);
    ctx.rotate(0.2899690019263379);
    ctx.translate(-736.223, -65.5203);
    ctx.beginPath();
    ctx.moveTo(755.723, 65.5203);
    ctx.lineTo(770.723, 65.5203);
    ctx.bezierCurveTo(
      781.4925526217004,
      65.5203,
      790.223,
      74.25074737829954,
      790.223,
      85.0203
    );
    ctx.lineTo(790.223, 85.0203);
    ctx.bezierCurveTo(
      790.223,
      95.78985262170048,
      781.4925526217004,
      104.5203,
      770.723,
      104.5203
    );
    ctx.lineTo(755.723, 104.5203);
    ctx.bezierCurveTo(
      744.9534473782995,
      104.5203,
      736.223,
      95.78985262170048,
      736.223,
      85.0203
    );
    ctx.lineTo(736.223, 85.0203);
    ctx.bezierCurveTo(
      736.223,
      74.25074737829954,
      744.9534473782995,
      65.5203,
      755.723,
      65.5203
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(778.144, 81.6692);
    ctx.lineTo(734.219, 102.056);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(744.054, 71.4975);
    ctx.lineTo(769.493, 112.407);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(744.168, 120.265);
    ctx.lineTo(740.934, 131.105);
    ctx.lineTo(738.673, 130.43);
    ctx.lineTo(741.268, 121.731);
    ctx.lineTo(741.208, 121.713);
    ctx.lineTo(738.258, 122.529);
    ctx.lineTo(738.852, 120.538);
    ctx.lineTo(742.042, 119.63);
    ctx.lineTo(744.168, 120.265);
    ctx.closePath();
    ctx.moveTo(743.841, 129.885);
    ctx.lineTo(744.373, 128.104);
    ctx.lineTo(751.085, 122.329);
    ctx.lineTo(753.915, 123.173);
    ctx.lineTo(751.793, 130.285);
    ctx.lineTo(753.171, 130.696);
    ctx.lineTo(752.63, 132.508);
    ctx.lineTo(751.253, 132.097);
    ctx.lineTo(750.681, 134.013);
    ctx.lineTo(748.54, 133.375);
    ctx.lineTo(749.112, 131.458);
    ctx.lineTo(743.841, 129.885);
    ctx.closePath();
    ctx.moveTo(746.658, 128.753);
    ctx.lineTo(749.682, 129.655);
    ctx.lineTo(751.067, 125.014);
    ctx.lineTo(750.962, 124.982);
    ctx.lineTo(746.684, 128.663);
    ctx.lineTo(746.658, 128.753);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 15번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(810.189, 76.7637);
    ctx.lineTo(810.732, 75.1495);
    ctx.lineTo(815.794, 72.9523);
    ctx.bezierCurveTo(817.12, 72.3457, 817.864, 71.9421, 818.158, 71.0932);
    ctx.bezierCurveTo(818.489, 70.1411, 817.977, 69.3023, 816.993, 68.9692);
    ctx.bezierCurveTo(815.992, 68.6137, 815.115, 69.0206, 814.766, 70.0492);
    ctx.lineTo(812.655, 69.3171);
    ctx.bezierCurveTo(813.372, 67.2256, 815.386, 66.4272, 817.637, 67.2079);
    ctx.bezierCurveTo(819.925, 68.0015, 820.993, 69.8353, 820.35, 71.688);
    ctx.bezierCurveTo(819.928, 72.9059, 818.992, 73.6976, 816.101, 74.9277);
    ctx.lineTo(813.929, 75.9441);
    ctx.lineTo(813.909, 76.0031);
    ctx.lineTo(818.47, 77.5851);
    ctx.lineTo(817.836, 79.4156);
    ctx.lineTo(810.189, 76.7637);
    ctx.closePath();
    ctx.moveTo(819.829, 80.1068);
    ctx.lineTo(827.382, 72.8698);
    ctx.lineTo(827.408, 72.796);
    ctx.lineTo(822.167, 70.9786);
    ctx.lineTo(822.797, 69.1628);
    ctx.lineTo(830.341, 71.7789);
    ctx.lineTo(829.701, 73.6242);
    ctx.lineTo(822.132, 80.9054);
    ctx.lineTo(819.829, 80.1068);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(789.373, 80.6583);
    ctx.rotate(0.3338203993826954);
    ctx.translate(-789.373, -80.6583);
    ctx.beginPath();
    ctx.moveTo(808.873, 80.6583);
    ctx.lineTo(823.873, 80.6583);
    ctx.bezierCurveTo(
      834.6425526217005,
      80.6583,
      843.373,
      89.38874737829953,
      843.373,
      100.1583
    );
    ctx.lineTo(843.373, 100.1583);
    ctx.bezierCurveTo(
      843.373,
      110.92785262170047,
      834.6425526217005,
      119.6583,
      823.873,
      119.6583
    );
    ctx.lineTo(808.873, 119.6583);
    ctx.bezierCurveTo(
      798.1034473782996,
      119.6583,
      789.373,
      110.92785262170047,
      789.373,
      100.1583
    );
    ctx.lineTo(789.373, 100.1583);
    ctx.bezierCurveTo(
      789.373,
      89.38874737829953,
      798.1034473782996,
      80.6583,
      808.873,
      80.6583
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(830.545, 98.6293);
    ctx.lineTo(785.769, 117.071);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(796.934, 86.973);
    ctx.lineTo(820.555, 128.959);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(795.382, 135.862);
    ctx.lineTo(791.676, 146.55);
    ctx.lineTo(789.447, 145.777);
    ctx.lineTo(792.421, 137.2);
    ctx.lineTo(792.362, 137.18);
    ctx.lineTo(789.379, 137.865);
    ctx.lineTo(790.06, 135.902);
    ctx.lineTo(793.286, 135.135);
    ctx.lineTo(795.382, 135.862);
    ctx.closePath();
    ctx.moveTo(798.032, 148.92);
    ctx.bezierCurveTo(795.802, 148.147, 794.627, 146.267, 795.23, 144.409);
    ctx.lineTo(797.385, 145.157);
    ctx.bezierCurveTo(797.148, 146.009, 797.708, 146.848, 798.641, 147.163);
    ctx.bezierCurveTo(799.745, 147.554, 800.807, 147.046, 801.186, 145.929);
    ctx.bezierCurveTo(801.587, 144.795, 801.057, 143.726, 799.937, 143.329);
    ctx.bezierCurveTo(799.133, 143.05, 798.467, 143.158, 797.942, 143.646);
    ctx.lineTo(795.934, 142.95);
    ctx.lineTo(798.645, 136.994);
    ctx.lineTo(805.229, 139.277);
    ctx.lineTo(804.599, 141.093);
    ctx.lineTo(799.846, 139.444);
    ctx.lineTo(798.614, 142.043);
    ctx.lineTo(798.702, 142.074);
    ctx.bezierCurveTo(799.275, 141.686, 800.22, 141.584, 801.155, 141.916);
    ctx.bezierCurveTo(803.121, 142.59, 804.073, 144.565, 803.362, 146.617);
    ctx.bezierCurveTo(802.611, 148.78, 800.438, 149.754, 798.032, 148.92);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();

    // 16번 치아
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(862.423, 93.45);
    ctx.lineTo(863.019, 91.8545);
    ctx.lineTo(868.15, 89.8244);
    ctx.bezierCurveTo(869.496, 89.2616, 870.253, 88.8826, 870.575, 88.0439);
    ctx.bezierCurveTo(870.936, 87.103, 870.453, 86.2479, 869.48, 85.8828);
    ctx.bezierCurveTo(868.491, 85.4946, 867.601, 85.8726, 867.219, 86.8892);
    ctx.lineTo(865.133, 86.0883);
    ctx.bezierCurveTo(865.918, 84.0215, 867.957, 83.2894, 870.181, 84.1436);
    ctx.bezierCurveTo(872.442, 85.0117, 873.449, 86.8795, 872.746, 88.7101);
    ctx.bezierCurveTo(872.284, 89.9135, 871.322, 90.6741, 868.393, 91.8088);
    ctx.lineTo(866.189, 92.7535);
    ctx.lineTo(866.167, 92.8118);
    ctx.lineTo(870.674, 94.5424);
    ctx.lineTo(869.979, 96.3512);
    ctx.lineTo(862.423, 93.45);
    ctx.closePath();
    ctx.moveTo(875.452, 98.6197);
    ctx.bezierCurveTo(873.038, 97.6928, 871.796, 95.7601, 872.457, 94.0389);
    ctx.bezierCurveTo(872.967, 92.7115, 874.402, 92.0241, 875.794, 92.324);
    ctx.lineTo(875.822, 92.251);
    ctx.bezierCurveTo(874.761, 91.5926, 874.34, 90.3347, 874.792, 89.1779);
    ctx.bezierCurveTo(875.415, 87.534, 877.46, 86.9383, 879.619, 87.7672);
    ctx.bezierCurveTo(881.777, 88.5961, 882.898, 90.4072, 882.275, 92.051);
    ctx.bezierCurveTo(881.823, 93.2079, 880.646, 93.8525, 879.454, 93.6456);
    ctx.lineTo(879.426, 93.7185);
    ctx.bezierCurveTo(880.631, 94.4156, 881.252, 95.8927, 880.742, 97.2201);
    ctx.bezierCurveTo(880.082, 98.9413, 877.858, 99.5439, 875.452, 98.6197);
    ctx.closePath();
    ctx.moveTo(874.831, 94.6994);
    ctx.bezierCurveTo(874.477, 95.601, 874.977, 96.5211, 876.096, 96.9423);
    ctx.bezierCurveTo(877.209, 97.378, 878.196, 97.0291, 878.536, 96.122);
    ctx.bezierCurveTo(878.894, 95.2131, 878.364, 94.2397, 877.294, 93.8207);
    ctx.bezierCurveTo(876.219, 93.4164, 875.177, 93.7776, 874.831, 94.6994);
    ctx.closePath();
    ctx.moveTo(876.853, 90.22);
    ctx.bezierCurveTo(876.52, 91.0632, 876.957, 91.9088, 877.888, 92.2745);
    ctx.bezierCurveTo(878.817, 92.6229, 879.715, 92.2898, 880.047, 91.4465);
    ctx.bezierCurveTo(880.356, 90.6196, 879.921, 89.7914, 878.98, 89.4301);
    ctx.bezierCurveTo(878.032, 89.0661, 877.162, 89.393, 876.853, 90.22);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.translate(842.425, 97.0186);
    ctx.rotate(0.3666046640521569);
    ctx.translate(-842.425, -97.0186);
    ctx.beginPath();
    ctx.moveTo(861.925, 97.0186);
    ctx.lineTo(876.925, 97.0186);
    ctx.bezierCurveTo(
      887.6945526217004,
      97.0186,
      896.425,
      105.74904737829954,
      896.425,
      116.5186
    );
    ctx.lineTo(896.425, 116.51859999999999);
    ctx.bezierCurveTo(
      896.425,
      127.28815262170046,
      887.6945526217004,
      136.0186,
      876.925,
      136.0186
    );
    ctx.lineTo(861.925, 136.0186);
    ctx.bezierCurveTo(
      851.1554473782995,
      136.0186,
      842.425,
      127.28815262170046,
      842.425,
      116.51859999999999
    );
    ctx.lineTo(842.425, 116.5186);
    ctx.bezierCurveTo(
      842.425,
      105.74904737829954,
      851.1554473782995,
      97.0186,
      861.925,
      97.0186
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(882.985, 116.33);
    ctx.lineTo(837.629, 133.293);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.strokeStyle = '#495057';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(849.775, 103.578);
    ctx.lineTo(872.007, 146.315);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.fillStyle = '#868E96';
    ctx.beginPath();
    ctx.moveTo(846.621, 152.39);
    ctx.lineTo(842.567, 162.951);
    ctx.lineTo(840.364, 162.105);
    ctx.lineTo(843.618, 153.63);
    ctx.lineTo(843.56, 153.608);
    ctx.lineTo(840.556, 154.195);
    ctx.lineTo(841.301, 152.255);
    ctx.lineTo(844.55, 151.595);
    ctx.lineTo(846.621, 152.39);
    ctx.closePath();
    ctx.moveTo(849.045, 165.606);
    ctx.bezierCurveTo(846.867, 164.761, 845.283, 162.655, 846.774, 158.792);
    ctx.bezierCurveTo(848.151, 155.228, 850.66, 153.773, 853.3, 154.787);
    ctx.bezierCurveTo(855.422, 155.602, 856.366, 157.504, 855.911, 159.321);
    ctx.lineTo(853.752, 158.492);
    ctx.bezierCurveTo(853.86, 157.688, 853.431, 156.996, 852.577, 156.668);
    ctx.bezierCurveTo(851.141, 156.117, 849.894, 157.119, 849.081, 159.192);
    ctx.lineTo(849.183, 159.232);
    ctx.bezierCurveTo(850.033, 158.478, 851.286, 158.332, 852.453, 158.78);
    ctx.bezierCurveTo(854.356, 159.511, 855.225, 161.476, 854.454, 163.464);
    ctx.bezierCurveTo(853.638, 165.612, 851.428, 166.529, 849.045, 165.606);
    ctx.closePath();
    ctx.moveTo(848.538, 161.26);
    ctx.bezierCurveTo(848.155, 162.301, 848.615, 163.457, 849.697, 163.864);
    ctx.bezierCurveTo(850.781, 164.289, 851.893, 163.77, 852.301, 162.705);
    ctx.bezierCurveTo(852.708, 161.648, 852.241, 160.531, 851.171, 160.112);
    ctx.bezierCurveTo(850.089, 159.705, 848.948, 160.237, 848.538, 161.26);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    ctx.restore();
  };

  const clickHandler = (event: MouseEvent) => {
    if (readOnly) return;

    const rect = canvasRef.current?.getBoundingClientRect() || null;
    if (!rect) return;
    if (!ctx) return;
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const clickedArea = clickAreas.find((area) => {
      return ctx && ctx.isPointInPath(area.path(), x, y);
    });

    if (clickedArea) {
      setFillColors((prev) =>
        !prev.includes(clickedArea.id)
          ? [...prev, clickedArea.id]
          : prev.filter((id) => id !== clickedArea.id)
      );
    }
  };

  const hoverHandler = (event: MouseEvent) => {
    if (readOnly || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect() || null;
    if (!rect) return;
    if (!ctx) return;
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const hoverArea = clickAreas.find((area) => {
      return ctx && ctx.isPointInPath(area.path(), x, y);
    });

    if (hoverArea) {
      canvasRef.current.style.cursor = 'pointer';
    } else {
      canvasRef.current.style.cursor = 'default';
    }
  };

  return (
    <canvas
      ref={canvasRef}
      width="920"
      height="372"
      className={className}
    ></canvas>
  );
};

export default TeethCanvas;
